/**
 * Le Gantt : une barre par LIGNE de contre-étude, un résumé par titre.
 *
 * Reproduit la structure du classeur du métier — lignes groupées par chapitre,
 * couleurs des regroupements, frise en demi-journées — en jours relatifs. Le
 * calendrier viendra quand la date de début du chantier existera (PRD §4.5 : « le
 * relatif ne subsiste que pour les chantiers sans date »).
 *
 * ## La vision que cet écran met en œuvre
 *
 * *Posée par le métier le 12/08/2026 : « le chiffrage vit sur la LIGNE, pas sur la
 * tâche. Chaque ligne porte ses barres, placées librement ; étirer la barre d'une ligne
 * recalcule les heures de cette ligne, donc son coût ; la barre du titre devient un
 * résumé — la plus longue de son détail, ni déplaçable ni étirable. »*
 *
 * Trois conséquences visibles ici :
 *
 *  1. **le titre se lit, il ne se saisit pas** : sa barre est hachurée et plate, comme
 *     celle d'un chapitre. Une seule exception, bornée : un poste de fournitures pures
 *     n'a aucune ligne en heures, donc rien à quoi accrocher sa barre — il la porte
 *     lui-même, et elle reste saisissable ;
 *  2. **le détail se déplie pour agir** : replié, on lit le chantier ; déplié, on le
 *     modifie. C'est le seul endroit où les barres se posent, se déplacent et s'étirent ;
 *  3. **étirer la barre d'une ligne ne touche qu'elle** : rallonger la pelle ne rallonge
 *     plus le chef de chantier avec elle, ce que la maille de tâche imposait.
 *
 * ## Ce que cet écran doit rendre évident
 *
 * **Changer la durée d'une barre change le déboursé.** C'est la règle du métier, et
 * c'est contre-intuitif : on croit déplacer un rectangle, on modifie un chiffrage. À
 * nombre d'unités constant, allonger la barre allonge le travail — les heures montent,
 * le coût suit. L'écran l'annonce donc avant, et le confirme après.
 *
 * Un écran de planning qui toucherait au déboursé sans le dire serait le pire des
 * deux mondes : le conducteur croirait ranger des barres et découvrirait ses coûts
 * changés.
 *
 * ## Pourquoi un écran à part
 *
 * Le déboursé compte déjà quinze colonnes. Y ajouter une frise de cent
 * demi-journées rendrait les deux illisibles. C'est la même donnée, deux questions
 * différentes : *combien ça coûte* et *quand ça se fait*.
 */
import { Fragment, useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import {
  datesDesJoursOuvres,
  DEMI_JOURNEES_PAR_JOUR,
  depuisIndexDemiJournee,
  dureeDesPhases,
  etendueEnDemiJournees,
  barresDuResume,
  pastillesDuTitre,
  dec,
  echeancesDeLaVersion,
  echeancesDeLaTache,
  morceauxEnDemiJournees,
  totalDesEcheances,
  formaterDate,
  formaterDateCourte,
  formaterDateLongue,
  formaterMontant,
  formaterNombre,
  indexDemiJournee,
  jourDeLaSemaine,
  STATUT_RETIRE,
} from "@planif/moteur";
import type {
  DepoVue,
  EnteteVersion,
  LivraisonVue,
  NoeudVue,
  OuvrageVue,
  PhaseVue,
  RessourceVue,
  TransportVue,
} from "@planif/moteur";
import {
  adresseClasseurPlanning,
  decalerBloc,
  figerLeTransport,
  fixerUnites,
  modifierBarre,
  modifierLivraison,
  poserBarre,
  poserBarreDeTitre,
  hacherBarres,
  hacherBloc,
  poserLivraison,
  proposerPlanning,
  rendreLesHeuresDuDevis,
  repartirLaLivraison,
  organiserLeTransportDesMateriaux,
  modifierTransport,
  poserTransport,
  repartirLeTransport,
  retirerBarre,
  retirerLivraison,
  retirerTransport,
} from "./api";
import type {
  BarreEcrite,
  BilanDuTransport,
  BlocDecaleEcrit,
  BlocHache,
  Carriere,
  DemandeTransport,
  HeuresRestaurees,
  JourneeDeTransport,
  OuvrageOrganise,
} from "./api";
import { codesATrancher, partSurPorteEngin } from "./materielACharger";
import { Acheminement, ChampCourt } from "./Acheminement";
import type { ChangementAcheminement } from "./Acheminement";
import { lignesAcheminees, pastillesSansMode, tacheAcheminee } from "./livraisonParNosSoins";
import { attelageAffiche } from "./controleTransport";
import { aplatir, lignesDeLaTache, lignesVisibles as filtrerVisibles } from "./pliage";
import { amenerSousLesYeux, ancreDeLaLigne, ATTRIBUT_CIBLE } from "./cibles";
import { lireDefilement, lireEnsemble, retenirDefilement, retenirEnsemble } from "./position";
import type { CibleDeLaLigne } from "./cibles";
import type { LigneGantt } from "./pliage";

/**
 * Combien de jours la frise affiche.
 *
 * L'étendue réelle du chantier, avec un minimum : une frise de trois colonnes sur un
 * chantier à peine commencé ne se lit pas, et deux jours de marge à droite laissent
 * la place de déplacer une barre sans que la frise saute.
 */
const JOURS_MINIMUM = 10;
const MARGE_JOURS = 2;

/**
 * Le montant tient-il encore à droite de la barre ?
 *
 * La frise ne laisse que `MARGE_JOURS` après la dernière barre du chantier — deux
 * colonnes, soit à peine de quoi écrire « 12 345 € ». Un chapitre qui va jusqu'au bout
 * verrait donc son CA sortir du cadre et se faire couper par le défilement. Dans ce cas,
 * le montant se pose **à l'intérieur**, collé au bord droit : décalé, il se lirait comme
 * appartenant à un jour qui n'existe pas.
 */
function caAuBord(finDemi: number, jours: number): boolean {
  const restant = jours * 2 - (finDemi + 1);
  return restant < 5;
}

/**
 * Cette valeur vaut-elle quelque chose une fois AFFICHÉE ?
 *
 * Les heures viennent de temps unitaires à vingt décimales : un écart d'arrondi peut
 * valoir `0,0000000000000004`. Comparer la chaîne à « 0 » le déclarerait non nul et
 * l'écran signalerait en rouge un coût caché de « +0,00 h ». Le critère est donc ce
 * que le lecteur verra, à deux décimales — rien d'autre n'aurait de sens à montrer.
 */
function visible(valeur: string): boolean {
  return formaterNombre(valeur, 2) !== formaterNombre("0", 2);
}

/** Les tâches d'une branche, à n'importe quelle profondeur sous elle. */
function tachesDe(noeud: NoeudVue): OuvrageVue[] {
  if (noeud.nature === "ouvrage") return noeud.retenu ? [noeud] : [];
  if (!noeud.retenu) return [];
  return noeud.enfants.flatMap(tachesDe);
}

/**
 * Toutes les barres d'une tâche : celles de ses lignes, et les siennes propres.
 *
 * Une seule définition, parce que quatre endroits en ont besoin — l'étendue de la
 * frise, le résumé du chapitre, celui du titre, et la recherche d'une barre par son
 * identifiant. Quatre parcours de l'arbre finiraient par ne pas ramasser les mêmes.
 */
function barresDe(tache: OuvrageVue): PhaseVue[] {
  return [...tache.planning.phases, ...tache.ressources.flatMap((r) => r.planning.phases)];
}

/*
  Les barres qui comptent dans une barre GRISE vivent dans `barreGrise.ts` (migration 046) :
  les deux résumés — titre et chapitre — en dépendent, et la règle doit être vérifiable sans
  écran. Corriger le serveur seul n'avait rien changé ici, cet écran recalculant l'enveloppe
  à partir de toutes les barres qu'il ramasse.

  `barresDe`, juste au-dessus, reste NON filtrée, et ses trois appelants la veulent ainsi :
  la frise doit contenir tout ce qui est dessiné, cabane comprise — la filtrer sortirait sa
  barre du cadre —, et les deux compteurs de tâches planifiées répondent « cette tâche
  porte-t-elle quelque chose ? », à quoi une installation de chantier répond oui.
*/

/**
 * La barre récapitulative d'un chapitre : du premier au dernier jour de ce qu'il porte.
 *
 * **Elle se calcule, elle ne se pose pas.** Un chapitre n'a pas de durée propre : il
 * a celle de ses tâches. Le rendre déplaçable créerait un second endroit où dire la
 * même chose — et le jour où les deux se contrediraient, plus personne ne saurait
 * lequel fait foi pour le plan de charge.
 *
 * `dureeDesPhases` est celle du moteur : l'étendue d'un chapitre est exactement
 * l'étendue de l'ensemble des barres de son détail, et un second calcul finirait par
 * diverger du premier.
 */
function etendueDuChapitre(chapitre: NoeudVue): {
  debut: number;
  fin: number;
  /** Les mêmes bornes en demi-journées : c'est sur elles que la barre se dessine. */
  debutDemi: number;
  finDemi: number;
  taches: number;
} | null {
  const taches = tachesDe(chapitre);
  // Le résumé du chapitre exclut les mêmes barres que celui du titre : c'est la même
  // question, et deux périmètres feraient qu'une barre grise s'étirerait sans que celle du
  // dessous ne bouge.
  /*
    **Les barres, et elles seules** (27/08/2026) : aucune pastille n'entre dans un résumé, à
    aucun niveau — la règle et le contre-exemple qui l'ont fixée vivent dans `barreGrise.ts`.
    Un chapitre dont la dernière livraison tombe à J30 ne s'étend donc plus jusqu'à J30 : cette
    livraison est un instant, et l'intervalle qui l'y rattachait était inventé.
  */
  const phases = taches.flatMap(barresDuResume);
  const duree = dureeDesPhases(phases);
  if (duree.debut === null || duree.fin === null) return null;

  const enveloppe = enveloppeEnDemiJournees(phases, []);
  if (enveloppe === null) return null;

  return {
    debut: duree.debut,
    fin: duree.fin,
    debutDemi: enveloppe.debut,
    finDemi: enveloppe.fin,
    /*
      Le COMPTEUR, lui, continue de compter les pastilles : « 3 tâche(s) planifiée(s) »
      répond à *qu'y a-t-il de posé sous ce chapitre*, et un poste dont les camions sont
      placés est bien planifié. C'est la même divergence assumée que celle du 21/08 entre
      l'enchaînement et la fin prévue — deux questions, deux périmètres.
    */
    taches: taches.filter((t) => barresDe(t).length > 0 || livraisonsDe(t).length > 0).length,
  };
}

/**
 * L'enveloppe d'un récapitulatif, à la DEMI-JOURNÉE près.
 *
 * *Demande du métier, 16/08/2026 : « les barres sur les grands chapitres ne se
 * déplacent que de journée en journée, il faut qu'on puisse les aligner sur les
 * demi-journées ».*
 *
 * Une seule définition pour les deux récapitulatifs — celui du chapitre et celui du
 * titre —, parce qu'ils résument le même genre de chose et que deux calculs finiraient
 * par se décaler l'un de l'autre, sur un écran où l'un est dessiné juste au-dessus de
 * l'autre.
 *
 * **Un jour plein pour ce qui n'a pas de moment**, et c'est délibéré : une livraison
 * ou une rotation n'est ni matin ni après-midi — c'est son heure limite qui le dit, et
 * elle se saisit à part. La résumer sur un seul matin donnerait à l'enveloppe une
 * précision que la donnée n'a pas.
 */
function enveloppeEnDemiJournees(
  phases: readonly PhaseVue[],
  joursPleins: readonly number[],
): { debut: number; fin: number } | null {
  const desBarres = etendueEnDemiJournees(phases);
  let debut = desBarres?.debut ?? Number.POSITIVE_INFINITY;
  let fin = desBarres?.fin ?? Number.NEGATIVE_INFINITY;
  for (const jour of joursPleins) {
    const matin = (jour - 1) * DEMI_JOURNEES_PAR_JOUR;
    if (matin < debut) debut = matin;
    if (matin + 1 > fin) fin = matin + 1;
  }
  return debut > fin ? null : { debut, fin };
}


/**
 * Le bouton « Hacher » peut-il couper une fois de plus ?
 *
 * Chaque reprise dure au moins une demi-journée : N barres totalisant D demi-journées ne
 * se coupent en N + 1 que si D ≥ N + 1. Même règle que le serveur (`hacherEnPartsEgales`).
 */
export function peutHacher(barres: readonly { readonly demiJournees: number }[]): boolean {
  const total = barres.reduce((s, b) => s + b.demiJournees, 0);
  return barres.length > 0 && total >= barres.length + 1;
}

/** L'infobulle du bouton : ce que le prochain clic va faire, ou pourquoi il ne le peut pas. */
export function titreDuHachage(
  barres: readonly { readonly demiJournees: number }[],
  quoi: string,
): string {
  const total = barres.reduce((s, b) => s + b.demiJournees, 0);
  const parts = barres.length + 1;
  if (!peutHacher(barres)) {
    return (
      `Hacher : ${quoi} ne dure que ${String(total)} demi-journée(s) — impossible de la couper ` +
      `en ${String(parts)} reprises d'au moins une demi-journée. Allonger une barre d'abord.`
    );
  }
  return (
    `Hacher : couper ${quoi} en ${String(parts)} reprises égales. ` +
    `La durée totale (${String(total)} demi-journée(s)) ne change pas, aucun euro ne bouge ; ` +
    `la nouvelle reprise se colle à la dernière, à déplacer ensuite.`
  );
}

/**
 * Le jour où une ligne démarre — la dernière colonne figée *(Chris, 22/09/2026)*.
 *
 * *« Rajouter dans la partie figée une dernière colonne avec l'information J1 J2 J3 J4… De
 * cette manière l'utilisateur peut aller directement et rapidement avec la barre de défilement
 * horizontal sur le jour précis. »*
 *
 * ## Une pastille hors barre EST un départ
 *
 * *Réponse de Chris, 22/09/2026 : « oui une livraison sans barre c'est un départ ; en
 * l'occurrence c'est un constat d'huissier qui se fait avant tout démarrage de travaux, mais ce
 * sera une pastille qui se loge hors d'une barre ».*
 *
 * Le constat d'huissier précède les travaux : c'est **le premier jour du chantier**, et c'était
 * précisément la ligne que la colonne refusait d'annoncer. Elle compte donc les barres, les
 * livraisons et les rotations de transport — le symétrique exact de `finOccupee` (moteur,
 * `facturation.ts`), qui pose le CA au dernier jour occupé, pastilles comprises.
 *
 * **Cela ne contredit pas la règle de la barre grise** (« une pastille n'entre jamais dans un
 * résumé », 27/08/2026), et c'est ce qui rend les deux tenables ensemble : un résumé est une
 * DURÉE, et enfermer deux pastilles espacées dans une barre continue affirmerait un travail
 * continu qui n'existe pas. Un départ est un INSTANT — le premier. Prendre le plus tôt de
 * plusieurs instants n'invente aucun intervalle.
 *
 * ## Le plus tôt, et non le premier du tableau
 *
 * Une ligne hachée porte plusieurs barres (« reprendre cette ligne plus tard ») et rien ne
 * garantit qu'elles soient rangées dans l'ordre du temps — elles le sont par `ordre`, qui est
 * celui de leur création. Annoncer J12 pour une ligne qui commence à J3 enverrait chercher au
 * mauvais endroit, c'est-à-dire l'inverse de ce que cette colonne sert à faire.
 *
 * `null` quand la ligne ne porte rien du tout : la colonne y affiche un tiret. Un « J1 » posé
 * par défaut désignerait un jour où il n'y a rien.
 */
export function jourDeDepart(planning: {
  readonly phases: readonly PhaseVue[];
  readonly livraisons: readonly LivraisonVue[];
  readonly transports: readonly TransportVue[];
}): number | null {
  return leplusTot([
    ...planning.phases.map((p) => p.debutRelatif),
    ...planning.livraisons.map((l) => l.jourRelatif),
    ...planning.transports.map((t) => t.jourRelatif),
  ]);
}

/** Le plus petit jour d'une liste, ou `null` si elle est vide. */
function leplusTot(jours: readonly number[]): number | null {
  let plusTot: number | null = null;
  for (const jour of jours) {
    if (plusTot === null || jour < plusTot) plusTot = jour;
  }
  return plusTot;
}

/**
 * Le départ d'une TÂCHE : le premier jour de ce qui est DESSINÉ sur sa ligne.
 *
 * **Les deux mêmes sources que le dessin, et dans le même ordre de priorité** : la barre grise
 * quand elle existe (`barresDuResume`), ses pastilles sinon (`pastillesDuTitre`, qui rend une
 * liste vide dès qu'une barre résume la tâche — c'est la règle du 27/08/2026). La colonne ne
 * peut donc pas désigner un jour où la ligne ne montre rien.
 *
 * **Mesuré le 22/09/2026, et c'est ce qui a imposé cette forme** : compter les pastilles de
 * TOUTES les lignes faisait annoncer J1 à « 1.3 GNT » dont la barre grise commence à J12, J4 à
 * « 2.3 GNT » dessinée à J34. Le jour était vrai quelque part dans le détail replié, et faux
 * là où on le lisait — une adresse qui n'envoie sur rien est pire qu'une case vide.
 *
 * La tâche dont le seul événement est un constat d'huissier n'a, elle, aucune barre grise : ses
 * pastilles sont donc dessinées sur son titre, et la colonne les annonce.
 */
export function jourDeDepartDeLaTache(tache: OuvrageVue): number | null {
  const pastilles = pastillesDuTitre(tache);
  return leplusTot([
    ...barresDuResume(tache).map((p) => p.debutRelatif),
    ...pastilles.livraisons.map(({ livraison }) => livraison.jourRelatif),
    ...pastilles.transports.map(({ transport }) => transport.jourRelatif),
  ]);
}
/** Tous les transports d'une tâche, toutes lignes confondues. */
function transportsDe(tache: OuvrageVue): TransportVue[] {
  return tache.ressources.flatMap((r) => r.planning.transports);
}

/**
 * Les heures de main-d'œuvre du devis figé, par numéro de poste.
 *
 * C'est l'ANCRE qui manquait à cet écran. Étirer une barre multiplie les heures, et
 * `demiJourneesProposees` se recalcule ensuite **sur ces heures déjà multipliées** : la
 * proposition suit donc l'action, et le témoin « posées / proposées » ne peut
 * structurellement jamais être en désaccord après un étirement.
 *
 * *Mesuré le 11/08/2026 sur APO250010 : le poste 1.1.2.2 affichait « 2 / 2 »,
 * parfaitement d'accord, avec ses heures divisées par 4,6 et 6 660 € de déboursé
 * envolés. Rien à l'écran ne le disait.*
 *
 * Comparer au devis figé est la seule référence qui ne bouge pas — c'est la même
 * règle que pour le gain de la contre-étude, qui se mesure toujours contre ce qui a
 * été vendu, quel que soit le point de départ de la version.
 */
function heuresDuDevis(reference: DepoVue | null): Map<string, string> {
  const index = new Map<string, string>();
  if (reference === null) return index;
  const descendre = (noeuds: readonly NoeudVue[]): void => {
    for (const noeud of noeuds) {
      if (noeud.nature === "chapitre") descendre(noeud.enfants);
      else index.set(noeud.numero, noeud.planning.heuresMO);
    }
  };
  descendre(reference.racines);
  return index;
}

/**
 * Les heures que le devis figé porte sur CHAQUE LIGNE, et non sur la tâche.
 *
 * `heuresDuDevis` ancre la tâche ; il en fallait une par ligne, parce que c'est là
 * que le transport se chiffre. *Demande du métier, 14/08/2026 : « je ne vois plus
 * combien il y avait à l'origine sur les heures, je voudrais voir la différence qui
 * a été enlevée ou rajoutée avec les pastilles pour savoir l'impact de mes
 * changements. »*
 *
 * Le problème qu'il décrit est réel et il est structurel : corriger les heures d'une
 * pastille **recale la ligne**. La ligne affiche donc toujours ce qu'elle vaut
 * maintenant, et l'écart posé/chiffré retombe à zéro juste après la correction —
 * exactement au moment où l'on voudrait mesurer ce qu'on vient de faire. La seule
 * référence qui ne bouge pas est le devis figé, comme pour le gain de la
 * contre-étude.
 *
 * **L'appariement se fait par tâche et par RANG**, la même règle que le serveur
 * applique pour ramener une ligne aux heures du devis. Une ligne ajoutée par le
 * conducteur n'a donc pas de référence, et c'est juste : elle n'existe pas au devis,
 * son écart n'est pas « + tout », c'est « rien à comparer ».
 */
function heuresDuDevisParLigne(reference: DepoVue | null): Map<string, string> {
  const index = new Map<string, string>();
  if (reference === null) return index;
  const descendre = (noeuds: readonly NoeudVue[]): void => {
    for (const noeud of noeuds) {
      if (noeud.nature === "chapitre") {
        descendre(noeud.enfants);
        continue;
      }
      for (const r of noeud.ressources) {
        index.set(`${noeud.numero}#${String(r.rang)}`, r.quantiteReelle);
      }
    }
  };
  descendre(reference.racines);
  return index;
}

function etendueDuChantier(lignes: readonly LigneGantt[]): number {
  let fin = 0;
  for (const ligne of lignes) {
    // Les chapitres sont ignorés : leur étendue est celle de leurs tâches, déjà
    // parcourues ici. Les compter deux fois ne changerait rien au maximum, mais
    // ferait dépendre la frise d'un calcul dérivé plutôt que des barres réelles.
    if (ligne.nature !== "tache") continue;
    for (const phase of barresDe(ligne.tache)) {
      if (phase.finRelative > fin) fin = phase.finRelative;
    }
    // **Les livraisons repoussent la frise elles aussi.** Sans cela, un poste de matériaux
    // purs livré à J30 sortirait du cadre : sa pastille se dessinerait au-delà de 100 % et
    // resterait invisible.
    for (const l of livraisonsDe(ligne.tache)) {
      if (l.jourRelatif > fin) fin = l.jourRelatif;
    }
    // **Et les transports aussi** (27/08/2026). Ils manquaient ici, et le trou s'est vu en
    // sortant `transportsDe` du résumé du titre : une tâche peut n'avoir que des rotations —
    // c'est le cas de l'ouvrage 1 de DFCI MAILLAS en production —, et la dernière d'entre
    // elles se serait dessinée hors du cadre.
    for (const t of transportsDe(ligne.tache)) {
      if (t.jourRelatif > fin) fin = t.jourRelatif;
    }
  }
  return Math.max(fin + MARGE_JOURS, JOURS_MINIMUM);
}

/** Toutes les livraisons d'une tâche, toutes lignes confondues. */
function livraisonsDe(tache: OuvrageVue): LivraisonVue[] {
  return tache.ressources.flatMap((r) => r.planning.livraisons);
}

/**
 * Cette tâche porte-t-elle son planning elle-même ?
 *
 * Vrai pour un poste de fournitures pures — un panneau DFCI, un dos d'âne : il n'a
 * aucune ligne en heures, donc rien à quoi accrocher une barre, et le métier a
 * explicitement demandé de pouvoir lui en poser une. Partout ailleurs le titre est un
 * résumé, et c'est le serveur qui refuserait une barre posée dessus.
 */
function porteSesBarres(tache: OuvrageVue): boolean {
  return !tache.ressources.some((r) => r.enHeures);
}

/* ---------------------------------------------------------------------------
 * Une barre
 * ------------------------------------------------------------------------- */

/**
 * Ce qu'un glissé en cours est en train de faire.
 *
 * `deplacer` change le jour de début, `etirer` change la durée. Les deux se
 * ressemblent à la souris et n'ont rien à voir : déplacer une barre ne touche pas au
 * chiffrage, l'étirer le change. C'est pour ça que la poignée d'étirement est un
 * bord distinct, et non le corps de la barre.
 */
type Glisse = {
  readonly phaseId: string;
  readonly quoi: "deplacer" | "etirer";
  /** Position du pointeur au départ, pour mesurer le déplacement. */
  readonly xDepart: number;
  /**
   * Le rang de demi-journée de départ, PAS le jour.
   *
   * C'était `debutRelatif` — un jour — auquel on ajoutait un décalage compté en
   * demi-journées : la barre partait deux fois trop loin, et un après-midi était
   * inatteignable à la souris. Une seule unité de bout en bout, celle du geste.
   */
  readonly indexDepart: number;
  readonly dureeDepart: number;
  /** Largeur d'une demi-journée en pixels, mesurée sur la piste au moment du clic. */
  readonly pasPixels: number;
  /** Décalage courant, en demi-journées. */
  readonly delta: number;
};

/**
 * Une phase, dessinée sur la frise — déplaçable et étirable à la souris.
 *
 * *Le premier jet n'offrait qu'un panneau de saisie. Les mots du métier : « je ne
 * peux pas les bouger de gauche à droite ou bien les étirer ? ». L'architecture le
 * disait pourtant — React plutôt qu'Expo « parce que le Gantt gestuel l'exige ».*
 *
 * Positionnée en pourcentage plutôt qu'en pixels : la frise doit tenir dans la
 * largeur disponible quel que soit le nombre de jours, et une largeur en pixels
 * obligerait à faire défiler dès trente jours.
 *
 * La demi-journée est l'unité de position, pas le jour — sans quoi une phase qui
 * démarre l'après-midi se dessinerait au matin, et le décalage serait invisible à
 * l'œil sur une frise serrée.
 *
 * **Le déplacement s'affiche pendant le glissé, sans rien enregistrer.** Envoyer à
 * chaque pixel écrirait cent fois pour un seul geste, et le journal deviendrait
 * illisible. L'écriture a lieu au relâcher, une fois.
 */
function Barre({
  phase,
  jours,
  nommerJour,
  couleur,
  nature,
  intitule,
  glisse,
  modifiable,
  selectionnee,
  onSelectionner,
  onCommencer,
  onRetirer,
}: {
  phase: PhaseVue;
  jours: number;
  /** Comment ce jour se nomme : « J4 », ou sa date si le chantier en a une. */
  nommerJour: (jour: number) => string;
  couleur: string | null;
  /** La nature de ce qui est planifié — elle donne la teinte quand aucun groupe ne la donne. */
  nature: "mo" | "materiel" | "fourniture" | null;
  /** Ce que la barre planifie, en tête d'infobulle : « _MOICDC Chef de chantier ». */
  intitule: string;
  glisse: Glisse | null;
  modifiable: boolean;
  selectionnee: boolean;
  onSelectionner: () => void;
  onCommencer: (quoi: "deplacer" | "etirer", evenement: React.PointerEvent) => void;
  onRetirer: () => void;
}): JSX.Element {
  const demiJourneesTotal = jours * 2;

  // Aperçu du geste en cours : la barre suit le pointeur avant tout enregistrement.
  const enCours = glisse?.phaseId === phase.id ? glisse : null;
  const dureeAffichee =
    enCours?.quoi === "etirer" ? Math.max(1, phase.demiJournees + enCours.delta) : phase.demiJournees;

  // **Tout se compte en demi-journées ici**, y compris l'aperçu : c'est l'unité du
  // geste, et le plancher porte sur elle. Borner le jour aurait laissé passer un
  // après-midi négatif.
  const debutDemi =
    enCours?.quoi === "deplacer"
      ? Math.max(0, indexDemiJournee(phase) + enCours.delta)
      : indexDemiJournee(phase);
  // Ce que la barre annonce PENDANT le glissé : le jour et le moment où elle
  // atterrirait. Sans ça, tirer d'une demi-journée ne se distingue pas à l'œil d'un
  // geste qui n'a rien fait.
  const atterrissage = depuisIndexDemiJournee(debutDemi);

  return (
    <div
      className={`barre${nature === null ? "" : ` barre--${nature}`}${
        selectionnee ? " barre--selectionnee" : ""
      }${enCours !== null ? " barre--glissee" : ""}`}
      style={{
        left: `${(debutDemi / demiJourneesTotal) * 100}%`,
        width: `${(dureeAffichee / demiJourneesTotal) * 100}%`,
        // La couleur du regroupement l'emporte quand il y en a une : c'est le code
        // couleur que le métier reconnaît sur son classeur. La nature ne sert qu'à
        // teinter ce qu'aucun regroupement ne couvre.
        ...(couleur === null ? {} : { backgroundColor: couleur }),
      }}
      title={
        `${intitule}\n` +
        (enCours?.quoi === "deplacer"
          ? `→ ${nommerJour(atterrissage.debutRelatif)} ${atterrissage.position === "apres_midi" ? "après-midi" : "matin"}`
          : `${nommerJour(phase.debutRelatif)} → ${nommerJour(phase.finRelative)} · ${phase.demiJournees} demi-journée(s)` +
            (phase.position === "apres_midi" ? " · démarre l'après-midi" : "")) +
        (modifiable
          ? "\nGlisser pour déplacer, à la demi-journée · tirer le bord droit pour allonger (change le déboursé de cette ligne)"
          : "")
      }
      onPointerDown={(e) => {
        onSelectionner();
        if (modifiable) onCommencer("deplacer", e);
      }}
    >
      <span className="barre__duree">{dureeAffichee}</span>
      {/* Retirer la barre, sans détour par le panneau : hacher est un geste
          d'essai, et défaire un essai doit coûter le même clic que le faire.
          Retirer ne touche PAS aux heures — la ligne garde son chiffrage, elle
          perd seulement sa place dans le temps. */}
      {modifiable && (
        <button
          type="button"
          className="barre__retirer"
          title="Retirer cette barre — le chiffrage de la ligne ne change pas"
          onPointerDown={(e) => {
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
            onRetirer();
          }}
        >
          ×
        </button>
      )}
      {modifiable && (
        <span
          className="barre__poignee"
          title="Allonger ou raccourcir — les heures de cette ligne et son déboursé suivront"
          onPointerDown={(e) => {
            // Sans cette interruption, le corps de la barre démarrerait aussi un
            // déplacement : on étirerait et on déplacerait du même geste.
            e.stopPropagation();
            onSelectionner();
            onCommencer("etirer", e);
          }}
        />
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Une livraison
 * ------------------------------------------------------------------------- */

/**
 * De quelle couleur se dessine une livraison.
 *
 * *Demandé par Chris le 12/08/2026 : « une grosse pastille — verte pour un matériau du
 * groupe, jaune pour l'externe ».*
 *
 * Le troisième état est celui qui compte : **du groupe, mais sans carrière désignée**.
 * C'est l'état à remplir — « c'est de chez nous, je ne sais pas encore d'où » —, déjà
 * affiché en ambre sur l'écran du déboursé. Ici, un vert CREUX plutôt qu'une nuance
 * d'ambre : à côté du jaune de l'externe, deux ocres ne se distingueraient pas, alors
 * qu'un contour vide se remarque et réclame la réponse qui manque.
 *
 * *Proposé à Chris le 12/08/2026, resté sans réponse ; appliqué et signalé comme tel.*
 */
function couleurDeLaLivraison(ressource: RessourceVue): "groupe" | "groupe-creux" | "externe" {
  if (!ressource.duGroupe) return "externe";
  return ressource.carriereId === null ? "groupe-creux" : "groupe";
}

/**
 * Une livraison, dessinée sur la frise — déplaçable à la souris.
 *
 * **Ronde et non rectangulaire, et c'est un choix qui parle** : une livraison est un
 * instant, pas une durée. La forme dit à elle seule qu'il n'y a pas de bord droit à tirer,
 * là où une barre invite à l'étirement. Ce sont les « deux natures de barres » du métier.
 *
 * **Le déplacement change le jour, et rien d'autre.** Aucun euro ne bouge : le volume
 * appartient à la ligne, et la déplacer dit seulement « le camion vient jeudi ». C'est
 * pour ça qu'elle s'applique sans demander confirmation, contrairement à un étirement.
 */
function Pastille({
  livraison,
  ressource,
  unite,
  jours,
  nommerJour,
  glisse,
  modifiable,
  selectionnee,
  onSelectionner,
  onCommencer,
  onRetirer,
}: {
  livraison: LivraisonVue;
  ressource: RessourceVue;
  unite: string;
  jours: number;
  /** Comment ce jour se nomme : « J4 », ou sa date si le chantier en a une. */
  nommerJour: (jour: number) => string;
  glisse: GlisseLivraison | null;
  modifiable: boolean;
  selectionnee: boolean;
  onSelectionner: () => void;
  onCommencer: (evenement: React.PointerEvent) => void;
  onRetirer: () => void;
}): JSX.Element {
  const enCours = glisse?.livraisonId === livraison.id ? glisse : null;
  // Le jour affiché pendant le geste : la pastille suit le pointeur avant tout
  // enregistrement. Bornée à J1 — rien ne se livre avant le début du chantier.
  const jour = enCours === null ? livraison.jourRelatif : Math.max(1, livraison.jourRelatif + enCours.delta);
  const couleur = couleurDeLaLivraison(ressource);

  return (
    <div
      className={
        `pastille-livraison pastille-livraison--${couleur}` +
        // Une échéance posée se signale par un anneau intérieur : c'est une contrainte, et
        // elle doit se distinguer d'une pastille libre sans qu'on ait à survoler chacune.
        (livraison.heureLimite === null ? "" : " pastille-livraison--limitee") +
        (selectionnee ? " pastille-livraison--selectionnee" : "")
      }
      // Centrée sur le MILIEU de sa journée : une livraison n'occupe pas la journée, elle
      // tombe dedans. L'aligner sur le bord gauche la ferait paraître appartenir au jour
      // précédent sur une frise serrée.
      style={{ left: `${((jour - 0.5) / jours) * 100}%` }}
      title={
        `${ressource.code} ${ressource.designation}\n` +
        // L'échéance porte sur le VOLUME entier, et l'infobulle le dit dans ces termes :
        // « les 1 000 T avant 11:00 », jamais « livraison à 11:00 ».
        (livraison.heureLimite === null
          ? `${formaterNombre(livraison.quantite, 2)} ${unite} le ${nommerJour(jour)}, sans heure imposée`
          : `les ${formaterNombre(livraison.quantite, 2)} ${unite} AVANT ${livraison.heureLimite}, le ${nommerJour(jour)}`) +
        "\n" +
        (couleur === "externe"
          ? "Fourniture externe"
          : couleur === "groupe"
            ? `Carrière du groupe : ${ressource.carriereCode ?? "?"}`
            : "Du groupe — la carrière reste à désigner") +
        (modifiable
          ? "\nGlisser pour changer de jour (aucun euro ne bouge) · cliquer pour le volume et l'heure"
          : "")
      }
      onPointerDown={(e) => {
        // **Sans cette interruption, saisir une pastille en créerait une nouvelle dessous** :
        // la piste écoute le même événement pour poser une livraison au jour cliqué.
        e.stopPropagation();
        onSelectionner();
        if (modifiable) onCommencer(e);
      }}
    >
      <span className="pastille-livraison__ordre">{livraison.ordre}</span>
      {modifiable && (
        <button
          type="button"
          className="barre__retirer"
          title="Retirer cette livraison — le volume retourne au reste à livrer, la quantité de la ligne ne change pas"
          onPointerDown={(e) => {
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
            onRetirer();
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}

/**
 * Une pastille de transport sur la frise — déplaçable, comme une livraison.
 *
 * **Ronde elle aussi**, et pour la même raison : un camion vient un jour, il n'occupe pas
 * une durée qu'on étirerait. Mais elle porte ses HEURES en clair plutôt qu'un rang : c'est
 * la grandeur qui coûte de l'argent, et la voir sur la frise évite d'avoir à descendre dans
 * la carte pour savoir si la rotation dure trois heures ou huit.
 *
 * **Le glissé change le jour, et rien d'autre** — aucun euro ne bouge, exactement comme
 * pour une livraison. Les heures, elles, ne se saisissent que dans la carte : un geste à la
 * souris qui déplacerait des euros serait trop facile à faire sans le vouloir.
 */
function PastilleTransport({
  transport,
  ressource,
  jours,
  nommerJour,
  glisse,
  modifiable,
  selectionnee,
  onSelectionner,
  onCommencer,
  onRetirer,
}: {
  transport: TransportVue;
  ressource: RessourceVue;
  jours: number;
  nommerJour: (jour: number) => string;
  glisse: GlisseLivraison | null;
  modifiable: boolean;
  selectionnee: boolean;
  onSelectionner: () => void;
  onCommencer: (evenement: React.PointerEvent) => void;
  onRetirer: () => void;
}): JSX.Element {
  const enCours =
    glisse !== null && glisse.livraisonId === transport.id && glisse.nature === "transport"
      ? glisse
      : null;
  const jour =
    enCours === null ? transport.jourRelatif : Math.max(1, transport.jourRelatif + enCours.delta);
  const unites = ressource.unites ?? 1;

  return (
    <div
      className={
        "pastille-livraison pastille-livraison--transport" +
        (selectionnee ? " pastille-livraison--selectionnee" : "")
      }
      style={{ left: `${((jour - 0.5) / jours) * 100}%` }}
      title={
        `${ressource.code} ${ressource.designation}\n` +
        `${formaterNombre(transport.heures, 2)} h le ${nommerJour(jour)}` +
        (unites > 1
          ? `, pour chacune des ${unites} unités — soit ${formaterNombre(String(Number(transport.heures) * unites), 2)} h au déboursé`
          : "") +
        (modifiable
          ? "\nGlisser pour changer de jour (aucun euro ne bouge) · les heures se corrigent dans la carte, et elles, elles coûtent"
          : "")
      }
      onPointerDown={(e) => {
        e.stopPropagation();
        onSelectionner();
        if (modifiable) onCommencer(e);
      }}
    >
      <span className="pastille-livraison__ordre">{formaterNombre(transport.heures, 1)}</span>
      {modifiable && (
        <button
          type="button"
          className="barre__retirer"
          title="Retirer ce transport — les heures de la ligne se recalent sur ce qui reste posé, donc le déboursé change"
          onPointerDown={(e) => {
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
            onRetirer();
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}

/** Ce qu'un glissé de pastille est en train de faire : changer son jour, et rien d'autre. */
type GlisseLivraison = {
  readonly livraisonId: string;
  /**
   * Laquelle des deux pastilles on déplace.
   *
   * Un seul état pour les deux natures, et non deux jumeaux : le geste est rigoureusement
   * le même — un jour qui change, aucun euro qui bouge — et seule la route d'arrivée
   * diffère. Deux états parallèles auraient fini par se désynchroniser sur le suivi du
   * pointeur, qui n'a rien à voir avec ce qu'on déplace.
   */
  readonly nature: "livraison" | "transport";
  readonly xDepart: number;
  /** Largeur d'un JOUR en pixels — une livraison ne connaît pas la demi-journée. */
  readonly pasPixels: number;
  readonly delta: number;
};

/**
 * Le glissé d'une barre GRISE : un chapitre ou un ouvrage entier, avec tout son détail.
 *
 * *Demande de Chris, 19/08/2026 : « quand tout est replié, pouvoir avancer ou reculer les
 * barres grises, et que tout le détail bouge automatiquement avec ».*
 *
 * C'est le retour sur la moitié d'une décision du 12/08 — la barre du résumé était « ni
 * déplaçable ni étirable ». Elle reste **non étirable** : étirer un résumé ne dirait pas
 * laquelle de ses lignes s'allonge, donc quel déboursé change. Déplacer, en revanche, n'a
 * qu'un sens possible et ne coûte rien : tout part ensemble, les écarts internes sont
 * conservés, aucune durée ni aucun euro ne bouge.
 *
 * Un état distinct de `Glisse` parce que la cible n'est pas de même nature : ici on ne
 * désigne pas une barre par son identifiant mais un BLOC par son numéro, et c'est le
 * serveur qui sait ce qu'il contient. Les mêler obligerait chaque lecture de l'état à
 * demander « barre ou bloc ? » avant de savoir ce qu'elle tient.
 */
type GlisseBloc = {
  readonly quoi: "ouvrage" | "chapitre";
  readonly numero: string;
  readonly xDepart: number;
  /** Largeur d'une DEMI-JOURNÉE : le bloc se recale à la demi-journée, comme une barre. */
  readonly pasPixels: number;
  readonly delta: number;
  /** Les bornes de la barre grise au départ, en demi-journées : l'aperçu les décale. */
  readonly debutDemi: number;
  readonly finDemi: number;
  /**
   * Vrai quand on tient UNE période d'une tâche hachée, et non la tâche entière : seules ses
   * barres partent, entre `debutDemi` et `finDemi` (Chris, 25/09/2026).
   */
  readonly seulementLaPeriode?: boolean;
};


const MOTIF_VOLUME = /^\d+([.,]\d+)?$/;
const MOTIF_JOUR = /^\d+$/;
const MOTIF_HEURE = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * La barre de saisie des livraisons d'une ligne — **sur la ligne, pas dans un panneau**.
 *
 * *Ce que Chris n'a pas trouvé, le 12/08/2026 : « je ne comprends pas comment ajouter des
 * pastilles et surtout comment saisir les quantités et les heures limites ».*
 *
 * Deux fautes d'ergonomie de ma part, et elles s'additionnaient :
 *
 *  1. **j'avais retiré le bouton** au profit d'un clic sur la piste, annoncé par une note
 *     grise de 0,7 rem. Un geste qui ne se voit pas n'existe pas ;
 *  2. **la saisie vivait dans un panneau en fin de page.** Sur un tableau de cent lignes,
 *     il s'ouvrait hors de l'écran — donc jamais vu.
 *
 * D'où ceci : tout est **ici**, sous la ligne, en clair. Un groupe de trois champs par
 * pastille — jour, volume, heure limite —, un bouton pour en ajouter une, un pour la
 * couper en deux, un pour la retirer. Rien à découvrir, rien à faire défiler.
 *
 * Le clic sur la piste reste offert : il est plus rapide quand on sait où l'on veut poser.
 * Mais il n'est plus le SEUL chemin, et c'était le défaut.
 */
function LivraisonsDeLaLigne({
  ressource,
  carrieres,
  attelages,
  jours,
  dateDuJour,
  modifiable,
  enAttente,
  choisie,
  onChoisir,
  onAjouter,
  onModifier,
  onRepartir,
  onRetirer,
}: {
  ressource: RessourceVue;
  /** Les sites de provenance proposables sur la pastille (039). */
  carrieres: readonly Carriere[];
  /**
   * Les ATTELAGES du poste, pour désigner celui qui emporte chaque pastille (076).
   *
   * *« Il faut donc choisir dans le menu déroulant les couplages »* et *« spécifie dans le
   * couplage le tonnage possible pour aiguiller un peu mieux »* (Chris, 09/09/2026). Le
   * regroupement et le tonnage viennent du SERVEUR : les refaire ici finirait par proposer un
   * attelage que le calcul ne forme pas.
   *
   * La liste s affiche dès qu il y a UN attelage (10/09/2026) : un poste qui n en porte qu un
   * n a rien à désigner, mais le conducteur a besoin de lire avec quoi son tonnage part. Il y
   * est alors présélectionné, sans option vide.
   */
  attelages: readonly { rang: number; libelle: string; tonnage: string | null }[];
  jours: number;
  /**
   * La date réelle d'un jour relatif, ou `null` si le chantier n'a pas de date.
   *
   * Affichée à côté du champ `J` plutôt qu'à sa place : **c'est le jour relatif qui
   * s'enregistre** (`livraison.jour_relatif`), et un champ qui accepterait une date
   * laisserait croire qu'on peut livrer un samedi. La date est le rendu, le jour est
   * la donnée — et une livraison au J8 reste au J8 si le chantier est décalé.
   */
  dateDuJour: (jour: number) => string | null;
  modifiable: boolean;
  enAttente: boolean;
  choisie: string | null;
  onChoisir: (id: string) => void;
  onAjouter: () => void;
  onModifier: (
    id: string,
    changement: {
      quantite?: string;
      jourRelatif?: number;
      heureLimite?: string | null;
      /** Le RANG de la ligne de camion qui emporte ce tonnage (076), ou nul pour « pas designe ». */
      attelageRang?: number | null;
    } & ChangementAcheminement,
  ) => void;
  onRepartir: (id: string) => void;
  onRetirer: (id: string) => void;
}): JSX.Element {
  const pl = ressource.planning;
  const reste = Number(pl.resteALivrer);
  const couleur = couleurDeLaLivraison(ressource);

  return (
    <div className="livraisons">
      <span className="livraisons__titre">
        Livraisons de {formaterNombre(ressource.quantiteReelle, 2)} {ressource.unite}
        {" · "}
        {formaterNombre(pl.quantiteLivree, 2)} placé(s)
        {Math.abs(reste) >= 0.005 && (
          <strong className={reste < 0 ? "gantt__cache" : "gantt__reste"}>
            {reste < 0
              ? ` — ${formaterNombre(String(-reste), 2)} de trop`
              : ` — reste ${formaterNombre(pl.resteALivrer, 2)}`}
          </strong>
        )}
      </span>

      {pl.livraisons.map((l) => (
        <span
          key={l.id}
          className={`carte-livraison carte-livraison--${couleur}${
            l.id === choisie ? " carte-livraison--choisie" : ""
          }`}
          onPointerDown={() => {
            onChoisir(l.id);
          }}
        >
          {/* Le jour. Le libellé « J » est dans la carte et non dans le champ : taper
              « J5 » serait refusé, et un champ qui refuse ce qu'il affiche déroute. */}
          <span className="carte-livraison__j">J</span>
          <ChampCourt
            valeur={String(l.jourRelatif)}
            largeur="2.4rem"
            motif={MOTIF_JOUR}
            invite="jour"
            titre={
              `Jour de livraison, entre J1 et J${jours}.` +
              (dateDuJour(l.jourRelatif) === null
                ? " La date réelle apparaîtra dès que le chantier portera une date de démarrage."
                : `\nSoit le ${dateDuJour(l.jourRelatif) ?? ""} — jours ouvrés comptés depuis le démarrage.`)
            }
            modifiable={modifiable}
            enAttente={enAttente}
            onValider={(v) => {
              onModifier(l.id, { jourRelatif: Number(v) });
            }}
          />
          {/* La date, à côté du jour et non à sa place : c'est le jour relatif qui
              s'enregistre, et le voir à côté de sa date est ce qui permet de vérifier
              qu'une échéance tombe bien le jour annoncé au client. */}
          {dateDuJour(l.jourRelatif) !== null && (
            <span className="carte-livraison__date">{dateDuJour(l.jourRelatif)}</span>
          )}
          <ChampCourt
            valeur={l.quantite}
            largeur="5rem"
            motif={MOTIF_VOLUME}
            invite="volume"
            titre={
              `Volume livré ce jour-là, en ${ressource.unite}.\n` +
              "La quantité de la ligne ne change pas : elle appartient à la contre-étude. " +
              "Seul le reste à placer se recalcule."
            }
            modifiable={modifiable}
            enAttente={enAttente}
            onValider={(v) => {
              // La virgule décimale est acceptée à la saisie — c'est ce qu'un clavier
              // français produit — et convertie ici : l'API veut un point.
              onModifier(l.id, { quantite: v.replace(",", ".") });
            }}
          />
          <span className="carte-livraison__unite">{ressource.unite}</span>
          {/* L'heure LIMITE. « avant » est écrit dans la carte, pas seulement en
              infobulle : c'est une échéance, et le mot doit être sous les yeux. */}
          <span className="carte-livraison__avant">avant</span>
          <ChampCourt
            valeur={l.heureLimite ?? ""}
            largeur="3.6rem"
            motif={MOTIF_HEURE}
            invite="--:--"
            videAutorise
            titre={
              "Heure LIMITE : le volume entier de cette pastille doit être sur le chantier " +
              "AVANT cette heure.\n" +
              "Vide = aucune échéance, l'heure est indifférente."
            }
            modifiable={modifiable}
            enAttente={enAttente}
            onValider={(v) => {
              onModifier(l.id, { heureLimite: v === "" ? null : v });
            }}
          />

          {/*
            **L'acheminement du matériau** (039) — le même panneau que sur une rotation.

            *« Il faut rajouter les informations sur l'article dans la carte de la pastille :
            mode de livraison, avec la coche groupe, un champ fournisseur puis ville puis
            CP »* (métier, 21/08/2026).

            La coche « groupe » et le site arrivent **pré-remplis depuis la ligne de
            contre-étude** — c'est le serveur qui le fait à la pose. Les corriger ici ne
            touche pas la ligne : la contre-étude dit d'où vient le matériau en général,
            cette pastille dit d'où vient CE volume-là.
          */}
          <Acheminement
            porteur={l}
            carrieres={carrieres}
            modifiable={modifiable}
            enAttente={enAttente}
            onModifier={(changement) => {
              onModifier(l.id, changement);
            }}
          />

          {/*
            L'ATTELAGE QUI EMPORTE CE TONNAGE (076, 09/09/2026).

            *« Régis veut faire les premiers voyages en 8×4 puis après en tracteur et benne […]
            on peut imaginer de créer une pastille en spécifiant 8×4 dessus, ou 4×2, ou benne,
            pour que tu comprennes la répartition »* (Chris).

            LA LISTE S'AFFICHE DÈS QU'IL Y A UN ATTELAGE — correction de Chris, 10/09/2026.

            *« Pourquoi dans MAILLAS je ne vois pas comme dans CAMPET le champ couplage de camion
            sur le détail de la ligne des matériaux ? Même s'il y a un seul couplage tu peux faire
            apparaître le champ […] et tu peux du coup renseigner directement le bon couplage
            quand il y en a qu'un. »*

            Elle ne s'affichait qu'au-delà d'un attelage, au motif qu'un poste qui n'en porte
            qu'un n'a rien à désigner. C'est vrai du calcul et faux de l'écran : **le conducteur
            a besoin de LIRE avec quoi son tonnage part**, et un champ absent ne dit pas « un
            seul attelage », il ne dit rien. MAILLAS n'affichait donc aucun camion là où CAMPET
            en montrait deux, et rien n'expliquait la différence.

            Avec un seul attelage, il est **présélectionné et sans option vide** : il n'y a rien
            à ne pas choisir, et proposer « — attelage ? » inviterait à défaire une réponse
            certaine. Le conducteur voit `PORTEUR 8X4 — 17 T`, ce qui est exactement ce que le
            calcul retiendra.

            Elle propose TOUTES les lignes de matériel de transport, tracteur compris : désigner
            le tracteur ou sa benne revient au même, ils forment un seul attelage d'après les
            couplages du Paramétrage transport. L'écran n'a donc pas à savoir laquelle des deux
            porte le tonnage, et ne peut pas refuser un choix que le conducteur lit comme juste.
          */}
          {attelages.length > 0 && (
            <label className="carte-livraison__attelage" title={AIDE_ATTELAGE}>
              <select
                value={attelageAffiche(attelages, l.attelageRang).valeur}
                disabled={!modifiable || enAttente}
                onChange={(e) => {
                  onModifier(l.id, {
                    attelageRang: e.target.value === "" ? null : Number(e.target.value),
                  });
                }}
              >
                {attelageAffiche(attelages, l.attelageRang).avecOptionVide && (
                  <option value="">— attelage ?</option>
                )}
                {attelages.map((a) => (
                  <option key={a.rang} value={String(a.rang)}>
                    {/* LE TONNAGE DANS LE LIBELLÉ — « spécifie dans le couplage le tonnage
                        possible pour aiguiller un peu mieux » (Chris). C'est lui qui permet de
                        choisir : 30 T de préparation en 8×4 puis 200 T en benne se décide en
                        lisant 17 T et 31 T, pas en retenant quel code porte quoi. */}
                    {a.libelle}
                    {a.tonnage === null ? " — charge inconnue" : ` — ${a.tonnage} T`}
                  </option>
                ))}
              </select>
            </label>
          )}

          {modifiable && (
            <>
              <button
                type="button"
                className="carte-livraison__action"
                disabled={enAttente}
                title="Couper en deux : la moitié reste ici, l'autre part le jour suivant. Le volume total ne change pas, et l'échéance est reportée sur les deux."
                onClick={() => {
                  onRepartir(l.id);
                }}
              >
                ÷
              </button>
              <button
                type="button"
                className="carte-livraison__action"
                disabled={enAttente}
                title="Retirer cette livraison — son volume retourne au reste à placer, la quantité de la ligne ne change pas"
                onClick={() => {
                  onRetirer(l.id);
                }}
              >
                ×
              </button>
            </>
          )}
        </span>
      ))}

      {/*
        Ajouter une livraison : **le bouton que j'avais retiré, et qu'il fallait garder**.
        Il pose le reste au premier jour libre après la dernière pastille — le conducteur
        corrige le jour dans le champ, ou glisse la pastille sur la frise.
      */}
      {modifiable && reste > 0.0000005 && (
        <button
          type="button"
          className="livraisons__ajouter"
          disabled={enAttente}
          title={
            `Ajouter une livraison de ${formaterNombre(pl.resteALivrer, 2)} ${ressource.unite} — ` +
            "tout ce qui reste à placer. Le jour et le volume se corrigent ensuite ici même, " +
            "ou en glissant la pastille sur la frise."
          }
          onClick={onAjouter}
        >
          + livraison de {formaterNombre(pl.resteALivrer, 2)} {ressource.unite}
        </button>
      )}
      {modifiable && reste <= 0.0000005 && pl.livraisons.length > 0 && (
        <span className="note livraisons__solde">
          tout est placé — réduire un volume pour en dégager
        </span>
      )}
    </div>
  );
}


/**
 * La barre de saisie des transports d'une ligne — le jumeau de celle des livraisons.
 *
 * *« Sur les lignes avec les codes matériel de transport, le conducteur doit pouvoir
 * utiliser le même système de pastille que pour les articles […] En fait le système
 * existant pour les articles me va très bien, tu peux calquer là-dessus »* (métier,
 * 13/08/2026). C'est donc calqué, et les différences sont exactement les trois que le
 * métier a nommées :
 *
 *  1. **on saisit des heures, pas un volume** — et ces heures sont celles d'UNE unité :
 *     trois camions à 8 h font 24 h au déboursé ;
 *  2. **il n'y a pas d'heure limite.** Une livraison a une échéance parce qu'elle arrive ;
 *     un transport occupe un temps ;
 *  3. **corriger les heures déplace des euros**, ce qu'aucune livraison ne fait. D'où
 *     l'avertissement chiffré sous la ligne, et le même bandeau qu'un étirement de barre.
 */
function TransportsDeLaLigne({
  ressource,
  heuresDevis,
  carrieres,
  jours,
  dateDuJour,
  modifiable,
  enAttente,
  choisi,
  onChoisir,
  onAjouter,
  onModifier,
  onRepartir,
  onRetirer,
  fige,
  onFiger,
}: {
  ressource: RessourceVue;
  /** Ce que le devis figé chiffrait sur CETTE ligne. `null` = ligne ajoutée depuis. */
  heuresDevis: string | null;
  /** La ligne est-elle figée face à « Organiser le transport des matériaux » ? (092) */
  fige: boolean;
  onFiger: (fige: boolean) => void;
  carrieres: readonly Carriere[];
  jours: number;
  dateDuJour: (jour: number) => string | null;
  modifiable: boolean;
  enAttente: boolean;
  choisi: string | null;
  onChoisir: (id: string) => void;
  onAjouter: () => void;
  onModifier: (id: string, changement: DemandeTransport) => void;
  onRepartir: (id: string) => void;
  onRetirer: (id: string) => void;
}): JSX.Element {
  const pl = ressource.planning;
  const reste = Number(pl.resteAPoser);
  const depassement = Number(pl.depassementTransport);
  const unites = ressource.unites ?? 1;

  /**
   * L'impact des corrections d'heures sur le chiffrage, mesuré contre le devis figé.
   *
   * Distinct du dépassement, et les deux ont besoin d'être là : le dépassement dit
   * « ce que j'ai placé au calendrier ne correspond pas à ce que la ligne chiffre »,
   * celui-ci dit « la ligne ne chiffre plus ce qui a été vendu ». Le premier se
   * referme dès qu'on corrige une pastille — c'est précisément ce qui faisait perdre
   * de vue le second.
   */
  const origine = heuresDevis === null ? null : Number(heuresDevis);
  const ecartDevis = origine === null ? null : Number(ressource.quantiteReelle) - origine;

  return (
    <div className="livraisons livraisons--transport">
      <span className="livraisons__titre">
        Transports de {formaterNombre(ressource.quantiteReelle, 2)} h
        {unites > 1 && <span className="note"> ({unites} × {formaterNombre(String(Number(ressource.quantiteReelle) / unites), 2)} h)</span>}
        {" · "}
        {formaterNombre(pl.heuresPosees, 2)} h posées
        {Math.abs(depassement) >= 0.005 && (
          <strong className={depassement < 0 ? "gantt__reste" : "gantt__cache"}>
            {depassement < 0
              ? ` — ${formaterNombre(String(-depassement), 2)} h à placer`
              : ` — dépassement de ${formaterNombre(String(depassement), 2)} h`}
          </strong>
        )}
      </span>

      {/* L'ancre : ce que le devis figé chiffrait, et de combien on s'en écarte. Sur sa
          propre ligne plutôt qu'à la suite du titre — trois nombres et deux écarts sur
          une seule ligne ne se lisent plus, et c'est celui-ci qu'on vient chercher. */}
      <span className="livraisons__origine">
        {origine === null ? (
          <span className="note">Ligne ajoutée : le devis figé n'a rien à quoi la comparer.</span>
        ) : (
          <>
            Devis figé : <strong>{formaterNombre(String(origine), 2)} h</strong>
            {ecartDevis !== null && Math.abs(ecartDevis) >= 0.005 ? (
              <strong className={ecartDevis > 0 ? "gantt__cache" : "gantt__gain"}>
                {ecartDevis > 0
                  ? ` — ${formaterNombre(String(ecartDevis), 2)} h ajoutées`
                  : ` — ${formaterNombre(String(-ecartDevis), 2)} h enlevées`}
              </strong>
            ) : (
              <span className="note"> — inchangé</span>
            )}
          </>
        )}
      </span>

      {pl.transports.map((t) => (
        <span
          key={t.id}
          className={`carte-livraison carte-livraison--transport${
            t.id === choisi ? " carte-livraison--choisie" : ""
          }`}
          onPointerDown={() => {
            onChoisir(t.id);
          }}
        >
          <span className="carte-livraison__j">J</span>
          <ChampCourt
            valeur={String(t.jourRelatif)}
            largeur="2.4rem"
            motif={MOTIF_JOUR}
            invite="jour"
            titre={
              `Jour du transport, entre J1 et J${jours}. Le déplacer ne coûte rien.` +
              (dateDuJour(t.jourRelatif) === null
                ? " La date réelle apparaîtra dès que le chantier portera une date de démarrage."
                : `\nSoit le ${dateDuJour(t.jourRelatif) ?? ""} — jours ouvrés comptés depuis le démarrage.`)
            }
            modifiable={modifiable}
            enAttente={enAttente}
            onValider={(v) => {
              onModifier(t.id, { jourRelatif: Number(v) });
            }}
          />
          {dateDuJour(t.jourRelatif) !== null && (
            <span className="carte-livraison__date">{dateDuJour(t.jourRelatif)}</span>
          )}
          <ChampCourt
            valeur={t.heures}
            largeur="4rem"
            motif={MOTIF_VOLUME}
            invite="heures"
            titre={
              `Heures qu'UNE unité passe sur le chantier ce jour-là.\n` +
              `Cette rotation engage ${formaterNombre(String(Number(t.heures) * (t.unites ?? unites)), 2)} h au déboursé (${t.unites ?? unites} × ces heures).\n` +
              "ATTENTION : corriger ces heures change les heures de la ligne, donc son déboursé. " +
              "Déplacer la pastille ou changer son équipe, non."
            }
            modifiable={modifiable}
            enAttente={enAttente}
            onValider={(v) => {
              onModifier(t.id, { heures: v.replace(",", ".") });
            }}
          />
          <span className="carte-livraison__unite">h</span>

          {/* L'effectif de CETTE rotation (021). Cinq camions le premier jour, deux en
              régime de croisière : un nombre par pastille, pas un par ligne. */}
          <span className="carte-livraison__avant">×</span>
          <ChampCourt
            valeur={t.unites === null ? "" : String(t.unites)}
            largeur="2.6rem"
            motif={MOTIF_JOUR}
            invite={String(unites)}
            videAutorise
            titre={
              "Combien de camions (ou de chauffeurs) sur CETTE rotation.\n" +
              `Vide = celui de la ligne, soit ${unites}.\n` +
              "Change les heures engagées, donc l'écart affiché — mais ne déplace aucun euro : " +
              "corriger les heures est le geste qui coûte."
            }
            modifiable={modifiable}
            enAttente={enAttente}
            onValider={(v) => {
              onModifier(t.id, { unites: v === "" ? null : Number(v) });
            }}
          />

          {/* L'heure LIMITE, comme sur une livraison — et « avant » est écrit dans la
              carte, pas seulement en infobulle : c'est une échéance. */}
          <span className="carte-livraison__avant">avant</span>
          <ChampCourt
            valeur={t.heureLimite ?? ""}
            largeur="3.6rem"
            motif={MOTIF_HEURE}
            invite="--:--"
            videAutorise
            titre={
              "Heure LIMITE : le transport doit être sur le chantier AVANT cette heure.\n" +
              "Vide = aucune échéance. Ne coûte rien : c'est une contrainte de calendrier."
            }
            modifiable={modifiable}
            enAttente={enAttente}
            onValider={(v) => {
              onModifier(t.id, { heureLimite: v === "" ? null : v });
            }}
          />

          <Acheminement
            porteur={t}
            carrieres={carrieres}
            modifiable={modifiable}
            enAttente={enAttente}
            onModifier={(changement) => {
              onModifier(t.id, changement);
            }}
            apresGroupe={
              /*
                **Figer face au calcul automatique** (092) — *Chris, 25/09/2026 : « une case à
                cocher pour figer le calcul des pastilles afin que lorsqu'on relance et qu'on
                reclique sur organiser le transport il ne remodifie pas les calculs sur les lignes
                cochées »*, posée *« juste après la case à cocher groupe »*.

                Elle vaut pour la LIGNE entière : le calcul efface et repose toutes ses rotations
                d'un coup, il n'y a donc rien à figer pastille par pastille. Cochée sur une carte,
                elle l'est sur toutes celles de la ligne — et l'infobulle le dit.
              */
              <label
                title={
                  "Figer : « Organiser le transport des matériaux » ne touche plus à cette ligne — " +
                  "ni à ses pastilles, ni à ses heures. S'applique à toutes les pastilles de la " +
                  "ligne. Tout reste modifiable à la main."
                }
              >
                <input
                  type="checkbox"
                  checked={fige}
                  disabled={!modifiable || enAttente}
                  onChange={(e) => {
                    onFiger(e.currentTarget.checked);
                  }}
                />
                {fige ? <strong> figé</strong> : " figer"}
              </label>
            }
          />

          {modifiable && (
            <>
              <button
                type="button"
                className="carte-livraison__action"
                disabled={enAttente}
                title="Couper en deux : la moitié des heures reste ici, l'autre part le jour suivant, avec la même équipe et la même provenance. Le total ne change pas, et aucun euro ne bouge."
                onClick={() => {
                  onRepartir(t.id);
                }}
              >
                ÷
              </button>
              <button
                type="button"
                className="carte-livraison__action"
                disabled={enAttente}
                title="Retirer cette rotation — ses heures retournent au reste à placer, le chiffrage de la ligne ne change pas"
                onClick={() => {
                  onRetirer(t.id);
                }}
              >
                ×
              </button>
            </>
          )}
        </span>
      ))}

      {/* La valeur annoncée est celle que le serveur posera : le reste BORNÉ à une
          journée. Annoncer le reste brut ferait promettre au bouton une pastille de 27 h
          que la base refuse — le défaut trouvé sur APO250010 le 13/08/2026. */}
      {modifiable && reste > 0.0000005 && (
        <button
          type="button"
          className="livraisons__ajouter"
          disabled={enAttente}
          title={
            `Ajouter un transport de ${formaterNombre(pl.heuresProposees, 2)} h — aucun euro ` +
            "ne bouge tant qu'on reste dans ce qui est chiffré." +
            (Number(pl.heuresProposees) < reste
              ? `\nIl restera ${formaterNombre(String(reste - Number(pl.heuresProposees)), 2)} h à placer : une pastille tient dans une journée.`
              : "") +
            "\nLe jour et les heures se corrigent ensuite."
          }
          onClick={onAjouter}
        >
          + transport de {formaterNombre(pl.heuresProposees, 2)} h
        </button>
      )}
      {modifiable && reste <= 0.0000005 && (
        <span className="note livraisons__solde">
          tout est placé — ajouter un jour se fait en corrigeant les heures d'une pastille
        </span>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Le nombre d'unités
 * ------------------------------------------------------------------------- */

/**
 * Un petit champ numérique qui n'enregistre qu'au départ du curseur.
 *
 * **Pas à la frappe**, et ce n'est pas un détail : saisir « 12 » passerait par « 1 »,
 * ce qui écrirait un nombre d'une unité puis de douze — deux lignes de journal
 * pour une décision, et un recalcul intermédiaire faux. Le vider et sortir remet à
 * « non renseigné », qui n'est pas la même chose que 1.
 */
function Nombre({
  valeur,
  propose,
  titre,
  modifiable,
  enAttente,
  onValider,
}: {
  valeur: number | null;
  /** Ce que l'outil suggère, affiché en filigrane quand rien n'est saisi. */
  propose: number;
  titre: string;
  modifiable: boolean;
  enAttente: boolean;
  onValider: (valeur: number | null) => void;
}): JSX.Element {
  const [saisie, setSaisie] = useState<string | null>(null);
  const affiche = saisie ?? (valeur === null ? "" : String(valeur));

  /**
   * **La saisie s'enregistre seule, peu après la frappe.**
   *
   * *Signalé trois fois par Chris le 12/08/2026 : « quand on augmente les équipes,
   * cela ne réduit pas le nombre de demi-journées ». Le mécanisme serveur était juste
   * — mesuré, 8 h à deux personnes passent bien de 2 à 1 demi-journée — mais la
   * valeur ne partait qu'au `blur`. Il tapait « 2 », regardait la colonne, et rien ne
   * bougeait : l'outil paraissait cassé alors qu'il n'avait simplement rien reçu.*
   *
   * Le délai existe pour la raison qui avait fait choisir le `blur` : sans lui, taper
   * « 12 » écrirait d'abord 1 unité, puis 12 — deux écritures, deux lignes de
   * journal, et un recalage de barre intermédiaire que personne n'a demandé. Une
   * demi-seconde suffit à ne voir passer que la valeur finale.
   *
   * `blur` et `Entrée` restent : ils enregistrent immédiatement, sans attendre.
   */
  // La fonction d'enregistrement est gardée dans une référence : elle est
  // reconstruite à chaque rendu du parent, et la mettre dans les dépendances
  // relancerait la minuterie à chaque rendu — la saisie ne partirait jamais sur un
  // écran qui se rafraîchit.
  const validerRef = useRef(onValider);
  validerRef.current = onValider;

  useEffect(() => {
    if (saisie === null) return;
    const brut = saisie.trim();
    if (brut === "") return;
    const n = Number(brut);
    if (!Number.isInteger(n) || n <= 0 || n === valeur) return;
    const minuterie = setTimeout(() => {
      setSaisie(null);
      validerRef.current(n);
    }, 500);
    return () => {
      clearTimeout(minuterie);
    };
  }, [saisie, valeur]);

  return (
    <input
      className={`nombre-effectif${valeur === null ? " nombre-effectif--vide" : ""}`}
      inputMode="numeric"
      value={affiche}
      placeholder={propose > 0 ? String(propose) : "—"}
      title={titre}
      disabled={!modifiable || enAttente}
      onChange={(e) => {
        setSaisie(e.target.value);
      }}
      onBlur={() => {
        if (saisie === null) return;
        const brut = saisie.trim();
        setSaisie(null);
        if (brut === "") {
          if (valeur !== null) onValider(null);
          return;
        }
        const n = Number(brut);
        // Une saisie qui n'est pas un entier positif ne s'enregistre pas et
        // n'efface rien : le champ reprend simplement la valeur connue.
        if (!Number.isInteger(n) || n <= 0 || n === valeur) return;
        onValider(n);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") setSaisie(null);
      }}
    />
  );
}

/* ---------------------------------------------------------------------------
 * Ce qu'un étirement coûte, dit avant de l'appliquer
 * ------------------------------------------------------------------------- */

/**
 * Ce que porte une barre : une ligne de contre-étude, ou le titre d'un poste sans détail.
 *
 * Réunis dans un seul type parce que tout ce qui suit — le glissé, le panneau, le
 * message d'avertissement — traite les deux de la même façon, à un détail près : un
 * titre ne porte aucune heure, donc l'étirer ne coûte rien.
 */
type Porteur = {
  readonly tache: OuvrageVue;
  /** Nul pour une barre de titre. */
  readonly ressource: RessourceVue | null;
};

/**
 * Ce qu'un changement de durée ferait aux heures et au coût de ce qui porte la barre.
 *
 * **Le facteur porte sur la durée totale du PORTEUR**, pas sur celle de la barre seule :
 * une ligne hachée en trois reprises dont une double n'a pas vu son travail doubler.
 *
 * Rend `null` quand rien ne bougerait — durée inchangée, ou barre de titre qui ne porte
 * aucune heure. L'écran n'a alors rien à annoncer, et un avertissement affiché pour rien
 * finirait par ne plus être lu.
 */
function effetDeLEtirement(
  porteur: Porteur,
  phase: PhaseVue,
  dureeVoulue: number,
): { heuresAvant: string; heuresApres: string; coutAvant: string; coutApres: string } | null {
  if (porteur.ressource === null) return null;
  const totalAvant = porteur.ressource.planning.demiJourneesPosees;
  const totalApres = totalAvant - phase.demiJournees + dureeVoulue;
  if (totalAvant <= 0 || totalApres <= 0 || totalApres === totalAvant) return null;
  const facteur = totalApres / totalAvant;
  return {
    heuresAvant: porteur.ressource.quantiteReelle,
    heuresApres: (Number(porteur.ressource.quantiteReelle) * facteur).toFixed(4),
    coutAvant: porteur.ressource.cout,
    coutApres: (Number(porteur.ressource.cout) * facteur).toFixed(2),
  };
}

/** Le nom de ce qui porte la barre, tel qu'un message doit le désigner. */
function nommer(porteur: Porteur): string {
  if (porteur.ressource === null) {
    return `${porteur.tache.numero} ${porteur.tache.designation}`;
  }
  return `${porteur.ressource.code} ${porteur.ressource.designation}`;
}

/* ---------------------------------------------------------------------------
 * Modifier la barre choisie
 * ------------------------------------------------------------------------- */

/**
 * Le panneau de modification d'une barre.
 *
 * **Il annonce le surcoût AVANT de l'appliquer.** Changer la durée multiplie les
 * heures de la ligne par le rapport des durées : le panneau calcule ce rapport à la
 * saisie et le dit en clair. Le métier a demandé un message d'alerte à l'allongement
 * comme à la réduction — le voici, mais chiffré plutôt que vague.
 */
function PanneauBarre({
  porteur,
  phase,
  modifiable,
  enAttente,
  onModifier,
  onRetirer,
  onFermer,
}: {
  porteur: Porteur;
  phase: PhaseVue;
  modifiable: boolean;
  enAttente: boolean;
  onModifier: (changement: {
    demiJournees?: number;
    debutRelatif?: number;
    position?: "matin" | "apres_midi";
  }) => void;
  onRetirer: () => void;
  onFermer: () => void;
}): JSX.Element {
  const [duree, setDuree] = useState(String(phase.demiJournees));
  const [debut, setDebut] = useState(String(phase.debutRelatif));

  const dureeVoulue = Number(duree);
  const dureeValide = Number.isInteger(dureeVoulue) && dureeVoulue > 0;
  const debutVoulu = Number(debut);
  const debutValide = Number.isInteger(debutVoulu) && debutVoulu > 0;

  const barres = porteur.ressource?.planning.phases ?? porteur.tache.planning.phases;
  const effet = dureeValide ? effetDeLEtirement(porteur, phase, dureeVoulue) : null;

  return (
    <section className="panneau-barre">
      <h3>{nommer(porteur)}</h3>
      <p className="etat">
        {porteur.ressource === null ? (
          <>Barre du poste — il ne porte aucune heure, l'allonger ne coûte rien. </>
        ) : (
          <>
            {porteur.tache.numero} {porteur.tache.designation} ·{" "}
          </>
        )}
        Barre {phase.ordre} sur {barres.length} · J{phase.debutRelatif} → J{phase.finRelative}
      </p>

      <div className="panneau-barre__champs">
        <label>
          Durée en demi-journées
          <input
            value={duree}
            disabled={!modifiable || enAttente}
            onChange={(e) => {
              setDuree(e.target.value);
            }}
          />
        </label>
        <label>
          Jour de début
          <input
            value={debut}
            disabled={!modifiable || enAttente}
            onChange={(e) => {
              setDebut(e.target.value);
            }}
          />
        </label>
        <label>
          Démarre
          <select
            value={phase.position}
            disabled={!modifiable || enAttente}
            onChange={(e) => {
              onModifier({ position: e.target.value === "apres_midi" ? "apres_midi" : "matin" });
            }}
          >
            <option value="matin">le matin</option>
            <option value="apres_midi">l'après-midi</option>
          </select>
        </label>
      </div>

      {/* L'avertissement chiffré. Le métier a demandé « attention vous allongez la
          durée de la tâche » ; un message qui dit AUSSI ce que ça coûte évite de
          devoir aller le chercher après coup. */}
      {effet !== null && (
        <p className="alerte" role="alert">
          <strong>
            Attention : {dureeVoulue > phase.demiJournees ? "vous allongez" : "vous réduisez"} le
            travail de cette ligne.
          </strong>{" "}
          Ses heures passeraient de {formaterNombre(effet.heuresAvant, 2)} h à{" "}
          {formaterNombre(effet.heuresApres, 2)} h, et son déboursé de{" "}
          {formaterMontant(effet.coutAvant)} € à {formaterMontant(effet.coutApres)} €. Le nombre
          d'unités reste le même : la barre plus longue, c'est plus de travail.{" "}
          <span className="note">Les autres lignes de la tâche ne sont pas touchées.</span>
        </p>
      )}

      <div className="panneau-barre__actions">
        <button
          type="button"
          className="principal"
          disabled={!modifiable || enAttente || !dureeValide || !debutValide}
          onClick={() => {
            onModifier({ demiJournees: dureeVoulue, debutRelatif: debutVoulu });
          }}
        >
          {enAttente ? "Enregistrement…" : effet !== null ? "Appliquer et recalculer" : "Appliquer"}
        </button>
        <button type="button" disabled={!modifiable || enAttente} onClick={onRetirer}>
          Retirer cette barre
        </button>
        <button type="button" onClick={onFermer}>
          Fermer
        </button>
      </div>

      {!modifiable && <p className="etat">Version figée — le planning ne se modifie pas ici.</p>}
    </section>
  );
}

/* ---------------------------------------------------------------------------
 * Le coût caché du chantier
 * ------------------------------------------------------------------------- */

/**
 * Ce que l'arrondi à la demi-journée coûte sur tout le chantier.
 *
 * **Deux grandeurs, deux unités, et c'est volontaire.** Le surcoût des machines est
 * chiffré en euros parce que chaque ligne porte son propre prix horaire : il
 * s'attribue exactement. Celui de la main-d'œuvre reste en heures ici — le total ne
 * distingue pas les taux, et une moyenne inventée vaudrait moins qu'une heure honnête.
 * Le coût en euros de chaque ligne, lui, s'affiche sur sa ligne : depuis que la durée
 * vit sur la ligne, on sait qui attend et à quel prix.
 *
 * **La couverture est affichée avec le chiffre.** Sans elle, « 0 € » se lit « rien à
 * signaler » alors qu'il veut dire « personne n'a déclaré de machine ». Un total muet
 * sur ce qu'il ignore rend une fausse assurance — c'est la leçon du contrôle de
 * schéma qui annonçait « 0 écart » sur 400 contraintes qu'il n'avait pas lues.
 */
function CoutCache({ cout }: { cout: DepoVue["totaux"]["coutArrondi"] }): JSX.Element | null {
  const heuresMO = Number(cout.heuresCachees);
  const heuresMat = Number(cout.heuresCacheesMateriel);
  const rien = Math.abs(heuresMO) < 0.005 && cout.machinesComptees === 0;
  if (cout.taches === 0) return null;

  return (
    <p className={rien ? "etat" : "alerte"} role={rien ? undefined : "status"}>
      <strong>Coût caché de l'arrondi à la demi-journée</strong> ·{" "}
      {Math.abs(heuresMO) >= 0.005 ? (
        <>
          main-d'œuvre <strong>+{formaterNombre(cout.heuresCachees, 2)} h</strong> sur{" "}
          {cout.tachesArrondies} tâche(s) de {cout.taches}
        </>
      ) : (
        <>main-d'œuvre : aucun</>
      )}
      {cout.machinesComptees > 0 && (
        <>
          {" · "}matériel{" "}
          <strong>
            {heuresMat < 0 ? "" : "+"}
            {formaterNombre(cout.heuresCacheesMateriel, 2)} h ={" "}
            {formaterMontant(cout.coutCacheMateriel)} €
          </strong>
          {/* Un écart négatif n'est pas une économie : c'est une machine trop peu
              nombreuse pour tenir ses heures dans la durée posée. */}
          {heuresMat < 0 && (
            <span className="note">
              {" "}
              — négatif : les machines déclarées ne tiennent pas leurs heures dans la
              durée posée. Il en faut davantage, ou la barre est trop courte.
            </span>
          )}
        </>
      )}
      {/*
        La couverture, toujours. C'est elle qui empêche de lire un total maigre comme
        un total complet — et elle dit au conducteur ce qu'il lui reste à renseigner.
      */}
      <span className="note">
        {" · "}
        {cout.machinesComptees} ligne(s) de matériel comptée(s) sur {cout.machinesDeclarables}
        {cout.machinesComptees < cout.machinesDeclarables && (
          <>
            {" "}— le reste n'est pas déclaré, donc pas compté (les rotations de transport
            n'ont pas à l'être)
          </>
        )}
        {/*
          Les lignes sans barre comptent ici sur la durée que leurs heures impliquent,
          alors que leur ligne n'affiche rien. Le total est donc légitimement supérieur
          à la somme de ce qu'on lit à l'écran, et le taire serait un écart qui
          n'apparaîtrait qu'au premier chiffre inexplicable.
        */}
        {cout.tachesPlanifiees < cout.taches && (
          <>
            {" · "}
            {cout.taches - cout.tachesPlanifiees} tâche(s) non planifiée(s) comptée(s) sur leur
            durée proposée — leur ligne n'affiche rien
          </>
        )}
      </span>
    </p>
  );
}


/**
 * La frise s'arrête au bas de l'écran — pour que sa barre de défilement horizontale y soit.
 *
 * *Chris, 22/09/2026 : « dans le planning c'est vraiment dur de bouger de droite à gauche pour
 * faire défiler la largeur quand il y a énormément de colonnes ».*
 *
 * **Mesuré avant d'y toucher**, à 1 920 × 1 000 : le cadre de la frise commençait à 336 px et
 * faisait 780 px de haut (`max-height: 78vh`), donc son bas tombait à **1 116 px — 116 px sous
 * le bord de la fenêtre**. Or c'est là que vit la barre de défilement horizontale. Il fallait
 * donc faire défiler la PAGE vers le bas pour l'attraper, ce qui emportait hors de vue la ligne
 * des dates, puis remonter pour lire où l'on était arrivé. La frise était large et son unique
 * moyen de la parcourir était hors de portée.
 *
 * `78vh` ne pouvait pas y suffire : une part de la fenêtre est déjà prise par le titre, le
 * menu, les sélecteurs et les avertissements, et cette part **change** — le menu se replie sur
 * téléphone, les avertissements vont et viennent, et la fenêtre satellite n'a ni menu ni titre.
 * Une fraction fixe de la hauteur ignore tout cela par construction. Ce qu'il faut est la place
 * qui reste RÉELLEMENT sous le haut de la frise, donc une mesure.
 *
 * Un plancher de 320 px protège le cas où il ne reste presque rien : mieux vaut une frise qui
 * déborde un peu qu'une frise haute de deux lignes.
 */
function useFriseQuiTientDansLEcran(
  cadre: React.RefObject<HTMLDivElement | null>,
  hauteurGeree: boolean,
): void {
  useEffect(() => {
    const element = cadre.current;
    if (element === null) return;
    /*
      **Dans la fenêtre, la disposition s'en charge** : elle occupe toute la hauteur et la frise
      y est la seule zone qui défile. Une hauteur posée en style direct l'emporterait sur le
      `flex` de la feuille — c'est la règle des styles en ligne — et reprendrait à la fenêtre
      précisément ce qu'elle vient d'obtenir.
    */
    if (hauteurGeree) {
      element.style.maxHeight = "";
      return;
    }

    const ajuster = (): void => {
      /*
        **La position dans le DOCUMENT, et non dans la fenêtre.** Mesurer `top` par rapport à
        la fenêtre paraissait suffire, et c'était un défaut mesuré le 22/09/2026 : en faisant
        défiler, `top` diminue, donc la place calculée grandit, donc la frise grandit, donc la
        page s'allonge — et l'on peut défiler encore. Mesuré dans la fenêtre : 812 px de frise
        au chargement, 1 031 après cinq crans de molette, 1 553 après dix, une page qui passait
        de 1 048 à 1 789 px sans jamais finir. La hauteur ne doit dépendre que de l'endroit où
        la frise COMMENCE dans la page, pas de l'endroit où on l'a fait glisser.
      */
      const hautDansLeDocument = element.getBoundingClientRect().top + window.scrollY;
      const place = window.innerHeight - hautDansLeDocument - 16;
      const voulue = `${String(Math.max(320, Math.round(place)))}px`;
      /*
        **N'écrire que si la valeur change**, et ce n'est pas une optimisation. L'observateur
        surveille le corps de page ; poser une hauteur change ce corps, donc le rappelle. Écrire
        systématiquement entretiendrait la boucle que le navigateur coupe en signalant
        « ResizeObserver loop limit exceeded », après avoir consommé une image d'animation à
        chaque tour. Un écart d'un pixel toléré vaut mieux qu'un tour de boucle de plus.
      */
      if (element.style.maxHeight === voulue) return;
      element.style.maxHeight = voulue;
    };

    ajuster();
    window.addEventListener("resize", ajuster);
    /*
      Ce qui est AU-DESSUS de la frise bouge sans que la fenêtre change de taille : un
      avertissement qui apparaît, le menu qui se replie, une tâche qu'on déplie. L'observateur
      sur le corps de page rattrape tout cela — s'abonner au seul redimensionnement de fenêtre
      laisserait la frise mal dimensionnée jusqu'au suivant. Pas d'écoute du DÉFILEMENT en
      revanche : la hauteur n'en dépend plus, et c'est tout l'objet de la correction ci-dessus.
    */
    const observateur = new ResizeObserver(ajuster);
    observateur.observe(document.body);
    return () => {
      window.removeEventListener("resize", ajuster);
      observateur.disconnect();
    };
  }, [cadre, hauteurGeree]);
}
/* ---------------------------------------------------------------------------
 * L'écran
 * ------------------------------------------------------------------------- */

export function Planning({
  entete,
  depo,
  reference,
  versionId,
  carrieres,
  attelagesDesPostes,
  transportsFiges = [],
  modifiable,
  cible,
  hauteurGeree = false,
  onChange,
}: {
  entete: EnteteVersion;
  depo: DepoVue;
  /** Le devis figé, quand on regarde une copie de travail : l'ancre des heures. */
  reference: DepoVue | null;
  versionId: string;
  /** Le référentiel des carrières : une pastille de transport y désigne son chargement. */
  carrieres: readonly Carriere[];
  /**
   * Les ATTELAGES proposables sur chaque poste, par numéro de poste (076).
   *
   * Groupés par le SERVEUR d apres les couplages du Parametrage transport, avec leur tonnage :
   * *« spécifie dans le couplage le tonnage possible pour aiguiller un peu mieux »* (Chris).
   */
  /** Les lignes de transport figées face au calcul automatique, en `poste#rang` (092). */
  transportsFiges?: readonly string[];
  attelagesDesPostes: Readonly<
    Record<string, readonly { rang: number; libelle: string; tonnage: string | null }[]>
  >;
  modifiable: boolean;
  /**
   * La ligne sur laquelle arriver quand on vient du plan de charge *(Chris, 17/09/2026)*.
   *
   * Désignée par le couple **(poste, rang)** et non par son identifiant : c'est ce couple qui
   * nomme une ligne dans cet écran, et l'identifiant y aurait obligé à parcourir l'arbre pour
   * retrouver ce que le numéro donne directement.
   */
  cible?: CibleDeLaLigne | null;
  /** Recharger la version : le déboursé a pu changer, pas seulement le planning. */
  onChange: () => void;
  /**
   * La hauteur de la frise est-elle posée par la DISPOSITION plutôt que mesurée ?
   *
   * Vrai dans la fenêtre du planning, qui occupe toute la hauteur de l'écran et fait de la
   * frise sa seule zone de défilement — sa barre horizontale reste alors au bas de l'écran en
   * permanence *(Chris, 22/09/2026 : « la barre doit être présente tout le temps, pas seulement
   * quand on est en bas du fichier »)*. Faux dans l'application, où la frise vit au milieu
   * d'une page qui défile.
   */
  hauteurGeree?: boolean;
}): JSX.Element {
  /** Le cadre de la frise : sa hauteur se mesure, elle ne se devine pas. Voir le commentaire
      de `useFriseQuiTientDansLEcran` juste au-dessus de cet écran. */
  const cadreDeLaFrise = useRef<HTMLDivElement>(null);
  useFriseQuiTientDansLEcran(cadreDeLaFrise, hauteurGeree);

  const [choisie, setChoisie] = useState<string | null>(null);
  /**
   * Les tâches dont le détail est déplié.
   *
   * **Un ensemble et non un seul numéro**, et c'est la conséquence directe de la
   * migration 012 : les barres vivent sur les lignes, donc travailler sur deux tâches
   * demandait auparavant de replier la première. Un dépliant exclusif convenait quand
   * il ne servait qu'à consulter du détail ; il ne convient plus quand c'est là que
   * tout se saisit.
   */
  const [depliees, setDepliees] = useState<ReadonlySet<string>>(() =>
    lireEnsemble("planning-depliees", versionId),
  );
  /**
   * Les chapitres ouverts. **Vide au départ : tout est replié.**
   *
   * *Demande de Chris, 24/08/2026 : « en vue planning tout est replié ; je voudrais une
   * flèche à côté des chapitres pour n'ouvrir que le chapitre, et le refermer ».*
   *
   * Un ensemble, et le même raisonnement que pour le détail des tâches : on travaille sur
   * deux chapitres à la fois, un dépliant exclusif refermerait le premier en ouvrant le
   * second. Ouvrir un chapitre montre ses **enfants directs**, chaque sous-chapitre
   * gardant sa propre flèche — sinon un clic sur « 1 » déverserait cent tâches, ce qui
   * est exactement l'état dont on sort.
   *
   * Ce que le pliage ne touche pas : le résumé d'un chapitre replié reste dessiné sur la
   * frise, et reste glissable. C'est tout l'intérêt de l'état replié — voir l'étendue de
   * ce qu'on déplace sans avoir son détail sous les yeux.
   */
  const [chapitresOuverts, setChapitresOuverts] = useState<ReadonlySet<string>>(() =>
    lireEnsemble("planning-chapitres", versionId),
  );

  /*
    Actualiser ne replie rien et ne ramène pas la frise en haut (Chris, 28/09/2026 : « il faut
    rester précisément là où l'on se trouve »). Le pliage et le défilement se retiennent par
    version, dans la session de l'onglet (voir `position.ts`) ; la règle « tout replié à
    l'ouverture » du 24/08 reste celle d'un onglet neuf.
  */
  useEffect(() => {
    retenirEnsemble("planning-depliees", versionId, depliees);
  }, [versionId, depliees]);
  useEffect(() => {
    retenirEnsemble("planning-chapitres", versionId, chapitresOuverts);
  }, [versionId, chapitresOuverts]);
  const defilementRenduPour = useRef<string | null>(null);
  useEffect(() => {
    const element = cadreDeLaFrise.current;
    if (element === null || defilementRenduPour.current === versionId) return;
    defilementRenduPour.current = versionId;
    const voulu = lireDefilement("planning-frise", versionId);
    if (voulu === undefined) return;
    requestAnimationFrame(() => {
      element.scrollTo(voulu.gauche, voulu.haut);
    });
  });

  /* ---------------------------------------------------------------------------
   * Arriver sur une ligne, quand on vient du plan de charge (Chris, 17/09/2026)
   * ------------------------------------------------------------------------- */

  /**
   * Ouvrir ce qu'il faut pour que la ligne visée existe à l'écran.
   *
   * Cet écran arrive **tout replié** (décision du 24/08/2026), et le détail d'une tâche est
   * replié dans le replié : une ligne de ressource n'est donc rendue que si tous ses chapitres
   * ancêtres sont ouverts ET que sa tâche est dépliée. Le saut ouvre les deux, et rien d'autre
   * — les chapitres déjà ouverts le restent, parce qu'on revient souvent d'un aller-retour et
   * que refermer ce qu'on avait ouvert serait le pire accueil.
   *
   * **Les mises à jour rendent l'ensemble PRÉCÉDENT quand il n'y a rien à ajouter.** Sans cela
   * l'effet bouclerait sans fin : il dépend de `depo`, qui est reconstruit à chaque rendu, et
   * un nouvel ensemble à chaque passage provoquerait le rendu suivant.
   */
  useEffect(() => {
    if (cible === undefined || cible === null) return;
    const visee = aplatir(depo.racines).find(
      (l) => l.nature === "tache" && l.tache.numero === cible.poste,
    );
    if (visee === undefined) return;
    const ancetres = visee.ancetres;
    setChapitresOuverts((avant) =>
      ancetres.every((numero) => avant.has(numero)) ? avant : new Set([...avant, ...ancetres]),
    );
    setDepliees((avant) => (avant.has(cible.poste) ? avant : new Set([...avant, cible.poste])));
  }, [cible, depo]);

  /**
   * Amener la ligne sous les yeux une fois qu'elle est rendue.
   *
   * Séparé de l'effet ci-dessus parce qu'il faut **un rendu de plus** : au moment où l'on
   * déplie, la rangée n'existe pas encore dans le document. La référence arrête le défilement
   * dès qu'il a réussi — sinon chaque modification de barre, qui rejoue ce rendu, ramènerait
   * l'écran sur cette ligne alors qu'on travaille peut-être ailleurs depuis.
   */
  const cibleAtteinte = useRef<CibleDeLaLigne | null>(null);
  useEffect(() => {
    if (cible === undefined || cible === null || cibleAtteinte.current === cible) return;
    if (amenerSousLesYeux(ancreDeLaLigne(cible.poste, cible.rang))) cibleAtteinte.current = cible;
  }, [cible, chapitresOuverts, depliees, depo]);

  const [enAttente, setEnAttente] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [dernierRecalcul, setDernierRecalcul] = useState<BarreEcrite["recalcul"]>(null);
  /** Combien de barres la dernière pose de pastille a retirées. Voir `poserTransport`. */
  const [barresRetirees, setBarresRetirees] = useState(0);
  /** Le compte rendu du dernier retour aux heures du devis, affiché au même endroit. */
  const [dernierRetour, setDernierRetour] = useState<HeuresRestaurees | null>(null);
  /** Le compte rendu du dernier hachage d'une barre grise — surtout ce qu'il a laissé. */
  const [dernierHachage, setDernierHachage] = useState<BlocHache | null>(null);
  /**
   * Le dernier décalage en bloc, pour en rendre compte.
   *
   * Il mérite son compte rendu parce que ce qu'il déplace **dépasse ce qu'on voit** : la
   * barre grise qu'on vient de tirer, mais aussi des livraisons qui ne sont pas à l'écran
   * quand tout est replié — et c'est précisément dans cet état que Chris a demandé ce geste.
   */
  const [dernierDecalage, setDernierDecalage] = useState<BlocDecaleEcrit | null>(null);
  /**
   * Le compte rendu du dernier calcul de transport de matériaux (09/09/2026).
   *
   * Affiché en entier plutôt que résumé : le conducteur doit pouvoir vérifier de tête que le
   * dimensionnement est juste — le tonnage, la charge utile, la durée d'un tour, le nombre de
   * tours, le nombre de camions. Un « 8 rotations posées » seul obligerait à croire, et un
   * calcul qu'on ne peut pas vérifier est un calcul qu'on cesse de lire à la première surprise.
   */
  const [bilanTransport, setBilanTransport] = useState<BilanDuTransport | null>(null);
  const [glisse, setGlisse] = useState<Glisse | null>(null);
  /**
   * Le glissé d'une pastille, tenu à part de celui d'une barre.
   *
   * Les deux gestes n'ont ni la même unité — le jour contre la demi-journée — ni le même
   * effet : déplacer une barre peut être suivi d'un étirement qui coûte de l'argent, une
   * pastille ne se déplace que dans le temps. Un seul état pour les deux obligerait chaque
   * lecture à se demander lequel des deux il décrit.
   */
  const [glisseLivraison, setGlisseLivraison] = useState<GlisseLivraison | null>(null);
  /** Le bloc en cours de glissé — chapitre ou ouvrage. Voir `GlisseBloc`. */
  const [glisseBloc, setGlisseBloc] = useState<GlisseBloc | null>(null);
  /**
   * Le planning est-il réduit au matériel transférable ? (métier, 18/08/2026)
   *
   * *« Je vais aller cocher dans générique les matériels transférables, et toi dans le
   * planning tu me mets un bouton pour filtrer les matériels transférables sur la vue
   * planning. »* Le filtre du responsable transport : ce qui part sur porte-engin, et rien
   * d'autre — ni la main-d'œuvre, ni les fournitures, ni les camions qui s'y rendent seuls.
   */
  const [filtreMateriel, setFiltreMateriel] = useState(false);
  /**
   * Le second filtre du responsable transport : ce qu'on achemine soi-même.
   *
   * *Demande de Chris, 19/08/2026.* Deux états séparés plutôt qu'un seul à trois valeurs :
   * les deux filtres ne répondent pas à la même question — « quelles machines charger sur un
   * porte-engin » et « quelles rotations j'assure, et avec quoi dedans » —, et un seul état
   * obligerait chaque lecture à se demander lequel des deux il décrit.
   *
   * **Ils s'excluent l'un l'autre**, en revanche : les croiser ne garderait presque rien —
   * une machine qui monte sur un porte-engin n'est pas un camion qui livre —, et un bouton
   * qui vide l'écran passe pour une panne.
   */
  const [filtreAcheminement, setFiltreAcheminement] = useState(false);
  /** La livraison choisie — sa propre sélection : ce n'est pas une barre. */
  const [livraisonChoisie, setLivraisonChoisie] = useState<string | null>(null);

  const lignes = aplatir(depo.racines);

  /* -------------------------------------------------------------------------
   * Le filtre « matériel transférable »
   * -------------------------------------------------------------------------
   *
   * Il ne touche QUE l'affichage. Aucune ligne n'est retirée du chiffrage, aucune barre ne
   * bouge, et les totaux de l'en-tête restent ceux du chantier entier : le responsable
   * transport regarde un planning complet à travers une fenêtre, il n'en fabrique pas un
   * second. C'est aussi ce qui permet de le laisser actif sur une version modifiable sans
   * risquer qu'un geste porte sur autre chose que ce qu'on croit toucher.
   */

  /**
   * Les lignes d'une tâche à montrer, filtre appliqué.
   *
   * ## Une ligne SUPPRIMÉE ne se planifie pas — décision de Chris, 10/09/2026
   *
   * *« Quand une ligne est retirée dans la contre-étude, nous avons validé de laisser la ligne
   * apparaître dans la contre-étude avec la mention supprimé et la ligne en grisé, mais par
   * contre pas la peine de la faire apparaître dans le planning si elle est supprimée. »*
   *
   * **Les deux écrans répondent à deux questions différentes**, et c'est ce qui justifie deux
   * traitements. La contre-étude compare au devis : une ligne écartée doit s'y voir, sinon
   * l'écart avec le classeur devient inexplicable — d'où la mention et le grisé. Le planning,
   * lui, dit qui travaille quel jour : une ligne retirée ne coûte rien, n'occupe personne et
   * ne porte aucune barre. L'afficher n'ajoute qu'une piste vide de plus à faire défiler.
   *
   * Le moteur l'écartait déjà de tous ses CALCULS — heures, durées, déboursé, attelages — mais
   * pas de cette liste-ci : le chiffrage était juste, l'écran seul montrait des lignes mortes.
   *
   * Le filtre s'applique **avant** les deux autres, et non à leur place : une ligne supprimée
   * qui part sur porte-engin ou qui est acheminée par nos soins reste une ligne supprimée.
   */
  const ressourcesVisibles = (tache: OuvrageVue): readonly RessourceVue[] =>
    lignesDeLaTache(tache, {
      surPorteEngin: partSurPorteEngin,
      acheminees: () => lignesAcheminees(tache),
      filtreMateriel,
      filtreAcheminement,
      statutRetire: STATUT_RETIRE,
    });

  /** Un chapitre garde-t-il quelque chose à montrer ? Sinon il n'a plus de titre à porter. */
  const chapitreRetenu = (chapitre: NoeudVue): boolean => {
    if (!filtreMateriel && !filtreAcheminement) return true;
    if (chapitre.nature === "chapitre") return chapitre.enfants.some(chapitreRetenu);
    return filtreMateriel
      ? chapitre.ressources.some(partSurPorteEngin)
      : tacheAcheminee(chapitre);
  };

  /**
   * Les codes que le référentiel n'a pas encore tranchés sur CE chantier.
   *
   * Calculé sur l'arbre entier et non sur ce que le filtre laisse voir : c'est justement ce
   * qui manque à l'écran qu'il faut nommer.
   */
  const aTrancher = codesATrancher(depo);
  /** Les pastilles dont personne n'a encore dit qui les assure. Voir le signalement plus bas. */
  const sansMode = pastillesSansMode(depo);
  // Les compteurs de l'en-tête parlent de TÂCHES : un chapitre n'est ni planifié ni
  // à planifier, et le faire entrer dans « 12 tâches sur 17 » gonflerait le
  // dénominateur d'un travail qui n'existe pas.
  const taches = lignes.flatMap((l) => (l.nature === "tache" ? [l.tache] : []));

  /**
   * Ce qui est réellement affiché : les lignes dont tous les chapitres au-dessus sont
   * ouverts.
   *
   * **Séparé de `lignes` à dessein.** Les compteurs (« 12 tâches sur 17 »), les heures et
   * l'étendue de la frise se calculent sur `lignes`, l'arbre entier : les brancher sur ce
   * qui est visible ferait tomber le planning à « 0 tâche sur 0 » et écraserait la frise
   * dès l'arrivée sur l'écran, tout étant replié. Un chiffre qui dépend de ce qu'on a
   * ouvert n'est pas un chiffre.
   *
   * **Un filtre actif ouvre tout**, comme il déplie déjà le détail des tâches : filtrer
   * pour ne rien voir n'aurait aucun sens, et c'est ce que ferait un chapitre resté fermé
   * sur les seules lignes qu'on cherchait.
   */
  const toutOuvert = filtreMateriel || filtreAcheminement;
  const visiblesALEcran = toutOuvert ? lignes : filtrerVisibles(lignes, chapitresOuverts);
  const chapitres = lignes.flatMap((l) => (l.nature === "chapitre" ? [l.chapitre.numero] : []));
  /**
   * **Le contrôle du CA facturable** — *Chris, 25/09/2026 : « il faudra aussi avoir un contrôle
   * que tous les montants correspondent bien au montant total du marché de la contre-étude ».*
   *
   * Le même jour, le montant a quitté le chapitre pour se poser sur chaque tâche (voir
   * `echeancesDeLaVersion` au moteur). La somme des montants posés se compare à DEUX grandeurs, et l'écran dit les
   * deux parce qu'elles ne répondent pas à la même question :
   *
   *  - la **vente retenue de la version** : aucun euro perdu ni compté deux fois en changeant
   *    d'étage. Égale par construction — l'afficher, c'est la vérifier sous ses yeux ;
   *  - le **montant du marché HT** de l'en-tête du déboursé : prix négocié, il peut s'écarter
   *    d'un chiffrage retravaillé, et l'écart est une information, jamais une erreur à corriger
   *    (règle du 09/08/2026). Mesuré le 25/09/2026 : 134,49 € sur la copie de travail
   *    d'APO260098.
   *
   * Calculé sur `echeancesDeLaVersion`, la fonction du carnet de facturation, et non en
   * additionnant ce que les lignes affichent : un contrôle qui relirait l'écran vérifierait
   * l'écran par lui-même.
   */
  const totalFacturable = totalDesEcheances(echeancesDeLaVersion(depo.racines));
  const ecartVente = dec(totalFacturable).minus(dec(depo.totaux.prixVenteTotal));
  const ecartMarche =
    entete.montantMarcheHT === null ? null : dec(totalFacturable).minus(dec(entete.montantMarcheHT));
  /** L'état de départ de l'écran : rien d'ouvert, ni un chapitre ni le détail d'une tâche. */
  const toutReplie = chapitresOuverts.size === 0 && depliees.size === 0;
  const devis = heuresDuDevis(reference);
  // L'ancre au niveau de la LIGNE, pour le transport : c'est là qu'il se chiffre, et
  // corriger une pastille recale la ligne — sans référence fixe, l'impact du geste
  // disparaît au moment même où il vient d'être fait.
  const devisParLigne = heuresDuDevisParLigne(reference);
  const jours = etendueDuChantier(lignes);
  const planifiees = taches.filter((t) => barresDe(t).length > 0).length;
  const total = taches.length;

  /**
   * La frise en DATES, dès que le chantier porte une date de démarrage.
   *
   * *PRD §4.5 : « dès que le chantier porte une date de démarrage — ferme ou
   * hypothèse —, le Gantt s'affiche calé sur le calendrier réel ; les jours relatifs
   * ne subsistent que pour les chantiers sans date. »*
   *
   * **Rien de ce qui est enregistré ne change.** Les barres et les livraisons restent
   * en jours relatifs en base : c'est ce qui permet de décaler un chantier entier en
   * changeant une seule date, sans réécrire une seule barre. Ce qui change ici est
   * l'étiquette des colonnes — et l'étiquette est ce que le conducteur lit.
   *
   * Le tableau est calculé en une passe pour toute la frise, week-ends et fériés
   * sautés : cent appels séparés recompteraient cent fois depuis J1.
   */
  const datesDeLaFrise =
    entete.dateDebutChantier === null
      ? null
      : datesDesJoursOuvres(entete.dateDebutChantier, jours);

  /**
   * Comment un jour se nomme partout — infobulles comprises : « J4 » ou
   * « J4 jeu. 20/08 ».
   *
   * **Le rang ne disparaît pas quand la date arrive**, il la précède. Ce qui est
   * enregistré et ce qui se saisit sont des jours relatifs — les champs de livraison
   * et de transport demandent « entre J1 et J34 » —, donc une infobulle qui n'annonce
   * qu'une date oblige à recompter les jours ouvrés pour retrouver le nombre à taper.
   */
  const nommerJour = (jour: number): string => {
    const date = datesDeLaFrise?.[jour - 1];
    return date === undefined ? `J${jour}` : `J${jour} ${formaterDateCourte(date)}`;
  };

  /** La date d'un jour, en clair, ou `null` quand le chantier n'a pas de date. */
  const dateDuJour = (jour: number): string | null => {
    const date = datesDeLaFrise?.[jour - 1];
    return date === undefined ? null : formaterDateLongue(date);
  };

  const basculer = (numero: string): void => {
    setDepliees((courant) => {
      const suivant = new Set(courant);
      if (!suivant.delete(numero)) suivant.add(numero);
      return suivant;
    });
  };

  /** Hacher une barre grise : le compte rendu dit ce qui a été coupé, et ce qui ne l'a pas été. */
  const hacherLeBloc = (quoi: "ouvrage" | "chapitre", numero: string): void => {
    agir(
      hacherBloc(versionId, { quoi, numero }).then((ecrit) => {
        setDernierHachage(ecrit);
        return ecrit;
      }),
    );
  };

  const agir = (promesse: Promise<unknown>): void => {
    setEnAttente(true);
    setErreur(null);
    // Les comptes rendus sont effacés AVANT que la promesse ne se résolve : sans
    // cela, celui de l'action précédente resterait affiché à côté du résultat de
    // celle-ci, et on ne saurait plus lequel décrit ce qui vient de se passer.
    setDernierRetour(null);
    setDernierHachage(null);
    setDernierDecalage(null);
    promesse
      .then((resultat) => {
        // Le recalcul est conservé et affiché : une modification qui a changé le
        // déboursé doit se voir après coup, pas seulement avant.
        const barre = resultat as (BarreEcrite & { barresRetirees?: number }) | undefined;
        setDernierRecalcul(barre?.recalcul ?? null);
        setBarresRetirees(barre?.barresRetirees ?? 0);
        onChange();
      })
      .catch((e: unknown) => {
        setErreur(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setEnAttente(false);
      });
  };

  /**
   * Retrouve la barre choisie, et ce qui la porte.
   *
   * Le parcours va d'abord aux lignes : c'est là que vivent presque toutes les barres,
   * et les phases propres d'une tâche n'existent que sur les postes de fournitures
   * pures.
   */
  const chercher = (phaseId: string): { porteur: Porteur; phase: PhaseVue } | null => {
    for (const tache of taches) {
      for (const ressource of tache.ressources) {
        const phase = ressource.planning.phases.find((p) => p.id === phaseId);
        if (phase !== undefined) return { porteur: { tache, ressource }, phase };
      }
      const propre = tache.planning.phases.find((p) => p.id === phaseId);
      if (propre !== undefined) return { porteur: { tache, ressource: null }, phase: propre };
    }
    return null;
  };
  const selection = choisie === null ? null : chercher(choisie);

  /**
   * Retrouve une livraison par son identifiant.
   *
   * Sert au glissé, qui doit connaître le jour de départ de la pastille relâchée. La saisie,
   * elle, n'en a plus besoin : elle vit sur la ligne, qui porte déjà ses livraisons.
   */
  const chercherLivraison = (id: string): LivraisonVue | null => {
    for (const tache of taches) {
      for (const ressource of tache.ressources) {
        const livraison = ressource.planning.livraisons.find((l) => l.id === id);
        if (livraison !== undefined) return livraison;
      }
    }
    return null;
  };

  const chercherTransport = (id: string): TransportVue | null => {
    for (const tache of taches) {
      for (const ressource of tache.ressources) {
        const transport = ressource.planning.transports.find((t) => t.id === id);
        if (transport !== undefined) return transport;
      }
    }
    return null;
  };

  /* -------------------------------------------------------------------------
   * Le glissé
   * -------------------------------------------------------------------------
   *
   * Trois temps : on note où le geste commence, on suit le pointeur sans rien
   * écrire, et on enregistre une fois au relâcher. Écrire pendant le glissé ferait
   * cent requêtes pour un geste et cent lignes de journal pour une décision.
   *
   * Le pas est mesuré **au moment du clic**, sur la piste réelle : la frise est en
   * pourcentage, donc la largeur d'une demi-journée dépend de la fenêtre. La calculer
   * une fois pour toutes ferait déraper le geste au premier redimensionnement.
   */

  const commencer = (
    phase: PhaseVue,
    quoi: "deplacer" | "etirer",
    evenement: React.PointerEvent,
  ): void => {
    const piste = (evenement.currentTarget as HTMLElement).closest(".gantt__piste");
    if (piste === null) return;
    const largeur = piste.getBoundingClientRect().width;
    if (largeur <= 0) return;

    evenement.preventDefault();
    setGlisse({
      phaseId: phase.id,
      quoi,
      xDepart: evenement.clientX,
      // Le rang de demi-journée, pas le jour : `pasPixels` mesure une demi-journée,
      // donc `delta` en compte des demi-journées. Les deux doivent parler la même
      // unité, sinon la barre va deux fois trop loin.
      indexDepart: indexDemiJournee(phase),
      dureeDepart: phase.demiJournees,
      pasPixels: largeur / (jours * 2),
      delta: 0,
    });
  };

  /**
   * Le jour visé par un clic sur une piste.
   *
   * *Retour de Chris le 12/08/2026 : « je veux pouvoir le mettre sur une date précise ».*
   *
   * La frise est en pourcentage, donc la position se lit sur la piste réelle au moment du
   * clic : une largeur mémorisée déraperait au premier redimensionnement de la fenêtre.
   * `floor + 1` parce que J1 occupe la première colonne, et le plancher à 1 parce que rien
   * ne se livre avant le début du chantier.
   */
  const jourVise = (evenement: React.PointerEvent): number | null => {
    const piste = (evenement.currentTarget as HTMLElement).closest(".gantt__piste");
    if (piste === null) return null;
    const cadre = piste.getBoundingClientRect();
    if (cadre.width <= 0) return null;
    const part = (evenement.clientX - cadre.left) / cadre.width;
    return Math.min(jours, Math.max(1, Math.floor(part * jours) + 1));
  };

  /**
   * Le glissé d'une pastille : le PAS EST LE JOUR, pas la demi-journée.
   *
   * Une livraison tombe dans une journée ; la couper en deux moments n'aurait pas de sens —
   * c'est l'heure limite qui dit quand dans la journée, et elle se saisit. Mesurer ce geste
   * en demi-journées ferait atterrir un camion « l'après-midi du 4 » sans que le modèle sache
   * l'écrire.
   */
  const commencerLivraison = (
    pastille: { id: string },
    nature: "livraison" | "transport",
    evenement: React.PointerEvent,
  ): void => {
    const piste = (evenement.currentTarget as HTMLElement).closest(".gantt__piste");
    if (piste === null) return;
    const largeur = piste.getBoundingClientRect().width;
    if (largeur <= 0) return;

    evenement.preventDefault();
    setGlisseLivraison({
      livraisonId: pastille.id,
      nature,
      xDepart: evenement.clientX,
      pasPixels: largeur / jours,
      delta: 0,
    });
  };

  /**
   * Le glissé d'un bloc entier, depuis sa barre grise.
   *
   * Même mesure que pour une barre — le pas est pris sur la piste réelle au moment du clic,
   * parce que la frise est en pourcentage et qu'une largeur mémorisée déraperait au premier
   * redimensionnement.
   */
  const commencerBloc = (
    cible: {
      quoi: "ouvrage" | "chapitre";
      numero: string;
      debutDemi: number;
      finDemi: number;
      seulementLaPeriode?: boolean;
    },
    evenement: React.PointerEvent,
  ): void => {
    const piste = (evenement.currentTarget as HTMLElement).closest(".gantt__piste");
    if (piste === null) return;
    const largeur = piste.getBoundingClientRect().width;
    if (largeur <= 0) return;

    evenement.preventDefault();
    setGlisseBloc({
      ...cible,
      xDepart: evenement.clientX,
      pasPixels: largeur / (jours * 2),
      delta: 0,
    });
  };

  const suivre = (evenement: React.PointerEvent): void => {
    if (glisseBloc !== null) {
      const delta = Math.round((evenement.clientX - glisseBloc.xDepart) / glisseBloc.pasPixels);
      // Le plancher est posé ICI, pendant le geste : l'aperçu ne doit jamais montrer une
      // position que le serveur refusera. Le serveur refuse quand même — il est le seul à
      // voir tout le contenu du bloc, livraisons comprises —, mais un aperçu qui sort de la
      // frise se lit comme un défaut d'affichage, pas comme une butée.
      const borne = Math.max(delta, -glisseBloc.debutDemi);
      if (borne !== glisseBloc.delta) setGlisseBloc({ ...glisseBloc, delta: borne });
      return;
    }
    if (glisseLivraison !== null) {
      const delta = Math.round(
        (evenement.clientX - glisseLivraison.xDepart) / glisseLivraison.pasPixels,
      );
      if (delta !== glisseLivraison.delta) setGlisseLivraison({ ...glisseLivraison, delta });
      return;
    }
    if (glisse === null) return;
    const delta = Math.round((evenement.clientX - glisse.xDepart) / glisse.pasPixels);
    if (delta !== glisse.delta) setGlisse({ ...glisse, delta });
  };

  const relacher = (): void => {
    /**
     * Un bloc relâché : on écrit, sans demander.
     *
     * **Aucun euro ne bouge** — c'est une translation, et le serveur ne touche que des
     * positions. Demander confirmation pour un geste sans conséquence sur le chiffrage
     * apprend à cliquer « oui » sans lire, et rendrait suspect le jour où l'on demandera
     * vraiment.
     *
     * Ce qui bouge, en revanche, et que le compte rendu doit dire : la **date de fin** du
     * chantier si c'est le dernier bloc, et les **jours de livraison** — donc le planning
     * des carrières et les tournées. Un geste silencieux laisserait croire qu'on n'a
     * déplacé qu'un rectangle gris.
     */
    if (glisseBloc !== null) {
      const encours = glisseBloc;
      setGlisseBloc(null);
      if (encours.delta === 0) return;
      agir(
        decalerBloc(versionId, {
          quoi: encours.quoi,
          numero: encours.numero,
          demiJournees: encours.delta,
          ...(encours.seulementLaPeriode === true
            ? { periode: { debut: encours.debutDemi, fin: encours.finDemi } }
            : {}),
        }).then((ecrit) => {
          setDernierDecalage(ecrit);
          return ecrit;
        }),
      );
      return;
    }

    /**
     * Une pastille relâchée : on écrit son nouveau jour, sans demander.
     *
     * **Aucun euro ne bouge**, contrairement à un étirement de barre : le volume appartient
     * à la ligne, et déplacer la pastille dit seulement « le camion vient jeudi ». Demander
     * confirmation pour un geste sans conséquence apprend à cliquer « oui » sans lire.
     */
    if (glisseLivraison !== null) {
      const encours = glisseLivraison;
      setGlisseLivraison(null);
      if (encours.delta === 0) return;
      // Les deux natures se glissent de la même façon, et pour la même raison : **déplacer
      // ne coûte rien**. Ce qui coûte, c'est le volume d'une livraison ou les heures d'un
      // transport — et ceux-là ne se saisissent que dans la carte, jamais à la souris.
      const cible =
        encours.nature === "livraison"
          ? chercherLivraison(encours.livraisonId)
          : chercherTransport(encours.livraisonId);
      if (cible === null) return;
      const jour = Math.max(1, cible.jourRelatif + encours.delta);
      if (jour === cible.jourRelatif) return;
      agir(
        encours.nature === "livraison"
          ? modifierLivraison(versionId, encours.livraisonId, { jourRelatif: jour })
          : modifierTransport(versionId, encours.livraisonId, { jourRelatif: jour }),
      );
      return;
    }

    if (glisse === null) return;
    const encours = glisse;
    setGlisse(null);
    if (encours.delta === 0) return;

    const cible = chercher(encours.phaseId);
    if (cible === null) return;

    if (encours.quoi === "deplacer") {
      // Déplacer ne touche pas au chiffrage : on applique sans demander.
      //
      // **Le calcul se fait en demi-journées, puis on retraduit en jour + moment.**
      // Le modèle stocke la position en deux morceaux parce que c'est ainsi que le
      // métier la nomme ; le geste, lui, ne connaît que la demi-journée. C'est la
      // conversion qui manquait — sans elle, un déplacement d'une demi-journée
      // s'écrivait comme un jour entier.
      const index = Math.max(0, encours.indexDepart + encours.delta);
      if (index === encours.indexDepart) return;
      // Les DEUX champs partent ensemble : envoyer le seul jour laisserait la barre au
      // matin quoi qu'on fasse, et la moitié des positions serait hors d'atteinte.
      agir(modifierBarre(versionId, encours.phaseId, depuisIndexDemiJournee(index)));
      return;
    }

    const duree = Math.max(1, encours.dureeDepart + encours.delta);
    if (duree === encours.dureeDepart) return;

    /**
     * **Étirer change le déboursé : on demande avant.**
     *
     * C'est la règle du métier — « étirer la barre d'une ligne recalcule les heures de
     * cette ligne, donc son coût » — et elle est contre-intuitive : on croit
     * redimensionner un rectangle. Le message chiffre l'effet plutôt que de prévenir
     * vaguement, parce qu'un avertissement sans montant ne permet pas de décider.
     *
     * Une barre de titre ne coûte rien : `effetDeLEtirement` rend alors `null`, et on
     * applique sans question. Demander confirmation pour un geste sans conséquence
     * apprend à cliquer « oui » sans lire.
     */
    const effet = effetDeLEtirement(cible.porteur, cible.phase, duree);
    if (effet !== null) {
      const accepte = globalThis.confirm(
        `${nommer(cible.porteur)}\n` +
          `${cible.porteur.tache.numero} ${cible.porteur.tache.designation}\n\n` +
          `Durée : ${encours.dureeDepart} → ${duree} demi-journées.\n\n` +
          `${duree > encours.dureeDepart ? "Vous allongez" : "Vous réduisez"} le travail de cette ligne :\n` +
          `  heures    ${formaterNombre(effet.heuresAvant, 2)} h → ${formaterNombre(effet.heuresApres, 2)} h\n` +
          `  déboursé  ${formaterMontant(effet.coutAvant)} € → ${formaterMontant(effet.coutApres)} €\n\n` +
          "Le nombre d'unités reste le même : une barre plus longue, c'est plus de travail.\n" +
          "Les autres lignes de la tâche ne sont pas touchées.\n\nAppliquer ?",
      );
      if (!accepte) return;
    }

    agir(modifierBarre(versionId, encours.phaseId, { demiJournees: duree }));
  };

  return (
    <>
      <p className="etat">
        {entete.apo} — {entete.nomChantier} · {planifiees} tâche(s) planifiée(s) sur {total} ·
        frise de {jours} jours{" "}
        {/*
          La phrase suivait ce que l'écran fait, elle ne l'a pas suivi : elle annonçait
          « les dates viendront avec la date de démarrage » sur un chantier daté, donc
          au-dessus d'une frise qui affichait déjà des dates. Les deux repères cohabitent
          depuis le 16/08 — la date pour se situer, le rang pour ressaisir.
        */}
        {datesDeLaFrise === null ? (
          <>
            en <strong>jours relatifs</strong> (J1 = premier jour du chantier). Les dates
            viendront avec la date de démarrage.
          </>
        ) : (
          <>
            en <strong>dates réelles</strong>, week-ends et fériés sautés. Le rang J-n reste
            écrit sous chaque date : c'est lui qui se saisit.
          </>
        )}{" "}
        ·{" "}
        {/*
          Le classeur de TOUTES les versions du chantier — pas seulement celle-ci. Posé ici
          parce que c'est en regardant un planning qu'on veut l'emporter dans Excel.
        */}
        <a
          className="lien"
          href={adresseClasseurPlanning(entete.chantierId)}
          download
          title="Exporter en Excel le planning de toutes les versions de ce chantier"
        >
          Export Excel
        </a>
      </p>

      {/*
        Le coût caché de l'arrondi sur tout le chantier — hommes ET machines.
        Il existait, calculé et testé, et **aucun écran ne l'appelait** : le chiffre
        vivait dans un script. C'est celui que le métier réclamait (« il y a là
        beaucoup de coûts cachés qui devraient apparaître »), et il ne servait à
        personne tant qu'il restait invisible.
      */}
      <CoutCache cout={depo.totaux.coutArrondi} />

      {/*
        Le contrôle du CA facturable (25/09/2026) — voir `totalFacturable`. Rouge si un euro
        s'est perdu ou dédoublé entre les étages ; orangé si seul le marché HT s'écarte, ce
        qui est une information (prix négocié) et non une panne.
      */}
      <p
        className={
          !ecartVente.isZero()
            ? "alerte"
            : ecartMarche !== null && !ecartMarche.isZero()
              ? "alerte-douce"
              : "conforme"
        }
      >
        <strong>Contrôle du CA facturable :</strong> {formaterMontant(totalFacturable)} € posés
        sur les tâches{" "}
        {ecartVente.isZero() ? (
          <>— égal à la vente de la contre-étude ✓</>
        ) : (
          <>
            — <strong>écart de {formaterMontant(ecartVente)} €</strong> avec la vente de la
            contre-étude ({formaterMontant(depo.totaux.prixVenteTotal)} €)
          </>
        )}
        {" · "}
        {ecartMarche === null ? (
          <>le déboursé n'annonce pas de montant du marché.</>
        ) : ecartMarche.isZero() ? (
          <>égal au marché HT ✓</>
        ) : (
          <>
            marché HT {formaterMontant(entete.montantMarcheHT ?? "0")} € :{" "}
            <strong>
              écart {ecartMarche.isPositive() ? "+" : ""}
              {formaterMontant(ecartMarche)} €
            </strong>
          </>
        )}
      </p>

      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}

      {/*
        Le ménage fait par la première pastille, annoncé plutôt que silencieux. Une barre
        qui disparaît sans un mot, c'est une régression du point de vue du conducteur — il
        a posé cette barre, il doit savoir pourquoi elle n'est plus là.
      */}
      {barresRetirees > 0 && (
        <p className="conforme">
          {barresRetirees === 1 ? "La barre de durée" : `Les ${barresRetirees} barres de durée`}{" "}
          de cette ligne {barresRetirees === 1 ? "a été retirée" : "ont été retirées"} : elle
          {barresRetirees === 1 ? " datait" : "s dataient"} d'avant son passage en transport, et
          une ligne de transport se pose en pastilles. <strong>Aucune heure n'a bougé.</strong>
        </p>
      )}

      {dernierRecalcul !== null && (
        <p className="conforme">
          Enregistré. Les heures de <strong>{dernierRecalcul.code}</strong> sont passées de{" "}
          {formaterNombre(dernierRecalcul.heuresAvant, 2)} h à{" "}
          {formaterNombre(dernierRecalcul.heuresApres, 2)} h — son déboursé a changé en
          conséquence, et les autres lignes de la tâche n'ont pas bougé.
        </p>
      )}

      {/*
        Le compte rendu du retour au devis, et surtout ce qu'il n'a PAS pu rendre.
        Sans « n lignes sans équivalent », « 27 lignes rendues » se lit comme « toute
        la tâche est revenue au devis » — même leçon que la couverture affichée à côté
        du coût caché : un chiffre sans son périmètre se lit comme un total.
      */}
      {dernierRetour !== null && (
        <p className="conforme">
          {dernierRetour.poste} : heures rendues au devis figé —{" "}
          {formaterNombre(dernierRetour.heuresAvant, 2)} h →{" "}
          {formaterNombre(dernierRetour.heuresApres, 2)} h sur{" "}
          {dernierRetour.lignesRestaurees} ligne(s).
          {dernierRetour.lignesSansReference > 0 &&
            ` ${dernierRetour.lignesSansReference} ligne(s) en heures n'existent pas dans le devis : elles gardent les leurs.`}
          {dernierRetour.barresAjustees.length > 0 && (
            <>
              {" "}
              {dernierRetour.barresAjustees.length} barre(s) recalée(s) sans qu'aucun euro ne bouge
              de ce fait :{" "}
              {dernierRetour.barresAjustees
                .map((b) => `${b.code} ${b.de} → ${b.a} ½ j`)
                .join(", ")}
              .
            </>
          )}
        </p>
      )}

      {/*
        Le compte rendu du hachage d'une barre grise, et d'abord ce qu'il a LAISSÉ : une
        ligne trop courte reste entière, et sans ce nom le bloc se lirait « tout coupé ».
      */}
      {dernierHachage !== null && (
        <p className={dernierHachage.tropCourtes.length > 0 ? "note" : "conforme"}>
          {dernierHachage.perimetre.numero} : {dernierHachage.coupees} ligne(s) coupée(s) d'une
          reprise de plus, durée totale et chiffrage inchangés.
          {dernierHachage.tropCourtes.length > 0 &&
            ` ${dernierHachage.tropCourtes.length} laissée(s) telle(s) quelle(s), trop courte(s) ` +
              `pour une part de plus d'au moins une demi-journée : ` +
              dernierHachage.tropCourtes
                .map((l) => (l.code === null ? l.poste : `${l.poste} ${l.code}`))
                .join(", ") +
              "."}
        </p>
      )}

      <div className="barre-outils">
        {modifiable && planifiees < total && (
          <button
            type="button"
            className="principal"
            disabled={enAttente}
            onClick={() => {
              agir(proposerPlanning(versionId));
            }}
          >
            Proposer le planning ({total - planifiees} tâche(s) sans barre)
          </button>
        )}
        {/*
          Déplier tout : depuis que les barres vivent sur les lignes, c'est là que le
          travail se fait — et déplier cinquante tâches une à une avant de pouvoir
          toucher une barre serait intenable.
        */}
        <button
          type="button"
          disabled={total === 0}
          onClick={() => {
            /*
              Un seul bouton pour les DEUX niveaux de pliage, depuis que les chapitres se
              replient (24/08/2026). Sans les chapitres, « Déplier le détail » dépliait
              des tâches qu'un chapitre fermé cachait encore : le bouton n'aurait rien
              montré, ce qui se lit comme une panne.
            */
            if (toutReplie) {
              setChapitresOuverts(new Set(chapitres));
              setDepliees(new Set(taches.map((t) => t.numero)));
            } else {
              setChapitresOuverts(new Set());
              setDepliees(new Set());
            }
          }}
        >
          {toutReplie ? "Déplier le détail" : "Replier tout"}
        </button>
        {/*
          « Chapitres seuls » — le même geste que dans la contre-étude.

          *Demande de Chris, 25/08/2026 : « dans la vue planning il faudrait mettre un filtre
          chapitre seul, le même qui existe dans la vue contre-étude, qui donne la même
          présentation, où on verrait que les lignes des barres grises ».*

          **Un preset des deux pliages, et non un troisième filtre** : tous les chapitres
          ouverts, aucun détail de tâche déplié. C'est exactement ce que fait le bouton de
          même nom sur la contre-étude, et ce qui reste à l'écran est le pendant de ce qu'il
          y montre — l'ossature entière du classeur, et chaque tâche réduite à sa barre
          grise. Aucune ligne de ressource, donc aucune barre de couleur.

          À distinguer de l'état de départ, qui ne laisse que les chapitres de PREMIER
          niveau : ici l'arbre est ouvert de bout en bout.

          Désactivé quand un filtre transport est actif : celui-ci tient déjà tout ouvert,
          le geste ne changerait rien à l'écran — et un bouton qui ne fait rien se lit
          comme une panne.
        */}
        <button
          type="button"
          disabled={total === 0 || toutOuvert}
          title={
            toutOuvert
              ? "Un filtre est actif : tout est déjà ouvert."
              : "Ouvrir tous les chapitres et replier le détail de toutes les tâches : l'ossature du chantier, barres grises seules."
          }
          onClick={() => {
            setChapitresOuverts(new Set(chapitres));
            setDepliees(new Set());
          }}
        >
          Chapitres seuls
        </button>
        {/*
          Le seul bouton de cet écran qui ne s'adresse pas au conducteur.

          *Demande du métier, 18/08/2026 : « je vais aller cocher dans générique les matériels
          transférables, et toi dans le planning tu me mets un bouton pour filtrer les
          matériels transférables sur la vue planning ».*

          Ouvert à tous les rôles, et sur toutes les versions : le responsable transport
          regarde ce qu'un conducteur lui a soumis, et le conducteur doit pouvoir vérifier ce
          qu'il envoie avant de le soumettre.

          Il **déplie de lui-même** : filtrer sans déplier ne montrerait que des titres de
          tâches, c'est-à-dire rien de ce qu'on cherche.
        */}
        <button
          type="button"
          className={filtreMateriel ? "actif" : undefined}
          title="Ne garder que les machines qui partent sur porte-engin, telles qu'elles sont cochées dans Référentiels → Matériel. La main-d'œuvre, les fournitures et ce qui se rend au chantier par ses propres moyens disparaissent de la frise."
          onClick={() => {
            setFiltreMateriel((f) => !f);
            setFiltreAcheminement(false);
          }}
        >
          {filtreMateriel ? "Voir tout le planning" : "Matériel transférable seulement"}
        </button>

        {/*
          Le second filtre du responsable transport (Chris, 19/08/2026).

          *« J'ai besoin d'un bouton pour filtrer les articles pour lesquels livraison par nos
          soins est précisé »*, puis *« il faut faire apparaître la ligne avec l'article et la
          ligne avec les heures le jour »*.

          **Il part maintenant de la pastille, des deux natures** (039) : le matériau qu'on va
          chercher soi-même et le camion qui l'assure déclarent tous deux leur mode
          d'acheminement, donc le filtre lit la réponse là où elle se donne. La version
          précédente partait de la rotation et gardait les articles de la tâche par voisinage,
          faute de champ « qui livre » sur la pastille du matériau.
        */}
        <button
          type="button"
          className={filtreAcheminement ? "actif" : undefined}
          title="Ne garder que les lignes acheminées « par nos soins » : celles que la contre-étude a paramétrées ainsi, et celles qui portent au moins une pastille interne — le matériau qu'on va chercher soi-même, et le camion ou le chauffeur qui l'assure. Les tâches s'ouvrent d'elles-mêmes sur les champs de la pastille. Ce qui est livré par le fournisseur ou par un transporteur affrété disparaît de la frise, et ce qui n'a pas encore de mode est annoncé au-dessus."
          onClick={() => {
            setFiltreAcheminement((f) => !f);
            setFiltreMateriel(false);
          }}
        >
          {filtreAcheminement ? "Voir tout le planning" : "Livraisons par nos soins"}
        </button>

        {/*
          ORGANISER LE TRANSPORT DES MATÉRIAUX (Chris, 09/09/2026).

          *« Pour déclencher ce calcul tu positionneras un bouton en haut à côté de livraison par
          nos soins pour dire lance les calculs et la position des transports de matériaux. »*

          Il ne s'applique qu'au périmètre que Chris a fermé lui-même : **par nos soins, groupe
          coché, matériau de nos carrières**. Le conducteur a dispatché ses pastilles de tonnage et
          choisi ses lignes de camion ; le bouton en déduit le nombre d'exemplaires et les heures,
          et pose les rotations sur le camion ET sur le chauffeur.

          Il **remplace** les rotations des ouvrages qu'il organise — ajouter les doublerait au
          second clic, et le second clic est le cas normal : on corrige un tonnage, on relance. Ce
          qui est retiré est annoncé, parce qu'un geste qui efface sans le dire est un geste qu'on
          n'ose plus refaire.
        */}
        <button
          type="button"
          /*
            Éteint sur une version FIGÉE (mesuré à l'écran le 09/09/2026).

            Le serveur refuse déjà, et son message est juste — mais un bouton qui ne peut que
            échouer est un bouton qu'il ne faut pas offrir : on clique, on lit un refus, et on se
            demande si la fonction marche. L'écran sait déjà que la version est figée, il le dit
            deux lignes plus haut.
          */
          disabled={enAttente || !modifiable}
          title={
            modifiable
              ? "Poser les rotations de camion et de chauffeur à partir des pastilles de tonnage : nombre de voyages, nombre de camions, heures. Ne s'applique qu'aux matériaux de nos carrières acheminés par nos soins. Les rotations de ces ouvrages sont REMPLACÉES."
              : "Cette version est figée : son planning ne se modifie plus. Ouvrez la copie de travail."
          }
          onClick={() => {
            setBilanTransport(null);
            agir(
              organiserLeTransportDesMateriaux(versionId).then((bilan) => {
                setBilanTransport(bilan);
                return bilan;
              }),
            );
          }}
        >
          Organiser le transport des matériaux
        </button>

        <span className="note">
          {filtreAcheminement ? (
            <>
              Seules les lignes que <strong>nous acheminons</strong> sont affichées — le
              matériau qu'on va chercher, et le camion qui l'assure —, qu'elles le déclarent{" "}
              <strong>en contre-étude</strong> ou sur une pastille. Les barres restent
              modifiables : ce filtre ne cache que de l'affichage.
            </>
          ) : filtreMateriel ? (
            <>
              Seules les lignes de matériel <strong>à transférer</strong> sont affichées. Les
              barres restent modifiables : ce filtre ne cache que de l'affichage, il ne retire
              rien du chiffrage.
            </>
          ) : (
            <>
              Les barres se posent sur les <strong>lignes</strong> : déplier une tâche pour les
              atteindre. La barre du titre est un résumé — allonger celle d'une ligne allonge
              son travail, donc son déboursé.
            </>
          )}
        </span>
      </div>

      {/*
        **Ce bloc est la raison d'être des trois états de `porte_engin`.**

        Un code matériel qui n'a pas encore reçu de réponse au référentiel ne peut pas
        disparaître en silence : le responsable transport lirait une frise courte et partirait
        avec un porte-engin à moitié chargé, sans que rien ne le lui ait dit. C'est la leçon
        des absences non rattachées du 16/08 — un tableau incomplet se lit exactement comme un
        tableau complet.
      */}
      {filtreMateriel && aTrancher.length > 0 && (
        <p className="alerte" role="alert">
          {aTrancher.length} code(s) matériel de ce chantier n'ont pas encore d'acheminement
          renseigné et <strong>ne sont donc pas dans cette vue</strong> : {aTrancher.join(", ")}
          . À trancher dans Référentiels → Matériel, colonne « Acheminement ».
        </p>
      )}

      {/*
        Le même signalement, pour le même motif, sur le second filtre.

        `mode_livraison` a TROIS états, et une pastille dont personne n'a dit qui l'assure
        disparaît de cette vue sans un mot. Le responsable transport lirait une liste courte et
        organiserait ses camions en croyant avoir tout — le pire mode de panne d'un écran
        d'alerte.

        **Les deux natures y sont depuis la 039**, et les compter séparément n'est pas un
        détail : elles ne se renseignent pas au même endroit — une livraison sur la carte du
        matériau, une rotation sur celle du camion —, donc un total unique ne dirait pas où
        aller. Et c'est ce compteur qui rend le resserrement du filtre sans danger : il ne
        garde plus les lignes d'une tâche par voisinage, donc ce qui ne déclare rien sort
        vraiment de la vue.
      */}
      {filtreAcheminement && sansMode.total > 0 && (
        <p className="alerte" role="alert">
          {sansMode.total} pastille(s) de ce chantier ne disent pas encore qui les assure et{" "}
          <strong>ne sont donc pas dans cette vue</strong>
          {sansMode.livraisons > 0 && sansMode.rotations > 0
            ? ` — ${sansMode.livraisons} livraison(s) et ${sansMode.rotations} rotation(s)`
            : sansMode.livraisons > 0
              ? ` — ${sansMode.livraisons} livraison(s) de matériau`
              : ` — ${sansMode.rotations} rotation(s) de transport`}
          . Ouvrir la pastille et renseigner le mode de livraison pour qu'elles y entrent.
        </p>
      )}

      {/*
        Ce qu'un décalage en bloc vient de déplacer.

        Le sens est écrit en toutes lettres — « avancé », « repoussé » — et non en nombre
        signé : personne ne relit le signe d'un nombre sur un écran qu'il parcourt. Et les
        livraisons sont comptées séparément des barres, parce qu'elles ne bougent pas
        toujours du même geste : glisser d'une demi-journée déplace le travail sans changer
        le jour de la livraison, et il faut le dire plutôt que le laisser deviner.
      */}
      {/*
        LE COMPTE RENDU DU CALCUL DE TRANSPORT (09/09/2026).

        Le dimensionnement se lit terme par terme : le tonnage, la charge utile, la durée d'un
        tour décomposée, le nombre de tours, le nombre de camions. C'est la même exigence que
        Chris a posée le 04/09 pour le contrôle du transport — *« sois plus factuel : 80 tonnes
        = 17 tours de 20 minutes »* —, et pour la même raison : un total ne se vérifie pas, un
        total dont on voit les termes se vérifie de tête.

        Les ouvrages que le calcul a REFUSÉ d'organiser sont annoncés aussi, avec leur motif. Un
        ouvrage passé sous silence se lirait « rien à faire ici », ce qui est l'inverse de « je
        ne sais pas » — et leurs rotations existantes n'ont surtout pas été touchées.
      */}
      {bilanTransport !== null && <BilanTransport bilan={bilanTransport} />}

      {dernierDecalage !== null && (
        <p className="etat">
          {dernierDecalage.perimetre.quoi === "chapitre" ? "Chapitre" : "Poste"}{" "}
          {dernierDecalage.perimetre.numero}{" "}
          {dernierDecalage.demiJournees < 0 ? "avancé" : "repoussé"} de{" "}
          {Math.abs(dernierDecalage.demiJournees)} demi-journée(s) : {dernierDecalage.barres}{" "}
          barre(s) déplacée(s)
          {dernierDecalage.livraisons + dernierDecalage.transports > 0 ? (
            <>
              , {dernierDecalage.livraisons + dernierDecalage.transports} livraison(s) et
              transport(s) de {Math.abs(dernierDecalage.deltaJours)} jour(s)
            </>
          ) : (
            <> — les livraisons ne bougent pas, le bloc reste sur le même jour</>
          )}
          . <strong>Aucune durée, aucun coût n'a changé.</strong>
          {/*
            **Une pastille retenue au premier jour se dit.**

            *Décision de Chris du 04/09/2026 : une livraison déjà posée au jour 1 ne bloque
            plus le décalage du travail, elle s'arrête là.* C'est la seule entorse à « la
            forme du bloc ne change pas » : ces matériaux ne sont plus à la même distance
            du travail qu'ils servent. Le taire livrerait un planning qui a l'air juste —
            et ce sont eux qui commandent une carrière et une tournée.
          */}
          {dernierDecalage.pastillesRetenues > 0 && (
            <>
              {" "}
              <strong className="note--alerte">
                {dernierDecalage.pastillesRetenues} livraison(s) ou transport(s) sont restés au
                premier jour du chantier : ils ne pouvaient pas reculer plus loin.
              </strong>
            </>
          )}
        </p>
      )}

      {total === 0 && (
        <p className="alerte">
          Aucune tâche retenue sur cette version : il n'y a rien à planifier.
        </p>
      )}

      {/*
        **Un écran vide doit dire pourquoi il est vide.**

        *Signalé par Chris le 13/08/2026 : « j'ai positionné une date ferme de départ sur
        le 250010 et je ne vois plus aucune barre » — il regardait le devis figé.*

        Le devis figé ne porte aucune barre : le planning se pose sur la copie de travail.
        C'est voulu, mais rien ne le disait — une frise vide juste après avoir saisi une
        date se lit comme une régression de la saisie, et c'est exactement la conclusion
        qu'il a tirée. La phrase compte plus que le mécanisme.
      */}
      {total > 0 && !modifiable && planifiees === 0 && (
        <p className="alerte">
          Cette version est figée et ne porte aucune barre : le planning se pose sur la{" "}
          <strong>copie de travail</strong>. Changer de version dans le sélecteur, en haut.
        </p>
      )}

      {total > 0 && (
        <div
          ref={cadreDeLaFrise}
          className={`gantt${glisse === null && glisseBloc === null ? "" : " gantt--glisse"}`}
          /**
           * Le nombre de jours est déclaré ICI, sur le conteneur, et non plus seulement sur
           * chaque piste : la ligne des dates en a besoin autant qu'elles, et elle ne l'avait
           * pas. Elle serait restée large de dix jours quel que soit le chantier, donc décalée
           * de ses propres barres dès le onzième — l'inverse de ce qu'on cherche en figeant
           * l'en-tête. Une seule déclaration, dont tout le tableau hérite.
           */
          style={{ ["--jours" as string]: jours }}
          onPointerMove={suivre}
          onPointerUp={relacher}
          // Le glisse se termine aussi si le pointeur sort de la frise : sans cela,
          // relacher hors du tableau laisserait une barre collee au curseur.
          onPointerLeave={relacher}
          onScroll={(evenement) => {
            const cadre = evenement.currentTarget;
            retenirDefilement("planning-frise", versionId, cadre.scrollTop, cadre.scrollLeft);
          }}
        >
          <table>
            <thead>
              <tr>
                <th className="gantt__poste">Tâche</th>
                <th className="gantt__groupe">Regroupement</th>
                {/*
                  Les chiffres de la contre-étude, à gauche et INTACTS : ce sont eux que
                  le conducteur reconnaît, et c'est par rapport à eux que tout le reste
                  de la ligne se lit. Les barres, à droite, ne les modifient pas —
                  seule une barre étirée le fait, et elle le dit avant.
                */}
                <th
                  className="gantt__initial col-nombre"
                  title="Heures de main-d'œuvre chiffrées dans la contre-étude, et transport écarté de la durée"
                >
                  h chiffrées
                </th>
                <th className="gantt__duree" title="Demi-journées posées / proposées par les heures">
                  ½ j
                </th>
                {/*
                  Le nombre d'unités : le champ du produit qui raccourcit une barre sans
                  toucher à un euro. 28,75 h à trois, ce sont 9,58 h chacune — même
                  travail, même déboursé, calendrier divisé. L'étirement, lui, multiplie
                  le travail : les deux gestes ne doivent pas se ressembler.
                */}
                <th
                  className="gantt__effectif"
                  title="Combien d'unités de cette ressource travaillent en même temps. Divise les heures de la ligne pour donner sa durée, et ne change jamais le déboursé."
                >
                  nombre
                </th>
                {/*
                  **La dernière colonne figée : le jour où la ligne démarre** *(Chris,
                  22/09/2026)*.

                  Elle ne porte aucune donnée nouvelle — le jour se lit déjà sur la frise, à
                  l'endroit où la barre commence. Ce qu'elle apporte est de le lire SANS la
                  frise : sur un chantier de 38 jours, la barre d'une ligne peut être à
                  700 px hors de l'écran, et rien ne disait où la chercher. Avec le rang sous
                  les yeux, la barre de défilement devient une adresse.

                  Figée, donc, et en DERNIÈRE position : elle est ce qu'on lit juste avant de
                  faire défiler, et c'est aussi elle qui porte désormais le trait de séparation
                  avec la frise.
                */}
                <th
                  className="gantt__depart"
                  title="Le jour où commence la barre de cette ligne. Reste à l'écran quand on fait défiler la frise."
                >
                  départ
                </th>
                <th className="gantt__frise">
                  {/*
                    **Des dates dès que le chantier en a une** (PRD §4.5), des jours
                    relatifs sinon.

                    Deux choses changent avec la datation, et la seconde compte autant
                    que la première :

                     1. l'étiquette devient « 20/08 » au lieu de « J6 » ;
                     2. **le repère devient le LUNDI** au lieu d'un jour sur cinq. Sur
                        une frise de jours ouvrés, un trait tous les cinq jours tombe
                        juste par accident — dès qu'un férié s'y glisse, il ne désigne
                        plus rien. Le lundi, lui, se reconnaît sur un planning de
                        chantier : c'est la semaine, l'unité dans laquelle le métier
                        parle de son avancement.

                    **TOUTES les dates sont écrites**, depuis le 14/08/2026 : « même si un
                    chantier est long, bien en visuel chaque date ». Une colonne sur deux
                    était masquée au-delà de vingt jours — non par choix, mais parce que la
                    frise était élastique et qu'une journée finissait par mesurer moins d'un
                    millimètre. Maintenant qu'elle vaut `--largeur-jour` quel que soit le
                    chantier, chaque étiquette a la place de s'écrire et rien n'est caché.
                  */}
                  <div className="gantt__jours">
                    {Array.from({ length: jours }, (_, i) => i + 1).map((jour) => {
                      const date = datesDeLaFrise?.[jour - 1];
                      const repere =
                        date === undefined ? jour % 5 === 0 : jourDeLaSemaine(date) === 1;
                      return (
                        <span
                          key={jour}
                          className={`gantt__jour${repere ? " gantt__jour--repere" : ""}`}
                          title={
                            date === undefined
                              ? undefined
                              : `J${jour} — ${formaterDateLongue(date)}`
                          }
                        >
                          {date !== undefined && (
                            <span className="gantt__jour-date">
                              {formaterDate(date).slice(0, 5)}
                            </span>
                          )}
                          {/*
                            **Le rang reste écrit SOUS la date** (demande de Chris,
                            16/08/2026). La saisie est en jours relatifs et le restera :
                            toutes les infobulles, tous les champs de livraison et de
                            transport parlent en « J6 ». Afficher la seule date obligerait
                            à recompter les jours ouvrés de tête pour retrouver le champ à
                            corriger — et un férié au milieu rend ce calcul faux.
                          */}
                          <span className="gantt__jour-rang">J{jour}</span>
                        </span>
                      );
                    })}
                  </div>
                </th>
                {/*
                  Le coût caché, en bout de ligne : ce que les demi-journées immobilisent
                  réellement, et l'écart avec les heures chiffrées. Une ligne de 6 h
                  occupe deux demi-journées, donc 8 h de présence — 2 h payées qui ne
                  figurent nulle part dans la contre-étude.
                */}
                <th
                  className="gantt__ecart col-nombre"
                  title="Heures immobilisées par les demi-journées posées, et écart avec les heures chiffrées"
                >
                  h posées
                </th>
              </tr>
            </thead>
            <tbody>
              {visiblesALEcran.map((ligne) => {
                /**
                 * La ligne d'un chapitre : son titre, et la barre de ce qu'il porte.
                 *
                 * Elle occupe les mêmes colonnes qu'une tâche pour que la frise reste
                 * alignée — une ligne à `colSpan` complet décalerait la piste et la
                 * barre récapitulative tomberait à côté des barres qu'elle résume.
                 */
                if (ligne.nature === "chapitre") {
                  // Un chapitre dont plus rien ne subsiste sous le filtre n'a plus de titre à
                  // porter : le laisser afficherait une frise de rubriques vides.
                  if (!chapitreRetenu(ligne.chapitre)) return null;
                  const etendue = etendueDuChapitre(ligne.chapitre);
                  const numero = ligne.chapitre.numero;
                  const ouvert = toutOuvert || chapitresOuverts.has(numero);
                  /*
                    Ce que le chapitre a réellement à montrer, filtre compris. Une flèche
                    qui n'ouvre rien serait pire qu'absente : on cliquerait, il ne se
                    passerait rien, et on chercherait la panne ailleurs.
                  */
                  const aOuvrir =
                    ligne.chapitre.nature === "chapitre"
                      ? ligne.chapitre.enfants.filter(chapitreRetenu).length
                      : 0;
                  return (
                    <tr className="gantt__chapitre" key={`c-${numero}`}>
                      <td className="gantt__poste" colSpan={6}>
                        <span style={{ paddingLeft: `${ligne.profondeur * 0.9}rem` }}>
                          {aOuvrir > 0 && (
                            <button
                              type="button"
                              className="lien gantt__plier"
                              /*
                                Désactivée — et non masquée — quand un filtre tient tout
                                ouvert : la faire disparaître donnerait à croire que ce
                                chapitre n'a pas de détail.
                              */
                              disabled={toutOuvert}
                              onClick={() => {
                                setChapitresOuverts((courant) => {
                                  const suivant = new Set(courant);
                                  if (!suivant.delete(numero)) suivant.add(numero);
                                  return suivant;
                                });
                              }}
                              title={
                                toutOuvert
                                  ? "Un filtre est actif : tout est ouvert."
                                  : ouvert
                                    ? `Replier ${numero}`
                                    : `Ouvrir ${numero} — ${aOuvrir} rubrique(s)`
                              }
                            >
                              {ouvert ? "▾" : "▸"}
                            </button>
                          )}{" "}
                          <span className="code">{numero}</span> {ligne.chapitre.designation}
                          {!ouvert && aOuvrir > 0 && (
                            <span className="note"> · {aOuvrir} rubrique(s)</span>
                          )}
                        </span>
                      </td>
                      <td className="gantt__frise">
                        <div className="gantt__piste" style={{ ["--jours" as string]: jours }}>
                          {etendue !== null &&
                            (() => {
                              /*
                                **Le résumé d'un chapitre se DÉPLACE désormais** (demande de
                                Chris, 19/08/2026) — il ne s'étire toujours pas.

                                Sa longueur reste calculée sur son détail : c'est pour ça
                                qu'il n'a pas de poignée d'étirement. Mais le glisser
                                translate tout ce qu'il contient, sous-chapitres compris,
                                sans toucher à une seule durée.
                              */
                              const glisseCeBloc =
                                glisseBloc?.quoi === "chapitre" &&
                                glisseBloc.numero === ligne.chapitre.numero
                                  ? glisseBloc
                                  : null;
                              const debutDemi = etendue.debutDemi + (glisseCeBloc?.delta ?? 0);
                              const atterrissage = depuisIndexDemiJournee(debutDemi);
                              return (
                                <div
                                  className={`barre barre--chapitre${
                                    glisseCeBloc !== null ? " barre--glissee" : ""
                                  }`}
                                  /*
                                    Dessinée en DEMI-JOURNÉES, comme les barres qu'elle
                                    résume (demande du métier, 16/08/2026). Sur des bornes
                                    en jours, elle partait du matin et finissait au soir
                                    quoi que fasse le détail : décaler une ligne d'une
                                    demi-journée ne la faisait pas bouger.
                                  */
                                  style={{
                                    left: `${(debutDemi / (jours * 2)) * 100}%`,
                                    width: `${((etendue.finDemi - etendue.debutDemi + 1) / (jours * 2)) * 100}%`,
                                    ...(modifiable ? { cursor: "grab" } : {}),
                                  }}
                                  title={
                                    `${ligne.chapitre.numero} ${ligne.chapitre.designation}\n` +
                                    (glisseCeBloc !== null
                                      ? `→ ${nommerJour(atterrissage.debutRelatif)} ${atterrissage.position === "apres_midi" ? "après-midi" : "matin"}`
                                      : `${nommerJour(etendue.debut)} → ${nommerJour(etendue.fin)} · ${etendue.taches} tâche(s) planifiée(s)`) +
                                    "\nSa longueur est calculée sur son détail : elle ne s'étire pas." +
                                    (modifiable
                                      ? " Glisser pour décaler TOUT le chapitre — aucune durée, aucun coût ne change."
                                      : "")
                                  }
                                  onPointerDown={(e) => {
                                    if (!modifiable || enAttente) return;
                                    commencerBloc(
                                      {
                                        quoi: "chapitre",
                                        numero: ligne.chapitre.numero,
                                        debutDemi: etendue.debutDemi,
                                        finDemi: etendue.finDemi,
                                      },
                                      e,
                                    );
                                  }}
                                />
                              );
                            })()}
                          {/* Hacher le chapitre : toutes ses lignes d'un coup. */}
                          {modifiable && etendue !== null && (
                            <button
                              type="button"
                              className="gantt__hacher"
                              disabled={enAttente}
                              title={
                                "Hacher tout le bloc : chaque ligne qui porte des barres reçoit une reprise de plus, " +
                                "en parts égales. Les pastilles de transport et de livraison ne bougent pas. " +
                                "Aucune durée totale, aucun euro ne change."
                              }
                              onClick={() => {
                                hacherLeBloc("chapitre", ligne.chapitre.numero);
                              }}
                            >
                              +
                            </button>
                          )}
                          {/*
                            **Plus aucun montant sur un chapitre** (Chris, 25/09/2026) : *« montant
                            sur chaque tâche »*. Le chapitre (16/08) puis le sous-chapitre (le
                            matin du 25/09) l'ont porté ; il vit désormais au bout de chaque tâche,
                            voir `echeancesTache`.
                          */}
                        </div>
                      </td>
                      <td className="gantt__ecart col-nombre" />
                    </tr>
                  );
                }

                const { tache } = ligne;
                const visibles = ressourcesVisibles(tache);
                /*
                  Une tâche dont il ne reste aucune ligne disparaît entièrement sous un
                  filtre — titre compris : ce qu'on cherche est une liste de machines ou de
                  matériaux, pas la liste des postes qui n'en ont pas.

                  **Les DEUX filtres**, depuis le 25/08/2026. Seul le porte-engin l'appliquait,
                  et c'est la moitié de l'anomalie de Chris : sous « livraisons par nos soins »,
                  toutes les tâches du chantier restaient à l'écran, chacune réduite à son
                  titre. L'écran ne montrait donc rien de moins qu'un planning replié — d'où
                  « cela n'a pas l'air de fonctionner ».
                */
                if ((filtreMateriel || filtreAcheminement) && visibles.length === 0) return null;
                const p = tache.planning;
                const ecart = p.demiJourneesPosees !== p.demiJourneesProposees;
                /*
                  Le filtre déplie de lui-même : sous filtre, une tâche repliée ne montrerait
                  que son titre, c'est-à-dire exactement ce qu'on ne cherche pas.

                  **L'autre moitié de l'anomalie du 25/08/2026.** `filtreAcheminement` avait
                  été oublié ici alors que le commentaire du bouton l'annonçait en toutes
                  lettres. Résultat : le responsable transport n'atteignait ni la pastille ni
                  ses champs — jour, date, volume, heure limite, mode, groupe, fournisseur —,
                  qui sont tout l'objet de cette vue. Ils vivent sous la ligne, et la ligne ne
                  s'affiche que dépliée.
                */
                const depliee = filtreMateriel || filtreAcheminement || depliees.has(tache.numero);
                // Une tâche sans ligne en heures porte ses barres elle-même : c'est le
                // seul cas où le titre se saisit.
                const titrePorteur = porteSesBarres(tache);
                /**
                 * Où le résumé du titre se dessine, à la demi-journée près.
                 *
                 * **Le même périmètre que le résumé du chapitre** — c'est la même question,
                 * et deux périmètres feraient qu'une barre grise s'étirerait sans que celle
                 * du dessus ne bouge. Depuis le 27/08/2026, ce périmètre est *les barres et
                 * rien d'autre* : une pastille n'étire aucun résumé.
                 *
                 * Le serveur, lui, garde ses bornes en jours, pastilles comprises : elles
                 * nomment les jours occupés par le chantier — c'est ce que la liste des
                 * chantiers et l'enchaînement lisent —, elles ne tracent pas. **L'infobulle
                 * de cette barre suit donc l'enveloppe et non ces bornes**, sans quoi elle
                 * annoncerait un J10 que la barre ne dessine pas.
                 */
                const enveloppeTitre = enveloppeEnDemiJournees(barresDuResume(tache), []);
                /*
                  Les MORCEAUX que la barre grise dessine : son étendue coupée à chaque trou
                  (Chris, 25/09/2026 — la 1.5 hachée à J5, J9 et J16 ne montre plus une barre
                  continue de J5 à J16), puis à chaque départ de barre (28/09/2026 — la reprise
                  qu'un hachage pose collée à la dernière se saisit seule, sans passer ligne
                  par ligne).
                */
                const morceauxTitre = morceauxEnDemiJournees(barresDuResume(tache));
                /*
                  Ce qu'on dessine à la place quand aucune barre ne résume la tâche : ses
                  pastilles, à leur jour, et rien entre elles. Sans ça, un poste qui n'a que
                  des rotations n'aurait plus rien montré une fois replié.
                */
                const pastillesDuTitreDeLaTache = pastillesDuTitre(tache);
                /**
                 * Le CA facturable de la tâche — **toute tâche, à n'importe quelle profondeur**
                 * (Chris, 25/09/2026 : 2.1.1 comme 1.1). Une échéance par période de travail
                 * quand elle est hachée ; les montants et les jours viennent du moteur, les
                 * mêmes que ceux du carnet de facturation.
                 */
                const echeancesTache = echeancesDeLaTache(tache);
                const heuresDevis = devis.get(tache.numero);
                /**
                 * De combien les heures de MO s'écartent de celles du devis figé.
                 *
                 * `null` quand il n'y a rien à comparer (on regarde le devis lui-même)
                 * ou quand elles sont d'accord **à l'affichage** — le même critère que
                 * `visible()` : un résidu d'arrondi de 10⁻¹⁸ h n'est pas un écart, et
                 * l'annoncer discréditerait le signal dès la première tâche.
                 */
                const ecartAuDevis =
                  heuresDevis === undefined ||
                  formaterNombre(heuresDevis, 2) === formaterNombre(p.heuresMO, 2)
                    ? null
                    : Number(p.heuresMO) - Number(heuresDevis);

                return (
                  // La clé porte sur le FRAGMENT, pas sur les `tr` qu'il contient :
                  // c'est lui l'élément de la liste rendue par `map`. Le raccourci
                  // `<>` n'accepte pas de clé — React avertissait donc une fois par
                  // ligne, et ne pouvait pas réconcilier les lignes à leur
                  // réordonnancement.
                  <Fragment key={tache.numero}>
                    <tr>
                      {/* Le retrait dit la place de la tâche dans le classeur : les
                          chapitres sont désormais des lignes à part entière, et sans
                          décalage on ne verrait plus ce qui dépend de quoi. */}
                      <td
                        className="gantt__poste"
                        style={{ paddingLeft: `${0.4 + ligne.profondeur * 0.9}rem` }}
                      >
                        <span className="groupe-pastille"
                          style={{
                            backgroundColor: tache.groupe?.couleur ?? "transparent",
                            borderColor: tache.groupe?.couleur ?? "var(--trait)",
                          }}
                        />
                        <span className="code">{tache.numero}</span> {tache.designation}
                      </td>
                      {/*
                        Le regroupement en clair, code ET libellé : la pastille de couleur
                        seule suppose qu'on connaisse le code par cœur, et une couleur
                        héritée ne se distingue pas d'une couleur posée à l'œil nu.
                      */}
                      <td className="gantt__groupe">
                        {tache.groupe === null ? (
                          <span className="neutre" title="Aucun regroupement affecté">
                            —
                          </span>
                        ) : (
                          <span
                            title={
                              tache.groupe.source === "affecte"
                                ? `${tache.groupe.libelle} — affecté sur cette tâche`
                                : `${tache.groupe.libelle} — ${tache.groupe.source}`
                            }
                          >
                            <span className="code">{tache.groupe.code}</span>{" "}
                            <span className="gantt__groupe-libelle">{tache.groupe.libelle}</span>
                          </span>
                        )}
                      </td>
                      <td className="gantt__initial col-nombre">
                        {formaterNombre(p.heuresMO, 2)} h
                        {/*
                          Le transport, affiché à côté et non retiré en silence : c'est la
                          seule façon d'expliquer qu'une tâche portant 8,50 h de
                          main-d'œuvre ne dure que deux demi-journées.
                        */}
                        {visible(p.heuresHorsDuree) && (
                          <span
                            className="gantt__transport"
                            title="Heures de transport : comptées dans le déboursé, écartées de la durée — les chauffeurs portent les matériaux en début de travail"
                          >
                            {" + "}
                            {formaterNombre(p.heuresHorsDuree, 2)} h transport
                          </span>
                        )}
                        {/*
                          **Les heures ne sont plus celles du devis.** C'est le seul
                          endroit où un étirement passé se voit : le témoin « posées /
                          proposées » ne peut pas le montrer, puisque la proposition se
                          recalcule sur les heures déjà étirées et retombe donc
                          toujours d'accord avec la barre.
                        */}
                        {ecartAuDevis !== null && (
                          <span
                            className={
                              ecartAuDevis < 0 ? "gantt__gain" : "gantt__cache"
                            }
                            title={
                              `Le devis figé chiffre ${formaterNombre(devis.get(tache.numero) ?? "0", 2)} h de main-d'œuvre sur cette tâche. ` +
                              "Un étirement de barre a changé les heures, donc le déboursé. " +
                              "Ce n'est pas forcément une erreur — mais ça ne doit pas passer inaperçu."
                            }
                          >
                            {" "}
                            (devis : {formaterNombre(devis.get(tache.numero) ?? "0", 2)} h)
                          </span>
                        )}
                        {/*
                          **Le retour en arrière, à l'endroit même où l'écart se voit.**

                          Signaler qu'une tâche s'est écartée du devis sans offrir le
                          moyen d'y revenir, c'est ce qui a coûté 6 660 € sur APO250010
                          le 11/08/2026 : la réparation a dû se faire en SQL, ligne à
                          ligne, et un conducteur n'aurait rien pu faire.

                          Le bouton n'existe QUE là où il y a quelque chose à rendre —
                          un bouton toujours présent inviterait à défaire un travail
                          volontaire, et sur les tâches conformes il ne ferait rien.
                        */}
                        {ecartAuDevis !== null && modifiable && (
                          <button
                            type="button"
                            className="lien gantt__rendre"
                            disabled={enAttente}
                            title={
                              "Remettre sur cette tâche les heures que le devis figé lui chiffre.\n" +
                              "Les prix et les fournitures ne bougent pas : seules les heures reviennent.\n" +
                              "C'est le seul geste qui rende la valeur EXACTE du devis — étirer puis " +
                              "réduire une barre laisse un résidu d'arrondi."
                            }
                            onClick={() => {
                              const cible = devis.get(tache.numero) ?? "0";
                              /**
                                Le déboursé n'est PAS estimé dans ce message, et c'est
                                voulu : le facteur des heures ne s'applique qu'aux
                                lignes en heures, jamais aux fournitures. Sur une tâche
                                mixte, un montant projeté serait faux — mieux vaut
                                annoncer le sens de l'effet et laisser le compte rendu
                                dire ce qui s'est réellement passé.
                              */
                              const accepte = globalThis.confirm(
                                `${tache.numero} ${tache.designation}\n\n` +
                                  "Rendre à cette tâche les heures du devis figé :\n" +
                                  `  heures    ${formaterNombre(tache.heures, 2)} h → ${formaterNombre(cible, 2)} h\n\n` +
                                  `Le déboursé de la tâche ${ecartAuDevis > 0 ? "baissera" : "remontera"} d'autant. ` +
                                  "Les prix négociés et les fournitures ne sont pas touchés.\n\n" +
                                  "Appliquer ?",
                              );
                              if (!accepte) return;
                              agir(
                                rendreLesHeuresDuDevis(versionId, tache.numero).then((rendu) => {
                                  setDernierRetour(rendu);
                                  return rendu;
                                }),
                              );
                            }}
                          >
                            ↩ rendre les heures du devis
                          </button>
                        )}
                      </td>
                      <td className="gantt__duree">
                        {p.demiJourneesPosees === 0 ? (
                          <span className="neutre">—</span>
                        ) : (
                          <span className={ecart ? "en-ecart" : undefined}
                            title={
                              ecart
                                ? `${p.demiJourneesPosees} posées (la plus longue de ses lignes), ${p.demiJourneesProposees} impliquées par ${formaterNombre(p.heuresMO, 2)} h de main-d'œuvre`
                                : `${formaterNombre(p.heuresMO, 2)} h de main-d'œuvre`
                            }
                          >
                            {p.demiJourneesPosees}
                            {ecart && ` / ${p.demiJourneesProposees}`}
                          </span>
                        )}
                      </td>
                      <td className="gantt__effectif">
                        {/*
                          **Pas de champ sur le TITRE : le nombre se saisit sur les lignes.**

                          *Décision du métier du 12/08/2026 — l'équipe s'affecte sur le
                          détail, MOICDC et MOIOUVTRX, et non sur la tâche.*

                          Reste ce que la tâche seule peut dire : la durée que son détail
                          implique, et le dépliant pour aller la travailler.
                        */}
                        <span className="note" title="Durée que le détail implique : la plus longue de ses lignes.">
                          {p.demiJourneesDuDetail > 0 ? `${p.demiJourneesDuDetail} ½ j` : "—"}
                        </span>
                        {/*
                          LE COMPTE EST CELUI DES LIGNES MONTRÉES, pas de toutes les lignes.

                          Il annonçait `tache.ressources.length`, donc les supprimées comprises
                          depuis qu'elles sont écartées de l'affichage (10/09/2026) : le bouton
                          aurait promis « 5 ligne(s) » pour en déplier 3, sans que rien
                          n'explique les deux manquantes. Un compteur qui ment sur ce qu'il
                          ouvre est pire qu'un compteur absent — on cherche la ligne perdue.
                        */}
                        {visibles.length > 0 && (
                          <button
                            type="button"
                            className="lien gantt__deplier"
                            title={
                              `${String(visibles.length)} ligne(s) de ressources sur cette tâche` +
                              (modifiable
                                ? " — c'est là que les barres se posent, se déplacent et s'étirent"
                                : "")
                            }
                            onClick={() => {
                              basculer(tache.numero);
                            }}
                          >
                            {depliee ? "▾" : "▸"} {visibles.length} ligne(s)
                          </button>
                        )}
                      </td>
                      {/*
                        Le départ de la TÂCHE : le premier jour où l'une quelconque de ses lignes
                        a quelque chose — barre OU pastille. Voir `jourDeDepartDeLaTache`.
                      */}
                      <td className="gantt__depart">
                        {(() => {
                          const jour = jourDeDepartDeLaTache(tache);
                          return jour === null ? (
                            <span className="neutre">—</span>
                          ) : (
                            <span title={nommerJour(jour)}>J{jour}</span>
                          );
                        })()}
                      </td>
                      <td className="gantt__frise">
                        <div className="gantt__piste" style={{ ["--jours" as string]: jours }}>
                          {/*
                            **La barre du titre résume son détail : elle se DÉPLACE, elle
                            ne s'étire pas.**

                            *Vision du métier du 12/08/2026 : « la barre du titre devient
                            un résumé — la plus longue de son détail, ni déplaçable ni
                            étirable ». Chris revient sur la moitié de cette décision le
                            19/08/2026 : « pouvoir avancer ou reculer les barres grises, et
                            que tout le détail bouge avec ».*

                            Le « ni étirable » demeure, et pour une raison qui n'a pas
                            bougé : étirer un résumé ne dirait pas LAQUELLE de ses lignes
                            s'allonge, donc quel déboursé change. Déplacer n'a qu'un sens
                            possible — tout part ensemble — et ne coûte rien. C'est la même
                            frontière que sur une barre de ligne, où le corps déplace et
                            seule la poignée du bord étire.

                            Ce qu'elle montre reste l'ÉTENDUE de son détail — du premier au
                            dernier jour occupé —, tandis que la colonne « ½ j » en donne la
                            durée. Les deux diffèrent dès que le travail est haché.
                          */}
                          {!titrePorteur &&
                            p.debutRelatif !== null &&
                            p.finRelative !== null &&
                            enveloppeTitre !== null &&
                            (() => {
                              const glisseCeBloc =
                                glisseBloc?.quoi === "ouvrage" &&
                                glisseBloc.numero === tache.numero
                                  ? glisseBloc
                                  : null;
                              const debutDemi =
                                (glisseCeBloc?.debutDemi ?? enveloppeTitre.debut) + (glisseCeBloc?.delta ?? 0);
                              /*
                                Une tâche hachée se déplace MORCEAU PAR MORCEAU (Chris, 25/09/2026 :
                                « pas que les 3 barres bougent synchronisées ») : on saisit une
                                période, elle seule part, avec ses lignes et ses pastilles. D'un
                                seul tenant, la barre emporte toujours la tâche entière.
                              */
                              const hachee = morceauxTitre.length > 1;
                              const bouge = (periode: { debut: number }): boolean =>
                                glisseCeBloc !== null &&
                                (glisseCeBloc.seulementLaPeriode !== true ||
                                  glisseCeBloc.debutDemi === periode.debut);
                              const atterrissage = depuisIndexDemiJournee(debutDemi);
                              /*
                                Les jours que l'infobulle annonce sont ceux de la BARRE, et
                                non les bornes du serveur : depuis le 27/08/2026 celles-ci
                                comptent les pastilles sans condition — elles disent jusqu'à
                                quand le chantier est occupé —, alors que le résumé les
                                écarte dès qu'il a des barres. Les lire ici ferait annoncer
                                un premier jour que la barre ne dessine pas.
                              */
                              return morceauxTitre.map((periode, rang) => {
                              const delta = bouge(periode) ? (glisseCeBloc?.delta ?? 0) : 0;
                              const premier = depuisIndexDemiJournee(periode.debut).debutRelatif;
                              const dernier = depuisIndexDemiJournee(periode.fin).debutRelatif;
                              return (
                              <div
                                key={periode.debut}
                                className={`barre barre--resume${
                                  bouge(periode) ? " barre--glissee" : ""
                                }${
                                  // Collé au précédent : un liseré, sans quoi deux morceaux qui
                                  // se touchent se liraient comme un seul.
                                  rang > 0 && morceauxTitre[rang - 1]?.fin === periode.debut - 1
                                    ? " barre--morceau-colle"
                                    : ""
                                }`}
                                /*
                                  En DEMI-JOURNÉES, comme le résumé du chapitre et comme
                                  les barres du détail : sur des bornes en jours, il
                                  débordait d'une demi-journée dès qu'une ligne
                                  commençait l'après-midi.
                                */
                                style={{
                                  left: `${((periode.debut + delta) / (jours * 2)) * 100}%`,
                                  width: `${((periode.fin - periode.debut + 1) / (jours * 2)) * 100}%`,
                                  ...(modifiable ? { cursor: "grab" } : {}),
                                }}
                                onPointerDown={(e) => {
                                  if (!modifiable || enAttente) return;
                                  commencerBloc(
                                    hachee
                                      ? {
                                          quoi: "ouvrage",
                                          numero: tache.numero,
                                          debutDemi: periode.debut,
                                          finDemi: periode.fin,
                                          seulementLaPeriode: true,
                                        }
                                      : {
                                          quoi: "ouvrage",
                                          numero: tache.numero,
                                          debutDemi: enveloppeTitre.debut,
                                          finDemi: enveloppeTitre.fin,
                                        },
                                    e,
                                  );
                                }}
                                title={
                                  `${tache.numero} ${tache.designation}\n` +
                                  (bouge(periode)
                                    ? `→ ${nommerJour(atterrissage.debutRelatif)} ${atterrissage.position === "apres_midi" ? "après-midi" : "matin"}`
                                    : `${nommerJour(premier)} → ${nommerJour(dernier)}` +
                                      (morceauxTitre.length > 1
                                        ? ` · morceau ${String(rang + 1)} sur ${String(morceauxTitre.length)}`
                                        : "") +
                                      ` · ${p.demiJourneesPosees} demi-journée(s) de travail`) +
                                  "\nRésumé de son détail : sa longueur ne s'étire pas. " +
                                  (modifiable
                                    ? hachee
                                      ? "Glisser pour décaler CE MORCEAU seul, avec ses lignes et ses livraisons — aucune durée, aucun coût ne change. "
                                      : "Glisser pour décaler TOUT le poste — aucune durée, aucun coût ne change. "
                                    : "") +
                                  "Déplier la tâche pour agir sur les barres de ses lignes."
                                }
                              />
                              );
                              });
                            })()}
                          {/* Hacher la tâche : toutes ses lignes d'un coup. Le titre porteur a déjà le sien. */}
                          {modifiable && !titrePorteur && enveloppeTitre !== null && (
                            <button
                              type="button"
                              className="gantt__hacher"
                              disabled={enAttente}
                              title={
                                "Hacher tout le bloc : chaque ligne qui porte des barres reçoit une reprise de plus, " +
                                "en parts égales. Les pastilles de transport et de livraison ne bougent pas. " +
                                "Aucune durée totale, aucun euro ne change."
                              }
                              onClick={() => {
                                hacherLeBloc("ouvrage", tache.numero);
                              }}
                            >
                              +
                            </button>
                          )}
                          {/*
                            **Les pastilles d'un poste que rien ne résume** (27/08/2026).

                            *« J'ai mis deux pastilles sur amené et repli, et quand je mets
                            une pastille sur jour 4 la barre grise s'étire, cela ne va
                            pas. »* Elle ne s'étire plus — et ce qui prend sa place est ce
                            qui est vrai : deux camions, deux jours, rien entre les deux.

                            **En lecture seule ici, et c'est délibéré.** La même pastille se
                            déplace, se retire et se saisit dans le détail, qui est le seul
                            endroit où l'on voit à QUELLE ligne elle appartient. Deux
                            endroits pour le même geste, sur une frise où l'un est dessiné
                            juste au-dessus de l'autre, finiraient par se contredire.
                          */}
                          {pastillesDuTitreDeLaTache.livraisons.map(({ ressource, livraison }) => (
                            <Pastille
                              key={livraison.id}
                              livraison={livraison}
                              ressource={ressource}
                              unite={ressource.unite}
                              jours={jours}
                              nommerJour={nommerJour}
                              glisse={null}
                              modifiable={false}
                              selectionnee={false}
                              onSelectionner={() => undefined}
                              onCommencer={() => undefined}
                              onRetirer={() => undefined}
                            />
                          ))}
                          {pastillesDuTitreDeLaTache.transports.map(({ ressource, transport }) => (
                            <PastilleTransport
                              key={transport.id}
                              transport={transport}
                              ressource={ressource}
                              jours={jours}
                              nommerJour={nommerJour}
                              glisse={null}
                              modifiable={false}
                              selectionnee={false}
                              onSelectionner={() => undefined}
                              onCommencer={() => undefined}
                              onRetirer={() => undefined}
                            />
                          ))}
                          {/*
                            Les barres PROPRES d'un poste de fournitures pures — un
                            panneau, un dos d'âne. Il n'a aucune ligne en heures, donc
                            rien à quoi accrocher sa barre : il la porte lui-même, et elle
                            reste pleinement saisissable.
                          */}
                          {titrePorteur &&
                            p.phases.map((phase) => (
                              <Barre
                                key={phase.id}
                                phase={phase}
                                jours={jours}
                                nommerJour={nommerJour}
                                couleur={tache.groupe?.couleur ?? null}
                                nature={null}
                                intitule={`${tache.numero} ${tache.designation}`}
                                glisse={glisse}
                                modifiable={modifiable}
                                selectionnee={phase.id === choisie}
                                onSelectionner={() => {
                                  setChoisie(phase.id);
                                  setLivraisonChoisie(null);
                                  setDernierRecalcul(null);
                                }}
                                onCommencer={(quoi, evenement) => {
                                  commencer(phase, quoi, evenement);
                                }}
                                onRetirer={() => {
                                  if (phase.id === choisie) setChoisie(null);
                                  agir(retirerBarre(versionId, phase.id));
                                }}
                              />
                            ))}
                          {/* Hacher le poste : une pose peut être interrompue puis
                              reprise autant de fois que nécessaire. */}
                          {titrePorteur && modifiable && p.phases.length > 0 && (
                            <button
                              type="button"
                              className="gantt__hacher"
                              disabled={enAttente || !peutHacher(p.phases)}
                              title={titreDuHachage(p.phases, "ce poste")}
                              onClick={() => {
                                agir(hacherBarres(versionId, tache.numero, null));
                              }}
                            >
                              +
                            </button>
                          )}
                          {/*
                            **Un poste de fournitures pures peut recevoir une barre.**

                            *Demandé par Chris le 12/08/2026 : « j'aimerais voir dans le
                            planning toutes les lignes de la contre-étude pour gérer les
                            barres sur toutes les lignes ».*

                            Sur un panneau DFCI à 250 € ou un dos d'âne, il n'y a aucune
                            heure de main-d'œuvre — donc aucune ligne à laquelle accrocher
                            une barre, et pourtant une équipe y passe une demi-journée.
                            Poser un panneau prend une demi-journée : c'est une décision de
                            conducteur, pas une conséquence d'un chiffrage.

                            **Le jour de début n'est pas envoyé** : le serveur la place
                            après ce qui est déjà planifié. Il valait `1` en dur, et les
                            deux barres posées ainsi sur APO250012 ont atterri sous celle
                            de 1.1.1 alors que le reste courait jusqu'à J21.
                          */}
                          {titrePorteur && modifiable && p.phases.length === 0 && (
                            <button
                              type="button"
                              className="lien gantt__poser"
                              disabled={enAttente}
                              title={
                                "Ce poste ne porte aucune heure : sa durée ne se déduit d'aucun " +
                                "calcul, c'est vous qui la décidez. La barre part à une demi-journée, " +
                                "après ce qui est déjà planifié, et se déplace ensuite."
                              }
                              onClick={() => {
                                agir(
                                  poserBarreDeTitre(versionId, tache.numero, { demiJournees: 1 }),
                                );
                              }}
                            >
                              poser 1 ½ j
                            </button>
                          )}
                          {/* Ce qui explique une piste vide : il n'y a rien à résumer
                              tant qu'aucune ligne ne porte de barre. */}
                          {!titrePorteur && p.debutRelatif === null && (
                            <span className="note gantt__sans-heures">
                              aucune barre sur son détail
                            </span>
                          )}
                          {/*
                            **Le CA facturable au bout de la barre de CHAQUE tâche** (Chris,
                            25/09/2026 : *« montant sur chaque tâche »*), à n'importe quelle
                            profondeur. Une tâche hachée en porte un par période de travail, au
                            prorata du déboursé planifié.

                            `prixVente` est ici celui du poste lui-même, et la tâche est retenue
                            — `aplatir` écarte les postes exclus du chiffrage. Une option écartée
                            n'apparaît pas dans ce CA.
                          */}
                          {echeancesTache.map((echeance) =>
                            echeance.finDemiJournee === null || !visible(echeance.prixVente) ? null : (
                              <span
                                key={echeance.rang}
                                className={`gantt__ca${
                                  caAuBord(echeance.finDemiJournee, jours) ? " gantt__ca--dedans" : ""
                                }`}
                                style={
                                  caAuBord(echeance.finDemiJournee, jours)
                                    ? undefined
                                    : { left: `${((echeance.finDemiJournee + 1) / (jours * 2)) * 100}%` }
                                }
                                title={
                                  `${tache.numero} ${tache.designation}\n` +
                                  (echeance.parts > 1
                                    ? `CA facturable à la fin de la période ${String(echeance.rang)} sur ${String(echeance.parts)} : ` +
                                      `${formaterMontant(echeance.prixVente)} € (sur ${formaterMontant(tache.prixVente)} €, au prorata du déboursé planifié)\n`
                                    : `CA facturable à la fin de la tâche : ${formaterMontant(echeance.prixVente)} €\n`) +
                                  "Le montant se facture sur chaque tâche, jamais sur un chapitre."
                                }
                              >
                                {formaterMontant(echeance.prixVente)} €
                              </span>
                            ),
                          )}
                        </div>
                      </td>
                      <td className="gantt__ecart col-nombre">
                        {p.demiJourneesPosees === 0 && !visible(p.heuresImmobilisees) ? (
                          <span className="neutre">—</span>
                        ) : (
                          <>
                            {formaterNombre(p.heuresImmobilisees, 2)} h
                            {visible(p.ecartArrondi) && (
                              <span
                                className={
                                  p.ecartArrondi.startsWith("-")
                                    ? "gantt__gain"
                                    : "gantt__cache"
                                }
                                title={
                                  p.ecartArrondi.startsWith("-")
                                    ? "Moins de demi-journées posées que les heures n'en demandent : un arbitrage du conducteur, pas une erreur."
                                    : "Heures immobilisées au-delà des heures chiffrées : le coût de l'arrondi à la demi-journée. Payées, mais absentes de la contre-étude."
                                }
                              >
                                {p.ecartArrondi.startsWith("-") ? " " : " +"}
                                {formaterNombre(p.ecartArrondi, 2)} h
                              </span>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                    {/*
                      Le détail de la tâche, une ligne par ressource — et c'est ICI que le
                      planning se travaille depuis la migration 012.

                      *Demandé par Chris le 12/08/2026 : « je ne vois pas toutes les lignes
                      de la contre-étude, transport, matériaux etc. », puis « chaque ligne
                      porte ses barres, placées librement ».*

                      **Le nombre proposé se déduit de la durée que la LIGNE pose** : à 4
                      demi-journées, la niveleuse chiffrée 28,75 h en demande 2 et la benne
                      d'approvisionnement chiffrée 55,25 h en demande 4. C'est pour ça
                      qu'un champ global ne suffirait pas.

                      Ce qui reste vide ne compte nulle part — le conducteur remplit ce que
                      son équipe conduit, les rotations de transport sont l'affaire du
                      responsable transport. Aucun code du référentiel n'a eu besoin d'être
                      classé.
                    */}
                    {depliee &&
                      visibles.map((r) => {
                        const pl = r.planning;
                        const ecartLigne =
                          pl.demiJourneesPosees > 0 &&
                          pl.demiJourneesPosees !== pl.demiJourneesProposees;
                        // DEUX rangées par ressource : la ligne elle-même, puis la saisie de
                        // ses livraisons. Un tableau plutôt qu'un fragment — `<tbody>`
                        // n'accepte que des `<tr>`, et un fragment porterait la clé au mauvais
                        // niveau.
                        return [
                          <tr
                            key={`mat-${tache.numero}-${r.rang}`}
                            // L'ancre du saut venu du plan de charge (17/09/2026), et le
                            // surlignage qui dit LAQUELLE des cent lignes on a demandée.
                            {...{ [ATTRIBUT_CIBLE]: ancreDeLaLigne(tache.numero, r.rang) }}
                            className={
                              cible?.poste === tache.numero && cible.rang === r.rang
                                ? "gantt__materiel cible"
                                : "gantt__materiel"
                            }
                          >
                            <td className="gantt__poste">
                              <span className="code">{r.code}</span> {r.designation}
                              {r.statut !== "conserve" && (
                                <span className="note"> · {r.statut.replace(/_/g, " ")}</span>
                              )}
                            </td>
                            <td className="gantt__groupe">
                              <span className="neutre">{r.unite}</span>
                            </td>
                            <td className="gantt__initial col-nombre">
                              {formaterNombre(r.quantiteReelle, 2)}
                              {r.enHeures ? " h" : ""}
                              {/* Le transport porte bien une durée, mais il n'allonge pas
                                  sa tâche : ses heures sont concomitantes au travail de
                                  l'équipe. Le dire ici évite qu'une ligne planifiée mais
                                  absente de la durée de la tâche paraisse oubliée. */}
                              {r.enHeures && !r.porteDuree && (
                                <span
                                  className="gantt__transport"
                                  title="Cette ligne n'allonge pas la tâche : ses heures sont concomitantes au travail de l'équipe. Elle porte quand même sa propre barre."
                                >
                                  {" "}
                                  hors durée
                                </span>
                              )}
                            </td>
                            <td className="gantt__duree">
                              {pl.demiJourneesPosees === 0 ? (
                                <span
                                  className="neutre"
                                  title={
                                    pl.demiJourneesProposees > 0
                                      ? `${pl.demiJourneesProposees} demi-journée(s) impliquées par ses heures — rien de posé`
                                      : "Cette ligne ne se compte pas en heures : elle se livre."
                                  }
                                >
                                  {pl.demiJourneesProposees > 0
                                    ? `— / ${pl.demiJourneesProposees}`
                                    : "—"}
                                </span>
                              ) : (
                                <span
                                  className={ecartLigne ? "en-ecart" : undefined}
                                  title={
                                    ecartLigne
                                      ? `${pl.demiJourneesPosees} posées, ${pl.demiJourneesProposees} impliquées par ${formaterNombre(r.quantiteReelle, 2)} h ÷ ${r.unites ?? 1}`
                                      : `${formaterNombre(r.quantiteReelle, 2)} h ÷ ${r.unites ?? 1} unité(s)`
                                  }
                                >
                                  {pl.demiJourneesPosees}
                                  {ecartLigne && ` / ${pl.demiJourneesProposees}`}
                                </span>
                              )}
                            </td>
                            {/*
                              **Le nombre se saisit ICI, sur la ligne.**

                              *Décision du métier du 12/08/2026 : « les équipes ne peuvent
                              pas être affectées sur le titre 1.1.2, il faut affecter les
                              équipes sur le détail MOICDC et MOIOUVTRX, idem pour le
                              matériel. »*

                              Toute ligne en heures en porte un : deux ouvriers sur celle-ci,
                              un chef sur celle-là, deux niveleuses sur la troisième. Chaque
                              ligne divise ses PROPRES heures par son propre nombre, et la
                              tâche dure ce que dure la plus longue.

                              Ce qui n'est pas en heures n'en porte pas : une tonne de grave
                              ne s'immobilise pas une demi-journée, elle se livre.
                            */}
                            <td className="gantt__effectif">
                              {r.enHeures ? (
                                <Nombre
                                  valeur={r.unites}
                                  propose={r.unitesProposees}
                                  titre={
                                    `Combien de « ${r.designation} » travaillent en même temps.\n` +
                                    `${formaterNombre(r.quantiteReelle, 2)} h chiffrées ÷ ce nombre ÷ 4 h = la durée de cette ligne.\n` +
                                    "Le déboursé ne change pas : les heures se partagent, elles ne se multiplient pas.\n" +
                                    "Sa barre, en revanche, se raccourcit.\n" +
                                    "Laisser vide si cette ligne n'est pas conduite par votre équipe : elle ne comptera nulle part."
                                  }
                                  modifiable={modifiable}
                                  enAttente={enAttente}
                                  onValider={(n) => {
                                    agir(fixerUnites(versionId, tache.numero, r.rang, n));
                                  }}
                                />
                              ) : (
                                <span className="neutre">—</span>
                              )}
                            </td>
                            {/*
                              Le départ de la LIGNE : le premier de ses jours occupés — barres,
                              livraisons et rotations. Voir `jourDeDepart`.
                            */}
                            <td className="gantt__depart">
                              {(() => {
                                const jour = jourDeDepart(pl);
                                return jour === null ? (
                                  <span className="neutre">—</span>
                                ) : (
                                  <span title={nommerJour(jour)}>J{jour}</span>
                                );
                              })()}
                            </td>
                            <td className="gantt__frise">
                              <div
                                className={`gantt__piste${
                                  modifiable &&
                                  pl.porteDesLivraisons &&
                                  Number(pl.resteALivrer) > 0.0000005
                                    ? " gantt__piste--livrable"
                                    : ""
                                }`}
                                style={{ ["--jours" as string]: jours }}
                                /**
                                 * **Cliquer un jour y pose une livraison du reste.**
                                 *
                                 * C'est le geste que Chris a demandé le 12/08/2026 — choisir
                                 * la date, et pouvoir en poser plusieurs. `onPointerDown` sur
                                 * la piste et non un bouton : la piste EST le calendrier, et
                                 * un bouton ne pourrait pas dire *où*.
                                 *
                                 * Le clic ne compte que s'il tombe sur le fond : une pastille
                                 * interrompt la propagation par son propre gestionnaire, sinon
                                 * la saisir pour la déplacer en créerait une nouvelle dessous.
                                 *
                                 * Rien ne se passe quand il ne reste rien à livrer — un clic
                                 * qui poserait un volume nul serait refusé par le serveur, et
                                 * l'erreur n'aurait aucun sens pour qui visait autre chose.
                                 */
                                onPointerDown={(evenement) => {
                                  if (!modifiable || enAttente) return;
                                  const jour = jourVise(evenement);
                                  if (jour === null) return;
                                  // Le même geste sur les deux natures — cliquer un jour y pose
                                  // ce qui reste —, et la ligne ne peut être que de l'une ou de
                                  // l'autre : un transport est en heures, il ne se livre pas.
                                  if (pl.porteDesTransports) {
                                    if (Number(pl.heuresProposees) <= 0.0000005) return;
                                    agir(
                                      poserTransport(versionId, tache.numero, r.rang, {
                                        jourRelatif: jour,
                                      }),
                                    );
                                    return;
                                  }
                                  if (!pl.porteDesLivraisons) return;
                                  if (Number(pl.resteALivrer) <= 0.0000005) return;
                                  agir(
                                    poserLivraison(versionId, tache.numero, r.rang, {
                                      jourRelatif: jour,
                                    }),
                                  );
                                }}
                              >
                                {pl.phases.map((phase) => (
                                  <Barre
                                    key={phase.id}
                                    phase={phase}
                                    jours={jours}
                                    nommerJour={nommerJour}
                                    couleur={null}
                                    nature={
                                      r.estMateriel ? "materiel" : r.enHeures ? "mo" : "fourniture"
                                    }
                                    intitule={`${r.code} ${r.designation} · ${formaterNombre(r.quantiteReelle, 2)} ${r.unite}`}
                                    glisse={glisse}
                                    modifiable={modifiable}
                                    selectionnee={phase.id === choisie}
                                    onSelectionner={() => {
                                      setChoisie(phase.id);
                                      setLivraisonChoisie(null);
                                      setDernierRecalcul(null);
                                    }}
                                    onCommencer={(quoi, evenement) => {
                                      commencer(phase, quoi, evenement);
                                    }}
                                    onRetirer={() => {
                                      if (phase.id === choisie) setChoisie(null);
                                      agir(retirerBarre(versionId, phase.id));
                                    }}
                                  />
                                ))}
                                {/* Hacher la ligne : ce travail-là peut être interrompu
                                    puis repris, indépendamment de ses voisines. */}
                                {modifiable &&
                                  r.enHeures &&
                                  !pl.porteDesTransports &&
                                  pl.phases.length > 0 && (
                                  <button
                                    type="button"
                                    className="gantt__hacher"
                                    disabled={enAttente || !peutHacher(pl.phases)}
                                    title={titreDuHachage(pl.phases, "cette ligne")}
                                    onClick={() => {
                                      agir(hacherBarres(versionId, tache.numero, r.rang));
                                    }}
                                  >
                                    +
                                  </button>
                                )}
                                {/* Pas sur une ligne de transport : elle se pose en pastilles,
                                    et le serveur refuserait la barre. */}
                                {modifiable &&
                                  r.enHeures &&
                                  !pl.porteDesTransports &&
                                  pl.phases.length === 0 && (
                                  <button
                                    type="button"
                                    className="lien gantt__poser"
                                    disabled={enAttente}
                                    title={
                                      (pl.demiJourneesProposees > 0
                                        ? `${formaterNombre(r.quantiteReelle, 2)} h ÷ ${r.unites ?? 1} unité(s) impliquent ${pl.demiJourneesProposees} demi-journée(s).`
                                        : "Cette ligne ne porte aucune heure : la barre part à une demi-journée.") +
                                      " Elle se pose après ce qui est déjà planifié, et se déplace ensuite."
                                    }
                                    onClick={() => {
                                      agir(
                                        poserBarre(versionId, tache.numero, r.rang, {
                                          demiJournees: Math.max(pl.demiJourneesProposees, 1),
                                        }),
                                      );
                                    }}
                                  >
                                    poser {Math.max(pl.demiJourneesProposees, 1)} ½ j
                                  </button>
                                )}
                                {/*
                                  **Les livraisons : la seconde nature de barre.**

                                  *Vision du métier du 12/08/2026 : « une ligne en quantité
                                  porte une livraison — un jour, un volume, une heure
                                  limite ».*

                                  Rondes plutôt que rectangulaires : une livraison tombe dans
                                  une journée, elle ne l'occupe pas. La forme dit à elle seule
                                  qu'il n'y a pas de bord droit à tirer.
                                */}
                                {pl.livraisons.map((l) => (
                                  <Pastille
                                    key={l.id}
                                    livraison={l}
                                    ressource={r}
                                    unite={r.unite}
                                    jours={jours}
                                    nommerJour={nommerJour}
                                    glisse={glisseLivraison}
                                    modifiable={modifiable}
                                    selectionnee={l.id === livraisonChoisie}
                                    onSelectionner={() => {
                                      setLivraisonChoisie(l.id);
                                      setChoisie(null);
                                      setDernierRecalcul(null);
                                    }}
                                    onCommencer={(evenement) => {
                                      commencerLivraison(l, "livraison", evenement);
                                    }}
                                    onRetirer={() => {
                                      if (l.id === livraisonChoisie) setLivraisonChoisie(null);
                                      agir(retirerLivraison(versionId, l.id));
                                    }}
                                  />
                                ))}
                                {/*
                                  **Une barre restée sur une ligne devenue transport.**

                                  Cocher un code au référentiel change la nature de toutes
                                  ses lignes, y compris celles qui portaient déjà une barre
                                  de durée. Elle ne disparaît pas toute seule — ce serait
                                  effacer du travail sans le dire — et le serveur refuse
                                  désormais d'en poser une nouvelle. L'écran dit donc ce
                                  qu'elle est, et laisse la retirer.
                                */}
                                {pl.porteDesTransports && pl.phases.length > 0 && (
                                  <span className="note gantt__sans-heures">
                                    barre d'avant le passage en transport — la première
                                    pastille la retirera
                                  </span>
                                )}
                                {/*
                                  **Les transports : la troisième nature.**

                                  *« Sur les lignes avec les codes matériel de transport, le
                                  conducteur doit pouvoir utiliser le même système de pastille
                                  que pour les articles »* (métier, 13/08/2026).

                                  Rondes comme les livraisons — un camion vient un jour —, mais
                                  elles portent leurs heures en clair : c'est la grandeur qui
                                  coûte de l'argent.
                                */}
                                {pl.transports.map((t) => (
                                  <PastilleTransport
                                    key={t.id}
                                    transport={t}
                                    ressource={r}
                                    jours={jours}
                                    nommerJour={nommerJour}
                                    glisse={glisseLivraison}
                                    modifiable={modifiable}
                                    selectionnee={t.id === livraisonChoisie}
                                    onSelectionner={() => {
                                      setLivraisonChoisie(t.id);
                                      setChoisie(null);
                                      setDernierRecalcul(null);
                                    }}
                                    onCommencer={(evenement) => {
                                      commencerLivraison(t, "transport", evenement);
                                    }}
                                    onRetirer={() => {
                                      if (t.id === livraisonChoisie) setLivraisonChoisie(null);
                                      agir(retirerTransport(versionId, t.id));
                                    }}
                                  />
                                ))}
                                {/*
                                  **On choisit le JOUR en cliquant dessus.**

                                  *Retour de Chris le 12/08/2026, en testant l'écran : « il se
                                  positionne automatiquement sur la fin alors que je veux
                                  pouvoir le mettre sur une date précise, et je dois pouvoir
                                  mettre plusieurs points pour livrer à différentes dates. »*

                                  Le bouton d'avant posait sans jour, et le serveur la plaçait
                                  après ce qui était planifié — la règle des barres de durée,
                                  qui n'a aucun sens pour une livraison : un camion vient le
                                  jour où on l'attend, pas à la fin du chantier.

                                  L'invite reste affichée tant qu'il reste à livrer : c'est
                                  ainsi qu'on pose la seconde pastille, puis la troisième. Le
                                  volume proposé est **ce qui reste** — retaper un volume que
                                  l'application connaît déjà serait une saisie pour rien, et
                                  une occasion de fausser une grandeur qui doit tomber juste.
                                */}
                                {modifiable &&
                                  pl.porteDesLivraisons &&
                                  Number(pl.resteALivrer) > 0.0000005 && (
                                    <span className="note gantt__inviter-livraison">
                                      cliquer un jour, ou « + livraison » ci-dessous
                                    </span>
                                  )}
                                {/*
                                  Ce qui se livre mais que le fichier chiffre à zéro : rien à
                                  placer, et le dire évite qu'une piste vide se lise comme un
                                  oubli.
                                */}
                                {pl.porteDesLivraisons &&
                                  pl.livraisons.length === 0 &&
                                  Number(pl.resteALivrer) <= 0.0000005 && (
                                    <span className="note gantt__sans-heures">
                                      aucune quantité à livrer
                                    </span>
                                  )}
                              </div>
                            </td>
                            <td className="gantt__ecart col-nombre">
                              {/*
                                Sur ce qui se livre, la colonne ne dit pas des heures : elle
                                dit **ce qui est placé sur ce qui est chiffré**. C'est
                                l'invariant du métier — la somme des livraisons égale la
                                quantité de la ligne — et il s'affiche au lieu de bloquer,
                                comme tous les autres écarts de ce produit.
                              */}
                              {pl.porteDesLivraisons ? (
                                pl.livraisons.length === 0 ? (
                                  <span className="neutre">—</span>
                                ) : (
                                  <>
                                    {formaterNombre(pl.quantiteLivree, 2)} {r.unite}
                                    {/*
                                      Les échéances, en clair et à côté du volume : c'est ce
                                      que le conducteur doit tenir. Sans elles ici, il faudrait
                                      survoler chaque pastille pour savoir si le chantier
                                      attend un camion avant 11h00.
                                    */}
                                    {pl.livraisons.some((l) => l.heureLimite !== null) && (
                                      <span
                                        className="gantt__reste"
                                        title={
                                          "Échéances de livraison — le volume entier de chaque pastille doit être " +
                                          "sur le chantier avant l'heure indiquée.\n" +
                                          pl.livraisons
                                            .filter((l) => l.heureLimite !== null)
                                            .map(
                                              (l) =>
                                                `J${l.jourRelatif} : ${formaterNombre(l.quantite, 2)} ${r.unite} avant ${l.heureLimite ?? ""}`,
                                            )
                                            .join("\n")
                                        }
                                      >
                                        {" "}
                                        ·{" "}
                                        {pl.livraisons
                                          .filter((l) => l.heureLimite !== null)
                                          .map((l) => `J${l.jourRelatif} av. ${l.heureLimite ?? ""}`)
                                          .join(", ")}
                                      </span>
                                    )}
                                    {visible(pl.resteALivrer) && (
                                      <span
                                        className={
                                          pl.resteALivrer.startsWith("-")
                                            ? "gantt__cache"
                                            : "gantt__reste"
                                        }
                                        title={
                                          pl.resteALivrer.startsWith("-")
                                            ? "Plus livré que chiffré : un arbitrage du conducteur, ou une pastille à réduire."
                                            : "Reste à placer dans le calendrier : la somme des livraisons doit égaler la quantité de la ligne."
                                        }
                                      >
                                        {pl.resteALivrer.startsWith("-")
                                          ? ` (+${formaterNombre(pl.resteALivrer.slice(1), 2)} de trop)`
                                          : ` (reste ${formaterNombre(pl.resteALivrer, 2)})`}
                                      </span>
                                    )}
                                  </>
                                )
                              ) : !r.enHeures || !visible(pl.heuresImmobilisees) ? (
                                <span className="neutre">—</span>
                              ) : (
                                <>
                                  {formaterNombre(pl.heuresImmobilisees, 2)} h
                                  {visible(pl.ecartArrondi) && (
                                    <span
                                      className={
                                        pl.ecartArrondi.startsWith("-")
                                          ? "gantt__gain"
                                          : "gantt__cache"
                                      }
                                      title={
                                        pl.ecartArrondi.startsWith("-")
                                          ? "Moins d'heures immobilisées que le fichier n'en chiffre : cette ressource ne tiendra pas ses heures dans cette durée. Soit il en faut davantage, soit la barre est trop courte."
                                          : "Heures immobilisées au-delà des heures chiffrées : le coût de l'arrondi à la demi-journée. Payées, absentes de la contre-étude."
                                      }
                                    >
                                      {pl.ecartArrondi.startsWith("-") ? " " : " +"}
                                      {formaterNombre(pl.ecartArrondi, 2)} h
                                      {/* Le coût en euros est EXACT sur une ligne : elle
                                          porte son propre prix horaire. À la maille tâche,
                                          on ne savait pas laquelle des trois personnes
                                          attendait ni à quel taux. */}
                                      {visible(pl.coutCache) && (
                                        <> = {formaterMontant(pl.coutCache)} €</>
                                      )}
                                    </span>
                                  )}
                                </>
                              )}
                            </td>
                          </tr>,
                            /*
                              **La saisie des livraisons, sous la ligne et toujours visible.**

                              *Ce que Chris n'a pas trouvé le 12/08/2026 : « je ne comprends
                              pas comment ajouter des pastilles et surtout comment saisir les
                              quantités et les heures limites ».* La saisie vivait dans un
                              panneau en fin de page — sur un tableau de cent lignes, il
                              s'ouvrait hors de l'écran, donc jamais vu.

                              Elle n'apparaît que sur ce qui se livre ET qui a quelque chose à
                              placer : une ligne chiffrée à zéro n'aurait rien à saisir, et une
                              rangée de champs vides sur chaque fourniture noierait l'écran.
                            */
                            pl.porteDesTransports && Number(r.quantiteReelle) > 0 ? (
                              <tr
                                key={`tra-${tache.numero}-${r.rang}`}
                                className="gantt__materiel gantt__ligne-livraisons"
                              >
                                <td colSpan={8}>
                                  <TransportsDeLaLigne
                                    ressource={r}
                                    heuresDevis={
                                      devisParLigne.get(`${tache.numero}#${String(r.rang)}`) ?? null
                                    }
                                    fige={transportsFiges.includes(`${tache.numero}#${String(r.rang)}`)}
                                    onFiger={(fige) => {
                                      agir(figerLeTransport(versionId, tache.numero, r.rang, fige));
                                    }}
                                    carrieres={carrieres}
                                    jours={jours}
                                    dateDuJour={dateDuJour}
                                    modifiable={modifiable}
                                    enAttente={enAttente}
                                    choisi={livraisonChoisie}
                                    onChoisir={(id) => {
                                      setLivraisonChoisie(id);
                                      setChoisie(null);
                                    }}
                                    onAjouter={() => {
                                      // Après la dernière pastille, comme pour les livraisons :
                                      // deux transports au même jour se superposeraient sur la
                                      // frise, et le second passerait inaperçu.
                                      const dernier = pl.transports.reduce(
                                        (max, t) => Math.max(max, t.jourRelatif),
                                        0,
                                      );
                                      agir(
                                        poserTransport(versionId, tache.numero, r.rang, {
                                          jourRelatif: dernier + 1,
                                        }),
                                      );
                                    }}
                                    onModifier={(id, changement) => {
                                      agir(modifierTransport(versionId, id, changement));
                                    }}
                                    onRepartir={(id) => {
                                      agir(repartirLeTransport(versionId, id));
                                    }}
                                    onRetirer={(id) => {
                                      if (id === livraisonChoisie) setLivraisonChoisie(null);
                                      agir(retirerTransport(versionId, id));
                                    }}
                                  />
                                </td>
                              </tr>
                            ) : pl.porteDesLivraisons &&
                              (pl.livraisons.length > 0 || Number(r.quantiteReelle) > 0) ? (
                              <tr
                                key={`liv-${tache.numero}-${r.rang}`}
                                className="gantt__materiel gantt__ligne-livraisons"
                              >
                                <td colSpan={8}>
                                  <LivraisonsDeLaLigne
                                    ressource={r}
                                    carrieres={carrieres}
                                    /* Les ATTELAGES du poste, groupés par le serveur d apres les
                                       couplages du Parametrage transport (076). Les refaire ici
                                       finirait par proposer un attelage que le calcul ne forme
                                       pas — c est la regle du calcul unique. */
                                    attelages={attelagesDesPostes[tache.numero] ?? []}
                                    jours={jours}
                                    dateDuJour={dateDuJour}
                                    modifiable={modifiable}
                                    enAttente={enAttente}
                                    choisie={livraisonChoisie}
                                    onChoisir={(id) => {
                                      setLivraisonChoisie(id);
                                      setChoisie(null);
                                    }}
                                    onAjouter={() => {
                                      /*
                                        Le jour proposé : juste après la dernière pastille, ou
                                        J1 sur une ligne vierge. Deux livraisons au même jour
                                        se superposeraient à l'écran, et le conducteur ne
                                        verrait pas qu'il en a créé une seconde.
                                      */
                                      const dernier = pl.livraisons.reduce(
                                        (max, l) => Math.max(max, l.jourRelatif),
                                        0,
                                      );
                                      agir(
                                        poserLivraison(versionId, tache.numero, r.rang, {
                                          jourRelatif: dernier + 1,
                                        }),
                                      );
                                    }}
                                    onModifier={(id, changement) => {
                                      agir(modifierLivraison(versionId, id, changement));
                                    }}
                                    onRepartir={(id) => {
                                      agir(repartirLaLivraison(versionId, id));
                                    }}
                                    onRetirer={(id) => {
                                      if (id === livraisonChoisie) setLivraisonChoisie(null);
                                      agir(retirerLivraison(versionId, id));
                                    }}
                                  />
                                </td>
                              </tr>
                            ) : null,
                          ];
                        })}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selection !== null && (
        <PanneauBarre
          porteur={selection.porteur}
          phase={selection.phase}
          modifiable={modifiable}
          enAttente={enAttente}
          onModifier={(changement) => {
            agir(modifierBarre(versionId, selection.phase.id, changement));
          }}
          onRetirer={() => {
            setChoisie(null);
            agir(retirerBarre(versionId, selection.phase.id));
          }}
          onFermer={() => {
            setChoisie(null);
          }}
        />
      )}

      {/*
        **Il n'y a plus de panneau pour les livraisons**, et c'est la correction du
        12/08/2026 : leur saisie vit sous la ligne, en clair. Un panneau en fin de page
        s'ouvrait hors de l'écran, et personne n'allait le chercher.

        `livraisonChoisie` reste : elle sert à mettre en évidence la pastille et sa carte de
        saisie ensemble, pour qu'on voie laquelle on modifie.
      */}
    </>
  );
}

/* ---------------------------------------------------------------------------
 * Le compte rendu du calcul de transport (09/09/2026)
 * ------------------------------------------------------------------------- */

/** Les motifs de refus, en phrases. Le code brut sur l'écran d'un conducteur ne dit rien. */
/** L'aide de la liste des attelages : ce que le choix décide, et ce qu'il ne décide pas. */
const AIDE_ATTELAGE =
  "Quel attelage emporte CE tonnage-là.\n" +
  "Ce poste porte plusieurs camions de capacités différentes : le calcul ne peut pas deviner " +
  "lequel roule, et les additionner ferait un véhicule qui n'existe pas.\n\n" +
  "C'est ainsi qu'on dit « les premiers voyages en 8×4 pour préparer l'accès, puis en tracteur " +
  "et benne » : deux pastilles le même jour, une par attelage.\n\n" +
  "Désigner le tracteur ou sa benne revient au même — ils forment un attelage au Paramétrage " +
  "transport. Ce choix ne déplace aucun euro : il dit seulement qui roule.";

const MOTIFS_SANS_ORGANISATION: Record<string, string> = {
  pas_par_nos_soins:
    "l'acheminement n'est pas « par nos soins » : aucun de nos camions n'est mobilisé.",
  pas_du_groupe: "le matériau n'est pas déclaré « du groupe », ou aucune carrière n'est désignée.",
  aucun_camion: "aucune ligne de camion n'est posée sur ce poste : il n'y a rien à dimensionner.",
  charge_utile_inconnue:
    "AUCUN des camions choisis n'a de charge utile au Paramétrage transport : sans elle, le " +
    "nombre de tours n'est pas calculable, et zéro tour se lirait « rien à transporter ». " +
    "Un tracteur seul ne suffit pas — c'est la benne qui porte, et son tonnage se saisit là-bas.",
  distance_inconnue:
    "la distance de la carrière à la commune du chantier n'est pas renseignée (menu Distance).",
  unite_non_convertible:
    "l'unité de la ligne ne se convertit pas en tonnes : aucun nombre de tours n'est calculable.",
  aucune_pastille: "aucune pastille de tonnage n'est posée : il n'y a aucun jour à organiser.",
  attelage_non_designe:
    "ce poste porte PLUSIEURS attelages de capacités différentes, et une pastille de tonnage ne " +
    "dit pas lequel l'emporte. Ouvrez la pastille et choisissez l'attelage — c'est ainsi qu'on " +
    "indique « les premiers voyages en 8×4, puis en tracteur et benne ». Deux attelages ne se " +
    "devinent pas : les additionner ferait un camion qui n'existe pas.",
  plusieurs_matieres_du_groupe:
    "DEUX matériaux de carrière du groupe sont posés sur ce poste, chacun avec sa carrière et " +
    "une pastille : le calcul ne peut pas deviner lequel vos camions vont chercher, ni comment " +
    "répartir la charge entre les deux. Séparez-les sur deux postes, ou décochez « du groupe » " +
    "sur celui qui n'est pas acheminé par nos camions. Les articles, eux, ne comptent pas ici — " +
    "le calcul ne porte que sur les matériaux de nos carrières.",
};

/** Un nombre d'heures en heures et minutes : « 1,8893 h » se vérifie mal, « 1 h 53 » se lit. */
function enHeuresMinutes(heures: string): string {
  const total = Number(heures);
  if (!Number.isFinite(total)) return heures;
  const h = Math.floor(total);
  const m = Math.round((total - h) * 60);
  return m === 0 ? `${String(h)} h` : `${String(h)} h ${String(m).padStart(2, "0")}`;
}

function BilanTransport({ bilan }: { bilan: BilanDuTransport }): JSX.Element {
  if (bilan.ouvrages.length === 0) {
    return (
      <p className="etat">
        Aucun poste à organiser : le calcul ne porte que sur les <strong>matériaux de nos
        carrières acheminés par nos soins</strong>, avec au moins une pastille de tonnage posée.
        Les <strong>articles</strong> n'entrent pas dans ce calcul, même cochés « du groupe ».
      </p>
    );
  }

  return (
    <div className="etat">
      <p>
        <strong>
          {bilan.rotationsPosees} rotation(s) posée(s)
          {bilan.rotationsRetirees > 0 && `, ${String(bilan.rotationsRetirees)} remplacée(s)`}
        </strong>{" "}
        sur {bilan.ouvrages.filter((o: OuvrageOrganise) => o.rotationsPosees > 0).length} poste(s).
      </p>
      <ul className="plan__transport-bilan">
        {bilan.ouvrages.map((o: OuvrageOrganise) => {
          const { organisation: org } = o;
          if (org.motifs.length > 0) {
            return (
              <li key={o.poste}>
                <strong>
                  {o.poste} — {o.materiau}
                </strong>{" "}
                <span className="note--alerte">
                  non organisé :{" "}
                  {org.motifs
                    .map((m: string) => MOTIFS_SANS_ORGANISATION[m] ?? m)
                    .join(" Et ")}{" "}
                  {/* Le refus NOMME ce qui manque quand il le peut : « J4, J5 » vaut mieux que
                      « une pastille », qui laisse chercher laquelle sur cinq. */}
                  {org.detail !== undefined && org.detail !== "" && (
                    <>
                      À corriger sur : <strong>{org.detail}</strong>.{" "}
                    </>
                  )}
                  Ses rotations existantes n'ont pas été touchées.
                </span>
              </li>
            );
          }
          return (
            <li key={o.poste}>
              <strong>
                {o.poste} — {o.materiau}
                {o.attelage !== "" && <> · {o.attelage}</>}
              </strong>{" "}
              {o.lignesFigees.length > 0 && (
                <span className="note--alerte">
                  {o.lignesFigees.join(", ")} figée{o.lignesFigees.length > 1 ? "s" : ""} : laissée
                  {o.lignesFigees.length > 1 ? "s" : ""} telle{o.lignesFigees.length > 1 ? "s" : ""}{" "}
                  quelle{o.lignesFigees.length > 1 ? "s" : ""}.{" "}
                </span>
              )}
              <span className="note">
                {/* La charge utile et la durée du tour d'abord : ce sont les deux hypothèses
                    dont tout le reste découle, donc les deux qu'il faut pouvoir contester. */}
                charge utile {org.chargeUtile} T
                {/*
                  CE QUI N'A RIEN PESÉ SE NOMME (09/09/2026).

                  *« Quand il y a tracteur il faut se baser sur les bennes »* : un maillon sans
                  tonnage ne ferme plus le calcul. Mais un tonnage OUBLIÉ au paramétrage lui
                  ressemble trait pour trait dans les données, et il annoncerait des camions en
                  trop sans que rien ne le dise. Le seul garde-fou possible est de le montrer.
                */}
                {org.maillonsSansCharge.length > 0 && (
                  <>
                    {" ("}
                    <strong>{org.maillonsSansCharge.join(", ")}</strong> sans charge utile au
                    Paramétrage transport, non compté
                    {org.maillonsSansCharge.length > 1 ? "s" : ""} — normal pour un tracteur, à
                    corriger là-bas pour une benne{")"}
                  </>
                )}{" "}
                · {org.distanceKm} km ×2 ={" "}
                {org.parVoyage?.kmAllerRetour} km par tour ·{" "}
                {org.parVoyage !== null &&
                  `${enHeuresMinutes(org.parVoyage.routeHeures)} de route + ` +
                    `${enHeuresMinutes(org.parVoyage.chargementHeures)} de chargement + ` +
                    `${enHeuresMinutes(org.parVoyage.manoeuvreHeures)} de manipulation = ` +
                    `${enHeuresMinutes(org.parVoyage.heures)} le tour`}
              </span>
              {/*
                LES DEUX TRAJETS DU DÉPÔT (09/09/2026, règle de l'approche fixée le 10/09).

                Dits séparément de la durée du tour parce qu'ils se comptent autrement : l'approche
                une fois par camion et par matin, le retour nulle part. Les deux se disent quand
                même, et chacun avec ce qu'il pèse — un trajet connu qui ne coûte rien doit se voir,
                sinon le total paraît incomplet et personne ne peut contester la règle qui l'écarte.
              */}
              <div className="note">
                dépôt →{" "}
                {org.distanceDepotCarriereKm === null ? (
                  <strong className="note--alerte">
                    carrière : distance inconnue, l'approche n'est pas comptée
                  </strong>
                ) : (
                  `carrière ${org.distanceDepotCarriereKm} km, comptée par camion chaque matin`
                )}
                {" · "}
                {org.distanceChantierDepotKm === null ? (
                  "chantier → dépôt : distance inconnue"
                ) : (
                  `chantier → dépôt ${org.distanceChantierDepotKm} km`
                )}
                , retour imputé sur le chantier suivant
              </div>
              <ul>
                {org.journees.map((j: JourneeDeTransport) => (
                  <li key={j.pastilleId}>
                    J{j.jourRelatif} · {j.tonnes} T ={" "}
                    {/*
                      LES TOURS SONT ENTIERS depuis le 10/09/2026 — *« il faudrait s'arranger pour
                      que les nombres de tours tombent juste, donc pas de décimal »* (Chris). Ce
                      champ affichait `toursComptes` à deux décimales — « 10,59 tours » — à côté
                      d'un compte de chargements arrondi au supérieur. Les deux ont fusionné.
                    */}
                    <strong>
                      {j.chargements} tour(s), {j.camions} camion(s)
                    </strong>{" "}
                    ({enHeuresMinutes(j.heuresTotales)} au total){" "}
                    {/*
                      LE REMPLISSAGE, ET NON LA MOYENNE — retour de Chris, 09/09/2026 : « tu me
                      mets 3 chauffeurs à 6,88 heures, donc si on peut remplir les journées je
                      pense que c'est mieux ? »

                      La moyenne donnait à lire trois journées à moitié vides. Le remplissage réel
                      est 4 + 4 + 3 chargements, soit 7 h 33 · 7 h 33 · 5 h 40 : les journées sont
                      déjà pleines autant qu'elles peuvent l'être. C'est le nombre qui répond à sa
                      question, et la moyenne devait cesser d'être ce qu'on lit en premier.
                    */}
                    <span className="note">
                      · journées de{" "}
                      <strong>
                        {j.heuresReellesParCamion.map((h) => enHeuresMinutes(h)).join(" · ")}
                      </strong>{" "}
                      ({j.chargementsParCamion.join(" + ")} chargements sur {j.chargements}
                      {" ; "}
                      {j.toursParCamion} tiennent dans la journée d'un chauffeur)
                    </span>
                    {/*
                      LE POURQUOI DU NOMBRE — question de Chris, 09/09/2026 : « pourquoi as-tu
                      considéré qu'il valait mieux mettre 3 camions ? ». Un camion ne fait que
                      des chargements COMPLETS dans sa journée, qui est pleine et entière depuis
                      que les trajets du dépôt n'y réservent plus rien. L'afficher évite de refaire
                      la division de tête, et rend le nombre contestable au lieu d'imposé.
                    */}
                    {/*
                      CE QUI EST RÉELLEMENT POSÉ — la demande de Chris, redite trois fois.

                      « Fais les calculs en remplissant les journées » : les pastilles portent
                      désormais les journées pleines et le reste, plus une moyenne. Les afficher
                      telles qu'elles sont posées est la seule façon de vérifier le Gantt sans
                      l'ouvrir.
                    */}
                    <span className="note">
                      · posé :{" "}
                      <strong>
                        {j.pastilles
                          .map(
                            (p) =>
                              `${String(p.unites)} × ${enHeuresMinutes(p.heures)}` +
                              ` (${String(p.chargements)} chargement${p.chargements > 1 ? "s" : ""}` +
                              `${p.porteLeTrajetDuDepot ? ", dépôt compris" : ""})`,
                          )
                          .join(" + ")}
                      </strong>
                      {" · "}
                      {enHeuresMinutes(j.heuresDesTours)} de tours
                      {Number(j.trajetDepotHeures) > 0 &&
                        ` + ${enHeuresMinutes(j.trajetDepotHeures)} de dépôt → carrière`}
                    </span>
                    {j.heureLimite !== null && (
                      <>
                        {" "}
                        · <span className="note">avant {j.heureLimite}</span>
                      </>
                    )}
                    {j.journeePleine && (
                      <>
                        {" "}
                        <span className="note--alerte">
                          — plus d'une journée de camion : c'est pourquoi il en faut{" "}
                          {j.camions}.
                        </span>
                      </>
                    )}
                  </li>
                ))}
              </ul>
              {/*
                LE DÉBOURSÉ SUIT DÉSORMAIS — décision de Chris, 09/09/2026 au soir.

                *« Dans le devis tu dois changer les heures par celles que tu as calculées, c'est
                bien l'objectif de corriger le calcul du devis figé qui n'est pas exact »*, puis
                *« pardon, tu ne changes pas les heures du devis figé, tu changes celles de la
                contre-étude »*. C'était le point ouvert du matin ; la règle (a) est retenue.

                Le libellé disait l'inverse — « le déboursé n'est pas modifié » — et devait suivre :
                un écran qui affirme le contraire de ce que le bouton vient de faire est pire
                qu'un écran muet. L'écart reste affiché, parce qu'il dit ce que le devis vendu
                sous-estimait, et c'est toute la raison d'être de la contre-étude.
              */}
              <p className="note">
                {enHeuresMinutes(org.heuresTotales)} posées contre{" "}
                {enHeuresMinutes(org.heuresChiffrees)} chiffrées sur les lignes de transport
                {Number(org.heuresChiffrees) > 0 && (
                  <>
                    {" "}
                    (
                    {Number(org.heuresTotales) >= Number(org.heuresChiffrees) ? "+" : "−"}
                    {Math.abs(
                      Math.round(
                        ((Number(org.heuresTotales) - Number(org.heuresChiffrees)) /
                          Number(org.heuresChiffrees)) *
                          100,
                      ),
                    )}
                    {" %"})
                  </>
                )}
                .{" "}
                <strong>
                  Les heures de la contre-étude ont été recalées sur ce qui est posé
                </strong>{" "}
                — le devis figé, lui, n'est jamais touché.
              </p>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
